<template>
  <div class="top-toolbar">
    <!-- 메인 컨트롤 영역 -->
    <div class="main-controls">
      <div class="controls-group">
        <div class="control-item">
          <YearMonthSelector v-model="searchParams.lebYm" :rules="[rules.required, rules.yyyymm]" hide-details
            density="compact" class="compact-ym" />
        </div>

        <div class="control-item">
          <div class="search-group">
            <v-text-field v-model="filterParams.caregiverName" placeholder="요양보호사"
              prepend-inner-icon="mdi-account-search" variant="outlined" density="compact" clearable hide-details
              class="mini-search" />
            <v-text-field v-model="filterParams.patientName" placeholder="수급자" prepend-inner-icon="mdi-account-heart"
              variant="outlined" density="compact" clearable hide-details class="mini-search" />
          </div>
        </div>
      </div>

      <div class="action-group">
        <v-btn density="comfortable" variant="elevated" @click="handleDownloadPayrollReport" :disabled="memberJoinData.length === 0" class="function-btn">
          <v-icon size="20">mdi-file-excel</v-icon>
          <span class="ml-1">보고서</span>
        </v-btn>
        <v-btn 
          v-if="memberJoinData.length > 0"
          density="comfortable" 
          variant="elevated" 
          @click="openSetModal"
          :disabled="isMagam"
          class="function-btn"
        >
          <v-icon size="20">mdi-cog</v-icon>
          <span class="ml-1">기능</span>
        </v-btn>
        <v-btn color="gray-darken-1" density="comfortable" variant="elevated" @click="saveAllData" :loading="saving"
          :disabled="memberJoinData.length === 0 || !hasTimePayData || isMagam" class="save-btn">
          <v-icon size="20">mdi-content-save</v-icon>
          <span class="ml-1">모두 저장</span>
        </v-btn>
      </div>
    </div>

    <!-- 통계 정보 바 -->
    <div class="stats-bar">
      <div class="stat-item">
        <div class="stat-icon">
          <v-icon size="18" color="#6366f1">mdi-account-group</v-icon>
        </div>
        <div class="stat-content">
          <span class="stat-label">요양보호사</span>
          <span class="stat-value">{{ summaryInfo.totalCaregivers }}</span>
        </div>
      </div>

      <div class="stat-item">
        <div class="stat-icon">
          <v-icon size="18" color="#8b5cf6">mdi-account-heart</v-icon>
        </div>
        <div class="stat-content">
          <span class="stat-label">수급자</span>
          <span class="stat-value">{{ summaryInfo.totalPatients }}</span>
        </div>
      </div>

      <div class="stat-item">
        <div class="stat-icon">
          <v-icon size="18" color="#10b981">mdi-cash-multiple</v-icon>
        </div>
        <div class="stat-content">
          <span class="stat-label">총수가</span>
          <span class="stat-value">{{ formatMoney(summaryInfo.totalSugaMoney) }}</span>
        </div>
      </div>

      <div class="stat-item">
        <div class="stat-icon">
          <v-icon size="18" color="#f59e0b">mdi-wallet</v-icon>
        </div>
        <div class="stat-content">
          <span class="stat-label">총월급</span>
          <span class="stat-value">{{ formatMoney(summaryInfo.totalMonthlyPay) }}</span>
        </div>
      </div>


      <div class="stat-item">
        <div class="stat-icon">
          <v-icon size="18" color="#10b981">mdi-piggy-bank</v-icon>
        </div>
        <div class="stat-content">
          <span class="stat-label">인건비 절감</span>
          <span class="stat-value text-green">{{ formatMoney(summaryInfo.totalCenterTaxSavings) }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- 멤버 조인 쿼리 결과 테이블 -->
  <div v-if="memberJoinData.length > 0" class="data-table-container">
    <table class="data-table">
      <thead>
        <tr>
          <th class="sortable" @click="handleSort('caregiver')">
            <div style="display: flex; justify-content: center; align-items: center; gap: 4px; cursor: pointer;">
              <span>요양보호사</span>
              <v-icon v-if="sortColumn === 'caregiver'" size="16"
                :class="{ 'sort-asc': sortDirection === 'asc', 'sort-desc': sortDirection === 'desc' }">
                {{ sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <v-icon v-else size="16" style="opacity: 0.3;">mdi-unfold-more-horizontal</v-icon>
            </div>
          </th>
          <th class="sortable" @click="handleSort('patient')">
            <div style="display: flex; justify-content: center; align-items: center; gap: 4px; cursor: pointer;">
              <span>수급자명</span>
              <v-icon v-if="sortColumn === 'patient'" size="16"
                :class="{ 'sort-asc': sortDirection === 'asc', 'sort-desc': sortDirection === 'desc' }">
                {{ sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <v-icon v-else size="16" style="opacity: 0.3;">mdi-unfold-more-horizontal</v-icon>
            </div>
          </th>
          <th class="sortable" @click="handleSort('timePay')">
            <div style="display: flex; justify-content: center; align-items: center; gap: 4px; cursor: pointer;">
              <span>시급</span>
              <v-icon v-if="sortColumn === 'timePay'" size="16"
                :class="{ 'sort-asc': sortDirection === 'asc', 'sort-desc': sortDirection === 'desc' }">
                {{ sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <v-icon v-else size="16" style="opacity: 0.3;">mdi-unfold-more-horizontal</v-icon>
            </div>
          </th>
          <th class="sortable" @click="handleSort('serviceDays')">
            <div style="display: flex; justify-content: center; align-items: center; gap: 4px; cursor: pointer;">
              <span>서비스횟수</span>
              <v-icon v-if="sortColumn === 'serviceDays'" size="16"
                :class="{ 'sort-asc': sortDirection === 'asc', 'sort-desc': sortDirection === 'desc' }">
                {{ sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <v-icon v-else size="16" style="opacity: 0.3;">mdi-unfold-more-horizontal</v-icon>
            </div>
          </th>
          <th class="sortable" @click="handleSort('sugaHours')">
            <div style="display: flex; justify-content: center; align-items: center; gap: 4px; cursor: pointer;">
              <span>서비스시간</span>
              <v-icon v-if="sortColumn === 'sugaHours'" size="16"
                :class="{ 'sort-asc': sortDirection === 'asc', 'sort-desc': sortDirection === 'desc' }">
                {{ sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <v-icon v-else size="16" style="opacity: 0.3;">mdi-unfold-more-horizontal</v-icon>
            </div>
          </th>
          <th class="sortable" @click="handleSort('totalSugaMoney')">
            <div style="display: flex; justify-content: center; align-items: center; gap: 4px; cursor: pointer;">
              <span>수가</span>
              <v-icon v-if="sortColumn === 'totalSugaMoney'" size="16"
                :class="{ 'sort-asc': sortDirection === 'asc', 'sort-desc': sortDirection === 'desc' }">
                {{ sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <v-icon v-else size="16" style="opacity: 0.3;">mdi-unfold-more-horizontal</v-icon>
            </div>
          </th>
          <th class="sortable" @click="handleSort('monthlyPay')">
            <div style="display: flex; justify-content: center; align-items: center; gap: 4px; cursor: pointer;">
              <span>급여</span>
              <v-icon v-if="sortColumn === 'monthlyPay'" size="16"
                :class="{ 'sort-asc': sortDirection === 'asc', 'sort-desc': sortDirection === 'desc' }">
                {{ sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <v-icon v-else size="16" style="opacity: 0.3;">mdi-unfold-more-horizontal</v-icon>
            </div>
          </th>
          <th class="sortable" @click="handleSort('bonusAllowance')">
            <div style="display: flex; justify-content: center; align-items: center; gap: 4px; cursor: pointer;">
              <span>수당</span>
              <v-icon v-if="sortColumn === 'bonusAllowance'" size="16"
                :class="{ 'sort-asc': sortDirection === 'asc', 'sort-desc': sortDirection === 'desc' }">
                {{ sortDirection === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
              </v-icon>
              <v-icon v-else size="16" style="opacity: 0.3;">mdi-unfold-more-horizontal</v-icon>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(group, sortedIndex) in sortedGroupedMemberData" :key="sortedIndex">
          <!-- 메인 행 (합계 표시) -->
          <tr class="main-row" :class="{
            'clickable-row': group.items.length > 0,
            'expanded': isRowExpanded(group._originalIndex)
          }" @click="group.items.length > 0 ? toggleRow(group._originalIndex) : null">
            <!-- 요양보호사 통합 정보 (rowspan 적용, 중증수급자 가산 포함) -->
            <td class="merged-cell text-center caregiver-info"
              :rowspan="isRowExpanded(group._originalIndex) ? group.items.length + 1 + getSevereCareAdditionForGroup(group._originalIndex).length : 1">
              {{ group.caregiverInfo.caregiver_name || "-" }}

            </td>

            <!-- 환자 정보 (그룹화된 정보 표시) -->
            <td class="merged-cell">
              {{ group.patientInfo.patient_name || "-" }}
            </td>
            <td class="total-cell text-right pr-2"
              :class="{ 'no-time-pay': hasEmptyTimePayInGroup(group._originalIndex) }">
              {{ formatMoney(group.totals.average_time_pay) }}원
            </td>
            <!-- 합계 데이터 셀들 -->
            <td class="total-cell">{{ group.totals.service_days || 0 }}회</td>
            <td class="total-cell">{{ formatSugaHours(group.totals.suga_hours) }}</td>
            <td class="total-cell text-right pr-2">{{ formatMoney(group.totals.total_suga_money) }}원</td>
            <td class="total-cell text-right pr-2">{{ formatMoney(group.totals.monthly_pay) }}원</td>
            <td class="total-cell text-right pr-2">{{ formatMoney((group.allowances.longService || 0) +
              (group.allowances.severeCareAddition || 0) + (group.allowances.laborDay || 0) + (group.allowances.holiday
                || 0) + (group.allowances.overtime || 0)) }}원</td>
          </tr>

          <!-- 세부 정보 행들 (펼쳐졌을 때만 표시) -->
          <template v-if="isRowExpanded(group._originalIndex)">
            <template v-for="(patientGroup, patientIndex) in group.patientGroups"
              :key="`patient-${group._originalIndex}-${patientIndex}`">
              <tr v-for="(item, itemIndex) in patientGroup.items"
                :key="`detail-${group._originalIndex}-${patientIndex}-${itemIndex}`"
                class="detail-row detail-row-enter-active">
                <!-- 환자 정보 (첫 번째 아이템에서만 표시하고 rowspan 적용, 중증수급자 가산 포함) -->
                <td v-if="itemIndex === 0" class="detail-cell"
                  :rowspan="patientGroup.items.length + getSevereCareAdditionCountForPatient(group._originalIndex, patientGroup.patient_code)">
                  {{ patientGroup.patient_name }} ({{ patientGroup.patient_code }})
                </td>
                <td class="detail-cell time-pay-td text-right">
                  <template v-if="item.suga_name !== '중증수급자 가산'">
                    <span class="time-pay-input" :class="{
                        'no-previous-data': item.noPreviousData,
                        'empty-time-pay': !settingsData[group._originalIndex]?.serviceTimePay?.[item.leb_patient_code]?.[item.suga_name] && !item.time_pay
                      }">
                      {{ settingsData[group._originalIndex]?.serviceTimePay?.[item.leb_patient_code]?.[item.suga_name] || item.time_pay || '시급' }}
                    </span>
                    원
                  </template>
                </td>
                <!-- 개별 데이터 -->
                <td class="detail-cell" v-html="(item.service_days || 0) + '회 * ' + item.suga_name.replace('(', '<br>(')"></td>
                <td class="detail-cell">
                  <template v-if="item.suga_name !== '중증수급자 가산'">
                    {{ formatSugaHours((item.service_days || 0) * parseFloat(String(item.suga_hours ||
                      0).replace(/[^\d.-]/g, ''))) }}
                  </template>
                  <template v-else>
                    {{ item.suga_name }}
                  </template>
                </td>
                <td class="detail-cell text-right pr-2">{{ formatMoney(item.total_suga_money) }}원</td>
                <td class="detail-cell text-right pr-2">{{ formatMoney(calculateMonthlyPay(item.time_pay,
                  (item.service_days || 0) *
                  parseFloat(String(item.suga_hours || 0).replace(/[^\d.-]/g, '')))) }}원</td>
                <td class="detail-cell text-right pr-2">-</td>
              </tr>

              <!-- 해당 환자의 중증수급자 가산 데이터 (환자별로 그룹화) -->
              <tr
                v-for="(severeItem, severeIndex) in getSevereCareAdditionForPatient(group._originalIndex, patientGroup.patient_code)"
                :key="`severe-${group._originalIndex}-${patientIndex}-${severeIndex}`"
                class="detail-row detail-row-enter-active severe-addition-row">
                <!-- 환자 정보는 rowspan으로 이미 표시됨 (첫 번째 td 생략) -->
                <td class="detail-cell time-pay-td text-right">
                  <!-- 중증수급자 가산은 시급 없음 -->
                  -
                </td>
                <td class="detail-cell">{{ severeItem.service_days || 0 }}회 * {{ severeItem.suga_name }}</td>
                <td class="detail-cell">
                  <span class="severe-badge">중증가산</span>
                </td>
                <td class="detail-cell text-right pr-2">{{ formatMoney(severeItem.total_suga_money) }}원</td>
                <td class="detail-cell text-right pr-2">-</td>
                <td class="detail-cell text-right pr-2">{{ formatMoney(severeItem.total_suga_money) }}원</td>
              </tr>
            </template>
            <tr class="pa-0" style="background-color: #fff;">
              <td colspan="8">
                <div class="payroll-details-compact">
                  <!-- 탭 네비게이션 -->
                  <v-tabs v-model="activeTab[group._originalIndex]" class="mb-4">
                    <v-tab value="summary">급여 상세</v-tab>
                    <v-tab value="services">서비스 내역</v-tab>
                    <v-tab value="weekly">주차별 근무내역</v-tab>
                    <v-tab value="settings">
                      <v-icon size="18" class="mr-1">mdi-cog</v-icon>
                      설정
                    </v-tab>
                  </v-tabs>

                  <v-window v-model="activeTab[group._originalIndex]">
                    <!-- 1탭: 기존 급여 상세 정보 -->
                    <v-window-item value="summary">
                      <div class="payroll-grid">
                        <!-- 기본정보 -->
                        <div class="info-section">
                          <h4 class="section-title">기본정보</h4>
                          <div class="info-grid">
                            <div class="info-item">
                              <span class="info-label">주민번호</span>
                              <span class="info-value">
                                {{ formatBirthToJumin(group.caregiverInfo.caregiver_birth,
                                group.caregiverInfo.lem_gender)}}
                                <span class="age-badge-info">(만 {{ calculateAge(group.caregiverInfo.caregiver_birth,
                                  searchParams.lebYm) }}세)</span>
                              </span>
                            </div>
                            <div class="info-item">
                              <span class="info-label">입사일</span>
                              <span class="info-value">{{ group.caregiverInfo.lem_work_start_date || "-" }}</span>
                            </div>
                          </div>
                        </div>
                        <!-- 참고자료 -->
                        <div class="info-section">
                          <h4 class="section-title">참고자료</h4>
                          <div class="info-grid">
                            <div class="info-item">
                              <span class="comparison-label">총수가금액</span>
                              <span class="comparison-value">{{ formatMoney(group.totals.total_suga_money || 0)
                                }}원</span>
                            </div>
                            <div class="info-item">
                              <span class="comparison-label">기관 수익</span>
                              <span class="comparison-value" :class="{
                                'text-green': getComparisonAmount(group, group._originalIndex) > 0,
                                'text-red': getComparisonAmount(group, group._originalIndex) < 0
                              }">
                                {{ formatMoney(getComparisonAmount(group, group._originalIndex)) }}원
                              </span>
                            </div>
                            <div class="info-item">
                              <span class="comparison-label">실수령액</span>
                              <span class="comparison-value">{{ formatMoney(group.totals.netPay) }}원</span>
                            </div>
                          </div>
                        </div>
                        <!-- 기본 급여 -->
                        <div class="payroll-section">
                          <h4 class="section-title">급여내역</h4>

                          <div class="payroll-group payroll-group--salary">
                            <div class="payroll-group__title">급여</div>
                            <div class="payroll-grid payroll-group__grid">
                              <div class="payroll-item">
                                <span class="payroll-label">기본급</span>
                                <span class="payroll-value">
                                  {{ 
                                    isAllFamilyCaregiver(group) 
                                      ? formatMoney(group.totals.basicTotal) 
                                      : formatMoney((taxData?.tr_time_pay || 0) * group.totals.suga_hours) 
                                  }}원
                                </span>
                              </div>
                              <div v-if="!isAllFamilyCaregiver(group)" class="payroll-item">
                                <span class="payroll-label">주휴수당</span>
                                <span class="payroll-value">
                                  {{ formatMoney((taxData?.tr_time_pay || 0) * group.totals.suga_hours * 20 / 100) }}원
                                </span>
                              </div>
                              <div v-if="!isAllFamilyCaregiver(group)" class="payroll-item">
                                <span class="payroll-label">연차수당</span>
                                <span class="payroll-value">
                                  {{ formatMoney(group.totals.vacationPay || 0) }}원
                                </span>
                              </div>
                              <div v-if="!isAllFamilyCaregiver(group)" class="payroll-item">
                                <span class="payroll-label">식대</span>
                                <span class="payroll-value">{{ formatMoney(group.allowances.meal) }}원</span>
                              </div>
                            </div>
                            <div class="payroll-subtotal payroll-subtotal--salary">
                              <span class="payroll-subtotal__label">급여 소계</span>
                              <span class="payroll-subtotal__value">
                                {{ formatMoney(group.totals.basicTotal || 0) }}원
                              </span>
                            </div>
                          </div>

                          <div class="payroll-group payroll-group--allowance">
                            <div class="payroll-group__title">수당(초과근무 및 정부지원관련)</div>
                            <div class="payroll-grid payroll-group__grid">
                              <div class="payroll-item">
                                <span class="payroll-label">휴일수당</span>
                                <span class="payroll-value">{{ formatMoney(group.allowances.holiday || 0) }}원</span>
                              </div>
                              <div class="payroll-item">
                                <span class="payroll-label">연장수당</span>
                                <span class="payroll-value">{{ formatMoney(group.allowances.overtime || 0) }}원</span>
                              </div>
                              <div class="payroll-item">
                                <span class="payroll-label">장기근속수당</span>
                                <span class="payroll-value">{{ formatMoney(group.allowances.longService) }}원</span>
                              </div>
                              <div class="payroll-item">
                                <span class="payroll-label">보수교육비</span>
                                <span class="payroll-value">{{ formatMoney(group.allowances.bosuEduPay) }}원</span>
                              </div>
                              <div class="payroll-item">
                                <span class="payroll-label">중증수급자가산</span>
                                <span class="payroll-value">{{ formatMoney(group.allowances.severeCareAddition) }}원</span>
                              </div>
                              <div class="payroll-item" v-if="searchParams.lebYm.substring(4, 6) === '05' && parseInt(searchParams.lebYm.substring(0, 4)) <= 2025">
                                <span class="payroll-label">근로자의날 수당</span>
                                <span class="payroll-value">{{ formatMoney(group.allowances.laborDay) }}원</span>
                              </div>
                            </div>
                            <div class="payroll-subtotal payroll-subtotal--allowance">
                              <span class="payroll-subtotal__label">수당 소계</span>
                              <span class="payroll-subtotal__value">
                                {{ formatMoney(
                                  (group.allowances.holiday || 0) +
                                  (group.allowances.overtime || 0) +
                                  (group.allowances.longService || 0) +
                                  (group.allowances.severeCareAddition || 0) +
                                  (searchParams.lebYm.substring(4, 6) === '05' && parseInt(searchParams.lebYm.substring(0, 4)) <= 2025 ? (group.allowances.laborDay || 0) : 0)
                                ) }}원
                              </span>
                            </div>
                          </div>

                          <div class="payroll-total">
                            <span class="payroll-total__label">급여내역 합계</span>
                            <span class="payroll-total__value">{{ formatMoney(group.totals.totalPay) }}원</span>
                          </div>
                        </div>
 <!-- 과세/비과세 구분 -->
 <div class="payroll-section">
                          <h4 class="section-title">과세/비과세 구분</h4>
                          <div class="payroll-grid">
                            <div class="payroll-item">
                              <span class="payroll-label">과세 금액</span>
                              <span class="payroll-value">{{ formatMoney(group.totals.taxableAmount) }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">비과세 금액</span>
                              <span class="payroll-value text-blue">{{ formatMoney(group.totals.nonTaxableAmount)
                                }}원</span>
                            </div>
                            <div v-if="group.totals.nonTaxableAmount > 0" class="payroll-item">
                              <v-tooltip location="top">
                                <template v-slot:activator="{ props }">
                                  <span class="payroll-label" v-bind="props">실수령액 증가액</span>
                                </template>
                                <span>비과세로 인한 근로자 공제 절감액입니다.</span>
                              </v-tooltip>
                              <span class="payroll-value text-green">{{ formatMoney(group.totals.employeeTaxSavings)
                                }}원</span>
                            </div>
                            <div v-if="group.totals.nonTaxableAmount > 0" class="payroll-item">
                              <v-tooltip location="top">
                                <template v-slot:activator="{ props }">
                                  <span class="payroll-label" v-bind="props">인건비 절감액</span>
                                </template>
                                <span>비과세로 인한 기관 공제 절감액입니다.</span>
                              </v-tooltip>
                              <span class="payroll-value text-green">{{ formatMoney(group.totals.centerTaxSavings)
                                }}원</span>
                            </div>
                          </div>
                          
                          <!-- 비과세 처리 조건 안내 -->
                          <div class="tax-info-box">
                            <div class="tax-info-title">
                              <v-icon size="18" color="info">mdi-information-outline</v-icon>
                              <span>비과세 처리 조건</span>
                            </div>
                            <div class="tax-info-content">
                              <p><strong>식대:</strong> 최대 20만원까지 비과세 (중식 제공 일수 × 2만원, 최대 20만원)</p>
                              <p><strong>휴일수당/연장수당:</strong></p>
                              <ul>
                                <li>급여 소계가 210만원 이하일 때만 비과세 처리 가능</li>
                                <li>각각 최대 20만원까지 비과세 (초과분은 과세)</li>
                                <li>수가시간 60시간 이상 필요</li>
                              </ul>
                              <p class="salary-subtotal-info">
                                <span>현재 급여 소계: </span>
                                <strong :class="{ 'text-red': group.totals.salarySubtotal > 2100000 }">
                                  {{ formatMoney(group.totals.salarySubtotal) }}원
                                </strong>
                                <span v-if="group.totals.salarySubtotal > 2100000" class="warning-text">
                                  (210만원 초과로 휴일/연장수당 비과세 불가)
                                </span>
                              </p>
                            </div>
                          </div>
                        </div>
                        <!-- 근로자 공제 -->
                        <div class="payroll-section">
                          <h4 class="section-title">근로자 공제</h4>
                          <div class="payroll-grid">
                            <div class="payroll-item">
                              <span class="payroll-label">국민연금</span>
                              <span class="payroll-value">{{ formatMoney(group.insurance.nationalPension.amount)
                                }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">건강보험</span>
                              <span class="payroll-value">{{ formatMoney(group.insurance.healthInsurance.amount)
                                }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">장기요양보험</span>
                              <span class="payroll-value">{{ formatMoney(group.insurance.longTermCare.amount) }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">고용보험</span>
                              <span class="payroll-value">{{ formatMoney(group.insurance.employmentInsurance.amount)
                                }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">소득세</span>
                              <span class="payroll-value">{{ formatMoney(group.tax.incomeTax) }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">지방세</span>
                              <span class="payroll-value">{{ formatMoney(group.tax.localTax) }}원</span>
                            </div>
                            <div class="payroll-item">
                              <v-tooltip location="top">
                                <template v-slot:activator="{ props }">
                                  <span class="payroll-label" v-bind="props">지원공제</span>
                                </template>
                                <span>장기근속수당, 보수교육비 및 중증수급자가산에 대한 공제금액입니다.</span>
                              </v-tooltip>
                              <span class="payroll-value">{{ formatMoney(group.insurance.supportDeduction) }}원</span>
                            </div>
                          </div>
                          <div class="insurance-total">
                            <span class="insurance-label font-weight-bold">근로자 공제 합계</span>
                            <span class="insurance-value font-weight-bold">{{ formatMoney(
                              (group.insurance.nationalPension.amount || 0) +
                              (group.insurance.healthInsurance.amount || 0) +
                              (group.insurance.longTermCare.amount || 0) +
                              (group.insurance.employmentInsurance.amount || 0) +
                              (group.tax.incomeTax || 0) +
                              (group.tax.localTax || 0) +
                              (group.insurance.supportDeduction || 0)
                            ) }}원</span>
                          </div>
                        </div>

                        <!-- 기관 공제 -->
                        <div class="payroll-section">
                          <h4 class="section-title">기관 공제</h4>
                          <div class="payroll-grid">
                            <div class="payroll-item">
                              <span class="payroll-label">국민연금</span>
                              <span class="payroll-value">{{ formatMoney(group.insurance.nationalPension.amount)
                                }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">건강보험</span>
                              <span class="payroll-value">{{ formatMoney(group.insurance.healthInsurance.amount)
                                }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">장기요양보험</span>
                              <span class="payroll-value">{{ formatMoney(group.insurance.longTermCare.amount) }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">고용보험</span>
                              <span class="payroll-value">{{ formatMoney(group.insurance.employmentInsurance.amount)
                                }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">실업급여</span>
                              <span class="payroll-value">{{ formatMoney(group.insurance.unemployment.amount) }}원</span>
                            </div>
                            <div class="payroll-item">
                              <span class="payroll-label">산재보험</span>
                              <span class="payroll-value">{{ formatMoney(group.insurance.industrialAccident.amount)
                                }}원</span>
                            </div>
                            <div class="payroll-item" v-if="settingsData[group._originalIndex]?.applyRetirement">
                              <span class="payroll-label">퇴직금</span>
                              <span class="payroll-value">{{ formatMoney(canApplyRetirement(group._originalIndex) ?
                                Math.round((group.totals.totalPay || 0) / 12) : 0) }}원</span>
                            </div>
                          </div>
                          <div class="insurance-total">
                            <span class="insurance-label font-weight-bold">기관 공제 합계</span>
                            <span class="insurance-value font-weight-bold">{{ formatMoney(getCenterDeductionTotal(group,
                              group._originalIndex)) }}원</span>
                          </div>
                        </div>

                       


                      </div>
                    </v-window-item>

                    <!-- 2탭: 서비스 내역 -->
                    <v-window-item value="services">
                      <div class="service-details">
                        <!-- 주차별 40시간 초과 알림 -->
                        <div v-if="getWeeklyOvertime(group._originalIndex).length > 0" class="weekly-overtime-alert">
                          <div class="overtime-header">
                            <v-icon size="18" color="#dc2626">mdi-alert-circle</v-icon>
                            <span class="overtime-title">주 40시간 초과</span>
                          </div>
                          <div class="overtime-items">
                            <div v-for="(week, idx) in getWeeklyOvertime(group._originalIndex)" :key="idx"
                              class="overtime-item">
                              <span class="overtime-week">{{ (week as any).month }}월 {{ (week as any).weekNumber
                                }}주차</span>
                              <span class="overtime-hours">{{ formatWorkHours((week as any).totalMinutes) }}</span>
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="individualServices[group._originalIndex] && individualServices[group._originalIndex].length > 0"
                          class="service-table-container">
                          <table class="service-table">
                            <thead>
                              <tr>
                                <th>서비스일</th>
                                <th>수급자명</th>
                                <th>서비스시간</th>
                                <th>수가명</th>
                                <th>서비스시간(분)</th>
                                <th>수가금액</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(service, serviceIndex) in individualServices[group._originalIndex]"
                                :key="serviceIndex">
                                <td>
                                  <div class="date-with-badge">
                                    {{ service.service_date }}
                                    <span v-if="isSunday(service.service_date)" class="sunday-badge">일</span>
                                    <span v-if="getHolidayName(service.service_date)" class="holiday-chip">
                                      {{ getHolidayName(service.service_date) }}
                                    </span>
                                  </div>
                                </td>
                                <td>{{ service.patient_name }}</td>
                                <td>
                                  {{ service.start_time }}~{{ service.end_time }}
                                  <span v-if="service.is_lunch_time" class="meal-allowance-badge">식대</span>
                                </td>
                                <td>{{ service.suga_name }}</td>
                                <td>{{ formatTotalMinutes(service.service_minutes) }}</td>
                                <td class="text-right">{{ formatMoney(service.suga_money) }}원</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="text-center pa-4">
                          <v-icon size="48" color="grey">mdi-clipboard-text</v-icon>
                          <p class="text-grey mt-2">서비스 내역이 없습니다.</p>
                        </div>
                      </div>
                    </v-window-item>

                    <!-- 3탭: 주차별 근무내역 (일자별 + 주차별 통합) -->
                    <v-window-item value="weekly">
                      <div class="service-details">
                        <div
                          v-if="combinedWeeklyServices[group._originalIndex] && combinedWeeklyServices[group._originalIndex].length > 0"
                          class="service-table-container">
                          <table class="service-table combined-weekly-table">
                            <thead>
                              <tr>
                                <th class="week-column">주차</th>
                                <th class="date-column">서비스일</th>
                                <th class="daily-hours-column">일일 근무시간</th>
                                <th class="weekly-total-column">주간 합계</th>
                              </tr>
                            </thead>
                            <tbody>
                              <template v-for="(weekGroup, weekIndex) in combinedWeeklyServices[group._originalIndex]"
                                :key="`week-${weekIndex}`">
                                <template v-for="(dailyItem, dailyIndex) in weekGroup.daily_items"
                                  :key="`daily-${weekIndex}-${dailyIndex}`">
                                  <tr :class="{ 'week-first-row': dailyIndex === 0, 'week-other-row': Number(dailyIndex) > 0 }">
                                    <!-- 주차 컬럼 (첫 번째 행에만 표시, rowspan 사용) -->
                                    <td v-if="dailyIndex === 0" :rowspan="weekGroup.daily_items.length"
                                      class="week-cell">
                                      <span class="week-badge">{{ weekGroup.service_week }}주차</span>
                                    </td>

                                    <!-- 서비스일 -->
                                    <td class="date-cell">
                                      <div class="date-with-badge">
                                        {{ dailyItem.service_date }}
                                        <span v-if="isSunday(dailyItem.service_date)" class="sunday-badge">일</span>
                                        <span v-if="getHolidayName(dailyItem.service_date)" class="holiday-chip">
                                          {{ getHolidayName(dailyItem.service_date) }}
                                        </span>
                                      </div>
                                    </td>

                                    <!-- 일일 근무시간 -->
                                    <td class="daily-hours-cell"
                                      :class="{ 'overtime': dailyItem.daily_total_hours > 8 }">
                                      {{ dailyItem.daily_total_hours }}시간
                                    </td>

                                    <!-- 주간 합계 (첫 번째 행에만 표시, rowspan 사용) -->
                                    <td v-if="dailyIndex === 0" :rowspan="weekGroup.daily_items.length"
                                      class="weekly-total-cell"
                                      :class="{ 'overtime': weekGroup.weekly_total_hours > 40 }">
                                      {{ weekGroup.weekly_total_hours }}시간
                                    </td>
                                  </tr>
                                </template>
                              </template>
                            </tbody>
                          </table>
                        </div>
                        <div v-else class="text-center pa-4">
                          <v-icon size="48" color="grey">mdi-calendar-clock</v-icon>
                          <p class="text-grey mt-2">주차별 근무내역이 없습니다.</p>
                        </div>
                      </div>
                    </v-window-item>
                    <!-- 4탭: 설정 -->
                    <v-window-item value="settings">
                      <div class="settings-container">
                        <div class="settings-grid">
                          <!-- 왼쪽 열 -->
                          <div class="settings-column">
                            <!-- 서비스 종류별 시급 설정 -->
                            <div class="settings-section">
                              <h4 class="settings-section-title">서비스 종류별 시급</h4>
                              <div class="service-time-pay-table">
                                <table class="settings-table">
                                  <thead>
                                    <tr>
                                      <th>수급자명</th>
                                      <th>수가명</th>
                                      <th>시급</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <template
                                      v-for="(patientItem, patientIndex) in getPatientSugaNames(group._originalIndex)"
                                      :key="`patient-${group._originalIndex}-${patientIndex}`">
                                      <tr v-for="(sugaName, sugaIndex) in patientItem.sugaNames"
                                        :key="`suga-${group._originalIndex}-${patientIndex}-${sugaIndex}`">
                                        <td v-if="sugaIndex === 0" :rowspan="patientItem.sugaNames.length"
                                          class="patient-name-cell">
                                          <span class="patient-name-wrapper">
                                            {{ patientItem.patientName }}
                                            <span class="patient-code-text">({{ patientItem.patientCode }})</span>
                                          </span>
                                        </td>
                                        <td class="suga-name-cell">{{ sugaName }}</td>
                                        <td class="time-pay-cell">
                                          <input
                                            :value="settingsData[group._originalIndex]?.serviceTimePay?.[patientItem.patientCode]?.[sugaName] || ''"
                                            type="text" class="time-pay-input" :class="{
                                              'no-previous-data': getNoPreviousDataFlag(group._originalIndex, patientItem.patientCode, sugaName),
                                              'empty-time-pay': !settingsData[group._originalIndex]?.serviceTimePay?.[patientItem.patientCode]?.[sugaName]
                                            }" :placeholder="'시급'" maxlength="9"
                                            :disabled="isMagam"
                                            @focus="onSettingsTimePayFocus($event, group._originalIndex, patientItem.patientCode, sugaName)"
                                            @blur="onSettingsTimePayBlur($event, group._originalIndex, patientItem.patientCode, sugaName)"
                                            @input="onSettingsTimePayInput($event, group._originalIndex, patientItem.patientCode, sugaName)" />
                                          <span class="input-suffix">원</span>
                                        </td>
                                      </tr>
                                    </template>
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            <!-- 계산 적용 여부 설정 -->
                            <div class="settings-section">
                              <h4 class="settings-section-title">계산 적용 여부 설정</h4>
                              <div class="insurance-settings">
                                <div class="settings-info-item">
                                  <span class="settings-info-value">만 나이: {{
                                    calculateAge(group.caregiverInfo.caregiver_birth,
                                    searchParams.lebYm) }}세</span>
                                </div>
                                <div class="settings-input-item">
                                  <div
                                    style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                                    <span class="checkbox-label">국민연금</span>
                                    <div style="display: flex; align-items: center; gap: 8px;">
                                      <input 
                                        type="text"
                                        v-model="settingsData[group._originalIndex].yeonPay"
                                        @input="onYeonPayInput($event, group._originalIndex)"
                                        @blur="onYeonPayBlur($event, group._originalIndex)"
                                        @focus="onYeonPayFocus($event, group._originalIndex)"
                                        class="yeon-pay-input"
                                        placeholder="0"
                                        :disabled="isMagam"
                                        style="width: 100px; text-align: right; padding: 4px 8px; border: 1px solid #ddd; border-radius: 4px;"
                                      />
                                      <span style="font-size: 12px; color: #666;">원</span>
                                    </div>
                                  </div>
                                </div>
                                <div class="settings-checkbox-item">
                                  <div
                                    style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                                    <span class="checkbox-label">건강보험 적용</span>
                                    <div style="display: flex; align-items: center; gap: 12px;">
                                      <v-checkbox v-model="settingsData[group._originalIndex].applyHealthInsurance"
                                        hide-details density="compact" :disabled="isMagam"></v-checkbox>
                                    </div>
                                  </div>
                                </div>
                                <div class="settings-checkbox-item">
                                  <div
                                    style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                                    <span class="checkbox-label">고용보험 적용</span>
                                    <div style="display: flex; align-items: center; gap: 12px;">
                                      <v-checkbox v-model="settingsData[group._originalIndex].applyEmploymentInsurance"
                                        hide-details density="compact" :disabled="isMagam"></v-checkbox>
                                    </div>
                                  </div>
                                </div>
                                <div class="settings-checkbox-item">
                                  <div style="display: flex; flex-direction: column; gap: 4px; width: 100%;">
                                    <div
                                      style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                                      <span class="checkbox-label">퇴직금 적립</span>
                                      <div style="display: flex; align-items: center; gap: 12px;">
                                        <span v-if="canApplyRetirement(group._originalIndex)"
                                          class="settings-info-value">
                                          {{ formatMoney(Math.round((group.totals.totalPay || 0) / 12)) }}원
                                        </span>
                                        <v-checkbox
                                          :model-value="canApplyRetirement(group._originalIndex) && settingsData[group._originalIndex].applyRetirement"
                                          @update:model-value="(val: boolean) => { if (canApplyRetirement(group._originalIndex)) settingsData[group._originalIndex].applyRetirement = val }"
                                          hide-details density="compact"
                                          :disabled="!canApplyRetirement(group._originalIndex) || isMagam"></v-checkbox>
                                      </div>
                                    </div>
                                    <div v-if="!canApplyRetirement(group._originalIndex)"
                                      style="color: #dc2626; font-size: 0.75rem; white-space: pre-line; padding-left: 0;">
                                      {{ getRetirementDisabledReason(group._originalIndex) }}
                                    </div>
                                    <div v-else
                                      style="color: #10b981; font-size: 0.75rem; white-space: pre-line; padding-left: 0;">
                                      {{ getRetirementApplyInfo(group._originalIndex) }}
                                    </div>
                                  </div>
                                </div>
                                <div v-if="searchParams.lebYm.substring(4, 6) === '05' && parseInt(searchParams.lebYm.substring(0, 4)) <= 2025" class="settings-checkbox-item">
                                  <div style="display: flex; flex-direction: column; gap: 4px; width: 100%;">
                                    <div
                                      style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                                      <span class="checkbox-label">근로자의날 수당 지급</span>
                                      <div style="display: flex; align-items: center; gap: 12px;">
                                        <span v-if="canApplyLaborDayAllowance(group._originalIndex)"
                                          class="settings-info-value">
                                          {{ formatMoney(group.allowances.laborDay || 0) }}원
                                        </span>
                                        <v-checkbox
                                          :model-value="canApplyLaborDayAllowance(group._originalIndex) && settingsData[group._originalIndex].applyLaborDayAllowance"
                                          @update:model-value="(val: boolean) => { if (canApplyLaborDayAllowance(group._originalIndex)) settingsData[group._originalIndex].applyLaborDayAllowance = val }"
                                          hide-details density="compact"
                                          :disabled="!canApplyLaborDayAllowance(group._originalIndex) || isMagam"></v-checkbox>
                                      </div>
                                    </div>
                                    <div v-if="!canApplyLaborDayAllowance(group._originalIndex)"
                                      style="color: #dc2626; font-size: 0.75rem; white-space: pre-line; padding-left: 0;">
                                      월 수가시간이 60시간 미만입니다.<br />근로자의날 수당을 지급할 수 없습니다.
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- 식대 설정 -->
                            <div class="settings-section">
                              <h4 class="settings-section-title">식대</h4>
                              <div class="meal-settings">
                                <div class="settings-info-item">
                                  <span class="settings-info-label">식대 지급 가능 일수:</span>
                                  <span class="settings-info-value">{{ group.totals.lunch_time_count || 0 }}일</span>
                                </div>
                                <div class="settings-info-item">
                                  <span class="settings-info-label">식대 금액 (지급시):</span>
                                  <span class="settings-info-value meal-amount">{{
                                    formatMoney(getMealAllowanceAmount(group._originalIndex, true)) }}원</span>
                                </div>
                                <div class="settings-info-item">
                                  <span class="settings-info-label">식대 금액 (미지급시):</span>
                                  <span class="settings-info-value meal-amount">{{
                                    formatMoney(getMealAllowanceAmount(group._originalIndex, false)) }}원</span>
                                </div>
                                <div class="settings-checkbox-item">
                                  <div
                                    style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                                    <span class="checkbox-label">식대 지급</span>
                                    <div style="display: flex; align-items: center; gap: 12px;">
                                      <v-checkbox v-model="settingsData[group._originalIndex].applyMealAllowance"
                                        hide-details density="compact" :disabled="isMagam"></v-checkbox>
                                    </div>
                                  </div>
                                </div>
                                <div
                                  v-if="settingsData[group._originalIndex] && settingsData[group._originalIndex].applyMealAllowance"
                                  class="settings-info-item active">
                                  <span class="settings-info-label">적용 식대:</span>
                                  <span class="settings-info-value active-amount">{{ formatMoney(group.allowances.meal
                                    || 0)
                                    }}원</span>
                                </div>
                                <div v-else class="settings-info-item inactive">
                                  <span class="settings-info-label">적용 식대:</span>
                                  <span class="settings-info-value inactive-amount">{{ formatMoney(group.allowances.meal
                                    || 0)
                                    }}원</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- 오른쪽 열 -->
                          <div class="settings-column">
                            <!-- 장기근속수당 설정 -->
                            <div class="settings-section">
                              <h4 class="settings-section-title">장기근속수당</h4>
                              <div class="service-time-pay-table">
                                <table class="settings-table">
                                  <thead>
                                    <tr>
                                      <th>입사일</th>
                                      <th>근속기간</th>
                                      <th>장기근속수당</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td class="patient-name-cell">{{ group.caregiverInfo.lem_work_start_date || '-' }}
                                      </td>
                                      <td class="suga-name-cell">{{ getWorkPeriodText(group.caregiverInfo.lem_work_start_date) }}</td>
                                      <td class="time-pay-cell">
                                        <input :value="settingsData[group._originalIndex]?.longServiceAllowance || ''"
                                          type="text" class="time-pay-input" placeholder="금액 입력" maxlength="9"
                                          :disabled="isMagam"
                                          @focus="onLongServiceFocus($event, group._originalIndex)"
                                          @blur="onLongServiceBlur($event, group._originalIndex)"
                                          @input="onLongServiceInput($event, group._originalIndex)" />
                                        <span class="input-suffix">원</span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            <!-- 보수교육비 설정 -->
                            <div class="settings-section">
                              <h4 class="settings-section-title">보수교육비</h4>
                              <div class="service-time-pay-table">
                                <table class="settings-table">
                                  <thead>
                                    <tr>
                                      <th>입사일</th>
                                      <th>근속기간</th>
                                      <th>보수교육비</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr>
                                      <td class="patient-name-cell">{{ group.caregiverInfo.lem_work_start_date || '-' }}
                                      </td>
                                      <td class="suga-name-cell">{{ getWorkPeriodText(group.caregiverInfo.lem_work_start_date) }}</td>
                                      <td class="time-pay-cell">
                                        <input :value="settingsData[group._originalIndex]?.bosuEduPayAllowance || ''"
                                          type="text" class="time-pay-input" placeholder="금액 입력" maxlength="9"
                                          :disabled="isMagam"
                                          @focus="onBosuEduPayFocus($event, group._originalIndex)"
                                          @blur="onBosuEduPayBlur($event, group._originalIndex)"
                                          @input="onBosuEduPayInput($event, group._originalIndex)" />
                                        <span class="input-suffix">원</span>
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>

                            <!-- 휴일수당 설정 -->
                            <div class="settings-section">
                              <h4 class="settings-section-title">휴일수당 (공휴일가산)</h4>
                              <div class="holiday-settings">
                                <div class="settings-info-item">
                                  <span class="settings-info-label">공휴일가산 서비스:</span>
                                  <span class="settings-info-value">{{ getHolidayCount(group._originalIndex) }}회</span>
                                </div>
                                <div class="settings-info-item">
                                  <span class="settings-info-label">공휴일가산 총시간:</span>
                                  <span class="settings-info-value">{{
                                    formatWorkHours(getHolidayTotalMinutes(group._originalIndex))
                                    }}</span>
                                </div>
                                <div class="settings-info-item">
                                  <span class="settings-info-label">휴일수당 금액:</span>
                                  <span class="settings-info-value holiday-amount">{{
                                    formatMoney(getHolidayAllowanceAmount(group._originalIndex, true)) }}원</span>
                                </div>
                                <div class="settings-checkbox-item">
                                  <div style="display: flex; flex-direction: column; gap: 4px; width: 100%;">
                                    <div
                                      style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                                      <span class="checkbox-label">휴일수당 지급</span>
                                      <div style="display: flex; align-items: center; gap: 12px;">
                                        <v-checkbox
                                          :model-value="canApplyHolidayAllowance(group._originalIndex) && settingsData[group._originalIndex].applyHolidayAllowance"
                                          @update:model-value="(val: boolean) => { if (canApplyHolidayAllowance(group._originalIndex)) settingsData[group._originalIndex].applyHolidayAllowance = val }"
                                          hide-details density="compact"
                                          :disabled="!canApplyHolidayAllowance(group._originalIndex) || isMagam"></v-checkbox>
                                      </div>
                                    </div>
                                    <div v-if="!canApplyHolidayAllowance(group._originalIndex)"
                                      style="color: #dc2626; font-size: 0.75rem; white-space: pre-line; padding-left: 0;">
                                      {{ getHolidayAllowanceDisabledReason(group._originalIndex) }}
                                    </div>
                                  </div>
                                </div>

                              </div>
                            </div>

                            <!-- 연장수당 설정 -->
                            <div class="settings-section">
                              <h4 class="settings-section-title">연장수당</h4>
                              <div class="overtime-settings">
                                <div class="settings-info-item">
                                  <span class="settings-info-label">연장수당 개수:</span>
                                  <span class="settings-info-value">{{ getOvertimeCount(group._originalIndex) }}일</span>
                                </div>
                                <div class="settings-info-item">
                                  <span class="settings-info-label">연장 근무 총시간:</span>
                                  <span class="settings-info-value">{{
                                    formatWorkHours(getOvertimeTotalMinutes(group._originalIndex)) }}</span>
                                </div>
                                <div class="settings-info-item">
                                  <span class="settings-info-label">연장수당 금액:</span>
                                  <span class="settings-info-value overtime-amount">{{
                                    formatMoney(getOvertimeAllowanceAmount(group._originalIndex, true)) }}원</span>
                                </div>
                                <div class="settings-checkbox-item">
                                  <div style="display: flex; flex-direction: column; gap: 4px; width: 100%;">
                                    <div
                                      style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                                      <span class="checkbox-label">연장수당 지급</span>
                                      <div style="display: flex; align-items: center; gap: 12px;">
                                        <v-checkbox
                                          :model-value="canApplyOvertimeAllowance(group._originalIndex) && settingsData[group._originalIndex].applyOvertimeAllowance"
                                          @update:model-value="(val: boolean) => { if (canApplyOvertimeAllowance(group._originalIndex)) settingsData[group._originalIndex].applyOvertimeAllowance = val }"
                                          hide-details density="compact"
                                          :disabled="!canApplyOvertimeAllowance(group._originalIndex) || isMagam"></v-checkbox>
                                      </div>
                                    </div>
                                    <div v-if="!canApplyOvertimeAllowance(group._originalIndex)"
                                      style="color: #dc2626; font-size: 0.75rem; white-space: pre-line; padding-left: 0;">
                                      수가시간 60시간 미만으로, 연장수당은 지급되지 않습니다.
                                    </div>
                                  </div>
                                </div>
                               
                              </div>
                            </div>


                          </div>
                        </div>

                        <!-- 설정 저장 버튼 -->
                        <div class="settings-actions">
                          <v-btn color="#1e3a8a" size="large" variant="elevated"
                            @click="saveSingleCaregiver(group._originalIndex)" :loading="saving"
                            :disabled="memberJoinData.length === 0 || !hasTimePayData || isMagam" class="save-settings-btn">
                            <v-icon size="20">mdi-content-save</v-icon>
                            <span class="ml-2">저장</span>
                          </v-btn>
                        </div>
                      </div>
                    </v-window-item>
                  </v-window>
                </div>
              </td>
            </tr>
          </template>
        </template>
      </tbody>
    </table>
  </div>
  <div v-else class="empty-data-message text-center pa-4">
    <v-icon size="64" color="grey">mdi-database-off</v-icon>
    <p class="text-grey mt-2">조회된 데이터가 없습니다.</p>
  </div>

  <!-- 로딩 오버레이 -->
  <v-overlay v-model="loading" class="align-center justify-center">
    <v-progress-circular color="primary" indeterminate size="64"></v-progress-circular>
  </v-overlay>

  <!-- 스낵바 -->
  <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
    {{ snackbar.message }}
    <template v-slot:actions>
      <v-btn variant="text" @click="snackbar.show = false"> 닫기 </v-btn>
    </template>
  </v-snackbar>

  <!-- 설정 모달 -->
  <SetModal v-model="showSetModal" :leb-ym="searchParams.lebYm" :sisul-code="searchParams.sisulCode"
    :current-data="memberJoinData" :isMagam="isMagam" @apply-bulk-time-pay="applyBulkTimePay"
    @apply-bulk-time-pay-family-60="applyBulkTimePayFamily60"
    @apply-bulk-time-pay-family-90="applyBulkTimePayFamily90"
    @apply-bulk-time-pay-from-previous="applyBulkTimePayFromPrevious"
    @apply-bulk-health-insurance="applyBulkHealthInsurance"
    @apply-bulk-holiday-allowance="applyBulkHolidayAllowance"
    @apply-bulk-holiday-allowance-from-previous="applyBulkHolidayAllowanceFromPrevious"
    @apply-bulk-overtime-allowance="applyBulkOvertimeAllowance"
    @apply-bulk-overtime-allowance-from-previous="applyBulkOvertimeAllowanceFromPrevious"
    @apply-bulk-retirement="applyBulkRetirement" @apply-bulk-retirement-from-previous="applyBulkRetirementFromPrevious"
    @apply-bulk-meal-allowance="applyBulkMealAllowance"
    @apply-bulk-meal-allowance-from-previous="applyBulkMealAllowanceFromPrevious"
    @apply-bulk-long-service-auto-calculate="applyBulkLongServiceAutoCalculate"
    @apply-bulk-long-service-clear="applyBulkLongServiceClear"
    @apply-bulk-long-service-from-previous="applyBulkLongServiceFromPrevious"
    @apply-bulk-bosu-edu-pay="applyBulkBosuEduPay"
    @apply-bulk-bosu-edu-pay-clear="applyBulkBosuEduPayClear"
    @apply-bulk-insurance-from-previous="applyBulkInsuranceFromPrevious"
    @apply-bulk-labor-day-allowance="applyBulkLaborDayAllowance"
    @apply-all-previous-settings="handleApplyAllPreviousSettings" @save-and-close="handleSaveAndClose" />

  <!-- 엑셀 다운로드 컴포넌트 -->
  <WageReportExcel ref="wageReportExcelRef" 
    :grouped-member-data="groupedMemberData" 
    :leb-ym="searchParams.lebYm"
    :sisul-code="searchParams.sisulCode"
    :tax-data="taxData"
    :settings-data="settingsData"
    :sabok-staff-data="props.sabokStaffData"
    :sabok-staff-settings="props.sabokStaffSettings"
    :work-info-data="[]"
    @show-snackbar="showSnackbar" />
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useStore } from "vuex";
import axios from "axios";
import YearMonthSelector from "@/components/common/YearMonthSelector.vue";
import SetModal from "@/components/wage/SetModal.vue";
import WageReportExcel from "@/components/wage/WageReportExcel.vue";

interface Props {
  sabokStaffData?: any[];
  sabokStaffSettings?: any;
  isMagam?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isMagam: false
});

const store = useStore();

// 반응형 데이터
const loading = ref(false);
const saving = ref(false);

// 연월 변경 확인을 위한 플래그
const isInitialized = ref(false);
const pendingLebYm = ref<string>('');
const isProcessingLebYmChange = ref(false);

// 설정 모달 관련
const showSetModal = ref(false);

// 현재 년월 가져오기 (YYYYMM 형식)
const getCurrentYearMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}${month}`;
};

// 검색 파라미터 (스토어에 저장된 날짜 또는 현재 날짜 사용)
const searchParams = ref({
  sisulCode: "", // 초기값은 빈 문자열
  lebYm: store.state.lebYm || getCurrentYearMonth(), // 스토어에 저장된 날짜 또는 현재 년월
});

// 필터링 파라미터
const filterParams = ref({
  caregiverName: "",
  patientName: ""
});

// 사용자 정보에서 시설코드 가져오기 (반응형)
const userSisulCode = computed(() => store.state.user?.m_sisul_code || "");

// 사용자 정보가 로드되면 시설코드 자동 설정
watch(
  userSisulCode,
  (newCode) => {
    if (newCode) {
      searchParams.value.sisulCode = newCode;
    }
  },
  { immediate: true }
);

// 년월이 변경되면 스토어에 저장
watch(
  () => searchParams.value.lebYm,
  (newLebYm) => {
    if (newLebYm) {
      store.commit('setLebYm', newLebYm);
    }
  }
);

// 스토어의 lebYm이 변경되면 searchParams.lebYm도 업데이트 (외부에서 쿼리 파라미터로 설정된 경우)
watch(
  () => store.state.lebYm,
  (newLebYm) => {
    if (newLebYm && newLebYm !== searchParams.value.lebYm) {
      // 초기화 전에는 무조건 업데이트
      if (!isInitialized.value) {
        searchParams.value.lebYm = newLebYm;
      }
    }
  },
  { immediate: true }
);

// 년월 변경 시 저장 확인 후 자동 조회
watch(
  () => searchParams.value.lebYm,
  async (newLebYm, oldLebYm) => {
    // 처리 중이면 무시
    if (isProcessingLebYmChange.value) {
      return;
    }

    // 초기 로드 시에는 확인 메시지 표시하지 않고 바로 조회
    if (!isInitialized.value || !oldLebYm) {
      if (newLebYm && searchParams.value.sisulCode) {
        await getTax();
        await fetchData();
        const year = newLebYm.substring(0, 4);
        await loadHolidays(year);

        await nextTick();
        if (groupedMemberData.value && groupedMemberData.value.length > 0) {
          isCalculating.value = true;
          try {
            groupedMemberData.value.forEach((group: any, groupIndex: number) => {
              calculateGroupTotals(group, groupIndex);
            });
          } finally {
            isCalculating.value = false;
          }
        }
        
        // 첫 번째 로드 완료
        isInitialized.value = true;
      }
      return;
    }

    // 연월이 실제로 변경된 경우
    if (newLebYm && oldLebYm && newLebYm !== oldLebYm) {
      // 데이터가 있으면 확인 메시지 표시
      if (memberJoinData.value.length > 0) {
        const confirmed = confirm('연월을 변경하시겠습니까?\n\n"확인"을 누르면 저장 후 변경됩니다.\n"취소"를 누르면 저장하지 않고 변경됩니다.');
        
        if (confirmed) {
          // 저장 실행
          await saveAllData();
        }
      }
      
      // 확인 여부와 관계없이 연월 변경 허용
      if (searchParams.value.sisulCode) {
        await getTax();
        await fetchData();
        const year = newLebYm.substring(0, 4);
        await loadHolidays(year);

        await nextTick();
        if (groupedMemberData.value && groupedMemberData.value.length > 0) {
          isCalculating.value = true;
          try {
            groupedMemberData.value.forEach((group: any, groupIndex: number) => {
              calculateGroupTotals(group, groupIndex);
            });
          } finally {
            isCalculating.value = false;
          }
        }
      }
    }
  }
);


// 데이터
const memberJoinData = ref([]) as any;
const memberJoinDetailData = ref([]) as any; // 중증수급자 가산 포함 데이터
const individualServicesData = ref([]) as any;
const dailyServicesData = ref([]) as any;
const weeklyServicesData = ref([]) as any;

// 세액 조회
const taxData = ref({}) as any;
const sojiData = ref({}) as any;

// 탭 관리
const activeTab = ref({}) as any;

// 설정 데이터 관리
const settingsData = ref({}) as any;

// 개별 서비스 데이터를 요양보호사별로 필터링하는 함수
const getIndividualServicesForGroup = (groupIndex: number) => {
  if (!individualServicesData.value || !groupedMemberData.value || !groupedMemberData.value[groupIndex]) {
    return [];
  }

  const group = groupedMemberData.value[groupIndex];
  const caregiverName = group.caregiverInfo.caregiver_name;
  const caregiverBirth = group.caregiverInfo.caregiver_birth;

  return individualServicesData.value.filter((service: any) =>
    service.caregiver_name === caregiverName &&
    service.caregiver_birth === caregiverBirth
  );
};

// 개별 서비스 데이터를 그룹별로 필터링
const individualServices = computed(() => {
  if (!individualServicesData.value || !groupedMemberData.value) return {};

  const result: any = {};

  groupedMemberData.value.forEach((group: any, groupIndex: number) => {
    result[groupIndex] = getIndividualServicesForGroup(groupIndex);
  });

  return result;
});

// 일자별 서비스 데이터를 그룹별로 필터링
const dailyServices = computed(() => {
  if (!dailyServicesData.value || !groupedMemberData.value) return {};

  const result: any = {};

  groupedMemberData.value.forEach((group: any, groupIndex: number) => {
    const caregiverName = group.caregiverInfo.caregiver_name;
    const caregiverBirth = group.caregiverInfo.caregiver_birth;

    // 해당 요양보호사의 일자별 서비스 데이터 필터링
    result[groupIndex] = dailyServicesData.value.filter((service: any) =>
      service.caregiver_name === caregiverName &&
      service.caregiver_birth === caregiverBirth
    );
  });

  return result;
});

// 주차별 서비스 데이터를 그룹별로 필터링
const weeklyServices = computed(() => {
  if (!weeklyServicesData.value || !groupedMemberData.value) return {};

  const result: any = {};

  groupedMemberData.value.forEach((group: any, groupIndex: number) => {
    const caregiverName = group.caregiverInfo.caregiver_name;
    const caregiverBirth = group.caregiverInfo.caregiver_birth;

    // 해당 요양보호사의 주차별 서비스 데이터 필터링
    result[groupIndex] = weeklyServicesData.value.filter((service: any) =>
      service.caregiver_name === caregiverName &&
      service.caregiver_birth === caregiverBirth
    );
  });

  return result;
});

// 주차별로 일자별 데이터를 그룹화 (멋진 표를 위한 데이터 구조)
const combinedWeeklyServices = computed(() => {
  if (!dailyServicesData.value || !weeklyServicesData.value || !groupedMemberData.value) return {};

  const result: any = {};

  groupedMemberData.value.forEach((group: any, groupIndex: number) => {
    const caregiverName = group.caregiverInfo.caregiver_name;
    const caregiverBirth = group.caregiverInfo.caregiver_birth;

    // 해당 요양보호사의 일자별 데이터
    const dailyData = dailyServicesData.value.filter((service: any) =>
      service.caregiver_name === caregiverName &&
      service.caregiver_birth === caregiverBirth
    );

    // 해당 요양보호사의 주차별 데이터
    const weeklyData = weeklyServicesData.value.filter((service: any) =>
      service.caregiver_name === caregiverName &&
      service.caregiver_birth === caregiverBirth
    );

    // 주차별로 그룹화
    const weeklyGroups: any = {};

    dailyData.forEach((daily: any) => {
      const weekKey = daily.service_week;
      if (!weeklyGroups[weekKey]) {
        // 해당 주차의 합계 정보 찾기
        const weekTotal = weeklyData.find((w: any) => w.service_week === weekKey);

        weeklyGroups[weekKey] = {
          service_week: weekKey,
          weekly_total_hours: weekTotal ? weekTotal.weekly_total_hours : 0,
          daily_items: []
        };
      }
      weeklyGroups[weekKey].daily_items.push(daily);
    });

    // 주차별로 정렬된 배열로 변환
    result[groupIndex] = Object.values(weeklyGroups).sort((a: any, b: any) => a.service_week - b.service_week);
  });

  return result;
});

// 펼쳐진 행들을 추적하는 반응형 데이터
const expandedRows = ref(new Set<number>());

// 테이블 소팅 상태
const sortColumn = ref<string | null>(null);
const sortDirection = ref<'asc' | 'desc'>('asc');

// 시급이 입력된 데이터가 있는지 확인하는 computed
const hasTimePayData = computed(() => {
  return memberJoinData.value.some((item: any) =>
    item.time_pay && item.time_pay.trim() !== '' && item.time_pay !== '0'
  );
});

// 전체 요약 정보 계산 함수
const calculateSummaryInfo = () => {
  if (!memberJoinData.value || memberJoinData.value.length === 0) {
    summaryInfo.value = {
      totalCaregivers: 0,
      totalPatients: 0,
      totalSugaMoney: 0,
      totalMonthlyPay: 0,
      totalTaxSavings: 0,
      totalEmployeeTaxSavings: 0,
      totalCenterTaxSavings: 0,
      totalServiceDays: 0,
      totalSugaHours: 0
    };
    return;
  }

  // 고유한 요양보호사 수
  const uniqueCaregivers = new Set();
  // 고유한 수급자 수
  const uniquePatients = new Set();

  let totalSugaMoney = 0;
  let totalMonthlyPay = 0;
  let totalTaxSavings = 0;
  let totalEmployeeTaxSavings = 0;
  let totalCenterTaxSavings = 0;
  let totalServiceDays = 0;
  let totalSugaHours = 0;

  memberJoinData.value.forEach((item: any) => {
    // 요양보호사 카운트
    const caregiverKey = `${item.caregiver_name}_${item.caregiver_birth}_${item.lem_gender}`;
    uniqueCaregivers.add(caregiverKey);

    // 수급자 카운트
    const patientKey = `${item.leb_patient_code}_${item.leb_patient_name}`;
    uniquePatients.add(patientKey);

    // 수가금액 합계
    totalSugaMoney += parseFloat(item.total_suga_money || 0);

    // 서비스일수 합계
    const serviceDays = item.service_days || 0;
    totalServiceDays += serviceDays;

    // 수가시간 합계
    const sugaHours = parseFloat(String(item.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;
    const calculatedSugaHours = serviceDays * sugaHours;
    totalSugaHours += calculatedSugaHours;
  });

  // 월급여 합계 및 비과세 혜택 합계 (그룹화된 데이터에서 계산)
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    // 각 그룹을 순회하면서 totals의 월급여 및 절감액들을 명시적으로 읽어서 합산
    for (let i = 0; i < groupedMemberData.value.length; i++) {
      const group = groupedMemberData.value[i];
      if (group && group.totals) {
        const totals = group.totals;
        
        // 실제 총 급여 (급여내역 합계: 기본급 + 주휴 + 연차 + 모든 수당)
        const totalPay = Number(totals.totalPay) || 0;
        totalMonthlyPay += totalPay;
        
        // 비과세 절감액
        const employeeSavings = Number(totals.employeeTaxSavings) || 0;
        const centerSavings = Number(totals.centerTaxSavings) || 0;
        const taxSavings = Number(totals.taxSavings) || 0;

        totalEmployeeTaxSavings += employeeSavings;
        totalCenterTaxSavings += centerSavings;
        totalTaxSavings += taxSavings;

      }
    }
  }

  summaryInfo.value = {
    totalCaregivers: uniqueCaregivers.size,
    totalPatients: uniquePatients.size,
    totalSugaMoney,
    totalMonthlyPay,
    totalTaxSavings,
    totalEmployeeTaxSavings,
    totalCenterTaxSavings,
    totalServiceDays,
    totalSugaHours
  };
};

// 전체 요약 정보 (ref로 변경하여 수동 업데이트)
const summaryInfo = ref({
  totalCaregivers: 0,
  totalPatients: 0,
  totalSugaMoney: 0,
  totalMonthlyPay: 0,
  totalTaxSavings: 0,
  totalEmployeeTaxSavings: 0,
  totalCenterTaxSavings: 0,
  totalServiceDays: 0,
  totalSugaHours: 0
});

// 필터링된 멤버 데이터
const filteredMemberData = computed(() => {
  if (!memberJoinData.value || memberJoinData.value.length === 0) return [];

  return memberJoinData.value.filter((item: any) => {
    const caregiverMatch = !filterParams.value.caregiverName ||
      item.caregiver_name?.toLowerCase().includes(filterParams.value.caregiverName.toLowerCase());
    const patientMatch = !filterParams.value.patientName ||
      item.leb_patient_name?.toLowerCase().includes(filterParams.value.patientName.toLowerCase());

    return caregiverMatch && patientMatch;
  });
});

// 중증수급자 가산 데이터 (그룹별로 매칭)
const getSevereCareAdditionForGroup = (groupIndex: number) => {
  if (!memberJoinDetailData.value || memberJoinDetailData.value.length === 0) return [];
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return [];

  const group = groupedMemberData.value[groupIndex];
  const caregiverName = group.caregiverInfo.caregiver_name;
  const caregiverBirth = group.caregiverInfo.caregiver_birth;

  // 해당 요양보호사의 중증수급자 가산 데이터 필터링
  return memberJoinDetailData.value.filter((item: any) =>
    item.caregiver_name === caregiverName &&
    item.caregiver_birth === caregiverBirth &&
    item.suga_name === '중증수급자 가산'
  );
};

// 특정 환자의 중증수급자 가산 개수 계산
const getSevereCareAdditionCountForPatient = (groupIndex: number, patientCode: string) => {
  const severeAdditions = getSevereCareAdditionForGroup(groupIndex);
  return severeAdditions.filter((item: any) => item.leb_patient_code === patientCode).length;
};

// 특정 환자의 중증수급자 가산 데이터 가져오기
const getSevereCareAdditionForPatient = (groupIndex: number, patientCode: string) => {
  const severeAdditions = getSevereCareAdditionForGroup(groupIndex);
  return severeAdditions.filter((item: any) => item.leb_patient_code === patientCode);
};

// 그룹별 중증수급자 가산 총액 계산
const getSevereCareAdditionTotal = (groupIndex: number) => {
  const severeAdditions = getSevereCareAdditionForGroup(groupIndex);
  if (!severeAdditions || severeAdditions.length === 0) return 0;

  let total = 0;
  severeAdditions.forEach((item: any) => {
    total += parseFloat(item.total_suga_money || 0);
  });

  return total;
};

// 소팅 함수
const handleSort = (column: string) => {
  if (sortColumn.value === column) {
    // 같은 컬럼 클릭 시 방향 전환
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    // 다른 컬럼 클릭 시 오름차순으로 설정
    sortColumn.value = column;
    sortDirection.value = 'asc';
  }
};
// 원래 그룹 인덱스를 찾는 함수
const findOriginalGroupIndex = (group: any) => {
  if (!groupedMemberData.value) return -1;
  return groupedMemberData.value.findIndex((g: any) =>
    g.caregiverInfo?.caregiver_name === group.caregiverInfo?.caregiver_name &&
    g.caregiverInfo?.caregiver_birth === group.caregiverInfo?.caregiver_birth &&
    g.caregiverInfo?.lem_gender === group.caregiverInfo?.lem_gender &&
    g.caregiverInfo?.lem_license === group.caregiverInfo?.lem_license &&
    g.caregiverInfo?.lem_work_start_date === group.caregiverInfo?.lem_work_start_date &&
    g.caregiverInfo?.lem_work_end_date === group.caregiverInfo?.lem_work_end_date
  );
};

// 소팅된 그룹 데이터 (원래 인덱스 포함)
const sortedGroupedMemberData = computed(() => {
  if (!groupedMemberData.value || groupedMemberData.value.length === 0) return [];
  if (!sortColumn.value) {
    // 소팅하지 않을 때는 원래 인덱스와 함께 반환
    return groupedMemberData.value.map((group: any, index: number) => ({
      ...group,
      _originalIndex: index
    }));
  }

  // 원래 인덱스와 함께 배열 생성
  const indexed = groupedMemberData.value.map((group: any, index: number) => ({
    ...group,
    _originalIndex: index
  }));

  indexed.sort((a: any, b: any) => {
    let aValue: any;
    let bValue: any;

    switch (sortColumn.value) {
      case 'caregiver':
        aValue = a.caregiverInfo?.caregiver_name || '';
        bValue = b.caregiverInfo?.caregiver_name || '';
        break;
      case 'patient':
        aValue = a.patientInfo?.patient_name || '';
        bValue = b.patientInfo?.patient_name || '';
        break;
      case 'timePay':
        aValue = parseFloat(String(a.totals?.average_time_pay || 0).replace(/[^\d]/g, '')) || 0;
        bValue = parseFloat(String(b.totals?.average_time_pay || 0).replace(/[^\d]/g, '')) || 0;
        break;
      case 'serviceDays':
        aValue = a.totals?.service_days || 0;
        bValue = b.totals?.service_days || 0;
        break;
      case 'sugaHours':
        aValue = parseFloat(String(a.totals?.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;
        bValue = parseFloat(String(b.totals?.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;
        break;
      case 'totalSugaMoney':
        aValue = parseFloat(String(a.totals?.total_suga_money || 0).replace(/[^\d.-]/g, '')) || 0;
        bValue = parseFloat(String(b.totals?.total_suga_money || 0).replace(/[^\d.-]/g, '')) || 0;
        break;
      case 'monthlyPay':
        aValue = parseFloat(String(a.totals?.monthly_pay || 0).replace(/[^\d.-]/g, '')) || 0;
        bValue = parseFloat(String(b.totals?.monthly_pay || 0).replace(/[^\d.-]/g, '')) || 0;
        break;
      case 'bonusAllowance':
        aValue = ((a.allowances?.longService || 0) + (a.allowances?.severeCareAddition || 0) + (a.allowances?.laborDay || 0) + (a.allowances?.holiday || 0) + (a.allowances?.overtime || 0));
        bValue = ((b.allowances?.longService || 0) + (b.allowances?.severeCareAddition || 0) + (b.allowances?.laborDay || 0) + (b.allowances?.holiday || 0) + (b.allowances?.overtime || 0));
        break;
      default:
        return 0;
    }

    // 문자열 비교
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection.value === 'asc'
        ? aValue.localeCompare(bValue, 'ko')
        : bValue.localeCompare(aValue, 'ko');
    }

    // 숫자 비교
    if (sortDirection.value === 'asc') {
      return aValue - bValue;
    } else {
      return bValue - aValue;
    }
  });

  return indexed;
});

// 동일한 요양보호사 정보를 가진 데이터를 그룹화하고 합계를 계산하는 computed
const groupedMemberData = computed(() => {
  if (!filteredMemberData.value || filteredMemberData.value.length === 0) return [];

  const groups: any[] = [];
  const processed = new Set();

  filteredMemberData.value.forEach((item: any, index: number) => {
    if (processed.has(index)) return;

    // 현재 아이템과 동일한 요양보호사 정보를 가진 모든 아이템 찾기
    const sameCaregiverItems = filteredMemberData.value.filter((otherItem: any, otherIndex: number) => {
      return !processed.has(otherIndex) &&
        otherItem.caregiver_name === item.caregiver_name &&
        otherItem.caregiver_birth === item.caregiver_birth &&
        otherItem.lem_gender === item.lem_gender &&
        otherItem.lem_license === item.lem_license &&
        otherItem.lem_work_start_date === item.lem_work_start_date &&
        otherItem.lem_work_end_date === item.lem_work_end_date;
    });

    // 합계 계산
    const totals = sameCaregiverItems.reduce((acc: any, currentItem: any) => {
      // 수가시간을 숫자로 변환 (문자열에서 숫자 부분만 추출)
      const currentSugaHours = parseFloat(String(currentItem.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;
      const currentServiceDays = currentItem.service_days || 0;

      // 수가시간 = 서비스횟수 * 수가시간
      const calculatedSugaHours = currentServiceDays * currentSugaHours;
      const accSugaHours = acc.suga_hours || 0;

      // 월급여 계산 (시급 × 수가시간)
      const currentTimePay = parseFloat(String(currentItem.time_pay || 0).replace(/[^\d]/g, '')) || 0;
      const currentMonthlyPay = calculateMonthlyPay(currentTimePay, calculatedSugaHours);
      const accMonthlyPay = acc.monthly_pay || 0;
      return {
        service_days: (acc.service_days || 0) + currentServiceDays,
        total_minutes: (acc.total_minutes || 0) + (parseFloat(String(currentItem.total_minutes || 0).replace(/[^\d.-]/g, '')) || 0),
        suga_hours: accSugaHours + calculatedSugaHours,
        total_suga_money: (acc.total_suga_money || 0) + (currentItem.total_suga_money || 0),
        suga_count: (acc.suga_count || 0) + (currentItem.suga_count || 0),
        //12:00 ~ 13:00 식대 일수
        lunch_time_count: (Number(currentItem.lunch_time_count) || 0),
        monthly_pay: accMonthlyPay + currentMonthlyPay,
        taxSavings: acc.taxSavings || 0
      };
    }, {});

    // 평균값 계산 추가
    totals.average_time_pay = calculateWeightedAverageTimePay(sameCaregiverItems);
    // 월급여 총합은 이미 totals.monthly_pay에 계산되어 있음

    // 환자 정보 그룹화
    const patientGroups = sameCaregiverItems.reduce((acc: any, currentItem: any) => {
      const patientKey = `${currentItem.leb_patient_code}_${currentItem.leb_patient_name}`;
      if (!acc[patientKey]) {
        acc[patientKey] = {
          patient_code: currentItem.leb_patient_code,
          patient_name: currentItem.leb_patient_name,
          items: []
        };
      }
      acc[patientKey].items.push(currentItem);
      return acc;
    }, {});

    const patientList = Object.values(patientGroups);
    const firstPatient = patientList[0] as any;

    // 수급자명 표시 형식 결정
    const patientDisplayName = patientList.length === 1
      ? `${firstPatient.patient_name}`
      : `${firstPatient.patient_name} 외 ${patientList.length - 1}명`;

    // taxData가 로드되지 않았으면 기본값 사용
    if (!taxData.value || !taxData.value.tr_time_pay) {
      // taxData가 없으면 그룹 생성만 하고 계산은 나중에
      const group = {
        caregiverInfo: {
          caregiver_name: item.caregiver_name,
          caregiver_birth: item.caregiver_birth,
          lem_gender: item.lem_gender,
          lem_license: item.lem_license,
          lem_work_start_date: item.lem_work_start_date,
          lem_work_end_date: item.lem_work_end_date,
        },
        patientInfo: {
          patient_code: firstPatient.patient_code,
          patient_name: patientDisplayName,
          patient_count: patientList.length
        },
        patientGroups: patientList,
        items: sameCaregiverItems,
        totals: totals,
        allowances: { meal: 0, longService: 0, severeCareAddition: 0, holiday: 0, overtime: 0, laborDay: 0 },
        insurance: {
          nationalPension: { amount: 0 },
          healthInsurance: { amount: 0 },
          employmentInsurance: { amount: 0 },
          industrialAccident: { amount: 0 },
          longTermCare: { amount: 0 },
          unemployment: { amount: 0 },
          supportDeduction: 0,
          total: 0
        },
        tax: { incomeTax: 0, localTax: 0, total: 0 },
        rowSpan: sameCaregiverItems.length
      };

      groups.push(group);

      // 처리된 아이템들 마킹
      sameCaregiverItems.forEach((groupItem: any) => {
        const itemIndex = filteredMemberData.value.findIndex((originalItem: any) => originalItem === groupItem);
        processed.add(itemIndex);
      });

      return; // 다음 아이템으로
    }

    // 가족 케어 전용 여부 확인 (초기 생성 시)
    const isFamilyOnlyCaregiver = sameCaregiverItems.every((item: any) => {
      const sugaName = item.suga_name || '';
      return sugaName.includes('가족');
    });

    // 수당 데이터 초기화
    const basicPay = taxData.value.tr_time_pay * totals.suga_hours;
    const holidayPay = taxData.value.tr_time_pay * totals.suga_hours * 0.2;
    // 연차수당 = (시간당임금 * 수가시간 / 근무일수) * 연차개수 / 12
    const annualLeaveDays = getAnnualLeaveDays(item.lem_work_start_date);
    let vacationPay = Math.round((taxData.value.tr_time_pay * totals.suga_hours / totals.service_days) * annualLeaveDays / 12);
    const totalMonthlyPay = totals.monthly_pay; // 총 월급여

    // 장기근속수당 (초기값 0, 나중에 calculateGroupTotals에서 계산)
    const longServiceAllowance = 0;
    // 보수교육비 (초기값 0, 나중에 calculateGroupTotals에서 계산)
    const bosuEduPayAllowance = 0;

    // 휴일수당 계산 (그룹 인덱스를 찾아서 계산해야 하므로 나중에 설정)
    const holidayAllowance = 0; // 초기값

    // 중식비 계산 (월급여를 초과하지 않도록 제한)
    let actualMealAllowance = 0;
    
    // 가족 케어 전용이 아닌 경우만 식대 계산
    if (!isFamilyOnlyCaregiver) {
      const maxMealAllowance = Math.max(0, totalMonthlyPay - (basicPay + holidayPay + vacationPay) - longServiceAllowance - holidayAllowance);
      const requestedMealAllowance = Math.min(totals.lunch_time_count * 20000, 200000);
      actualMealAllowance = Math.min(requestedMealAllowance, maxMealAllowance);
    }

    // 남은 금액을 연차수당에 합산
    const remainingAmount = totalMonthlyPay - (basicPay + holidayPay + vacationPay) - longServiceAllowance - holidayAllowance - actualMealAllowance;
    if (remainingAmount > 0) {
      vacationPay += remainingAmount;
    }

    // 기본급 총합 계산
    let basicTotal;
    if (isFamilyOnlyCaregiver) {
      // 가족 케어 전용: 월급여 전체가 기본급 (주휴수당, 연차수당, 식대 모두 포함)
      basicTotal = totalMonthlyPay;
    } else {
      // 일반: 기본급 + 주휴수당 + 연차수당
      basicTotal = basicPay + holidayPay + vacationPay;
    }

    // totals에 vacationPay와 basicTotal 저장
    totals.vacationPay = vacationPay;
    if (isFamilyOnlyCaregiver) {
      // 가족 케어 전용: basicTotal에 이미 모든 것이 포함됨
      totals.basicTotal = basicTotal;
    } else {
      // 일반: 기본급 + 주휴수당 + 연차수당 + 식대
      totals.basicTotal = basicTotal + actualMealAllowance;
    }

    const allowances = {
      meal: actualMealAllowance, // 제한된 식대 (비과세)
      longService: longServiceAllowance, // 장기근속수당 (초기값, 나중에 업데이트)
      bosuEduPay: bosuEduPayAllowance, // 보수교육비 (초기값, 나중에 업데이트)
      severeCareAddition: 0, // 중증수급자 가산 (초기값, 나중에 업데이트)
      holiday: holidayAllowance, // 휴일수당 (초기값, 나중에 업데이트)
      overtime: 0, // 연장수당 (초기값, 나중에 업데이트)
      laborDay: 0 // 근로자의날 수당 (초기값, 나중에 업데이트)
    };

    // 사대보험 데이터 초기화 (기본 계산값으로 초기화)
    const taxableAllowances = allowances.longService + allowances.bosuEduPay; // 과세 수당
    const taxablePay = basicPay + holidayPay + vacationPay + taxableAllowances;

    // 만 나이 계산 (초기화 시)
    const caregiverAge = calculateAge(item.caregiver_birth, searchParams.value.lebYm);

    const insurance = {
      nationalPension: { amount: caregiverAge >= 60 ? 0 : truncateToTen(taxablePay * taxData.value.tr_yeon) },
      healthInsurance: { amount: truncateToTen(taxablePay * taxData.value.tr_gun) },
      employmentInsurance: { amount: truncateToTen(taxablePay * taxData.value.tr_go) },
      industrialAccident: { amount: truncateToTen(taxablePay * taxData.value.tr_san) },
      longTermCare: { amount: 0 }, // 건강보험료의 12.95%로 계산
      unemployment: { amount: truncateToTen(taxablePay * taxData.value.tr_sil) },
      supportDeduction: 0, // 지원공제 (장기근속수당의 4대보험 금액)
      total: 0
    };

    // 장기요양보험은 건강보험료의 12.95% (원단위 절삭)
    insurance.longTermCare.amount = truncateToTen(insurance.healthInsurance.amount * taxData.value.tr_jang);

    // 세금 데이터 초기화
    const tax = {
      incomeTax: 0,
      localTax: 0,
      total: 0
    };

    // 그룹 생성
    const group = {
      caregiverInfo: {
        caregiver_name: item.caregiver_name,
        caregiver_birth: item.caregiver_birth,
        lem_gender: item.lem_gender,
        lem_license: item.lem_license,
        lem_work_start_date: item.lem_work_start_date,
        lem_work_end_date: item.lem_work_end_date,
      },
      patientInfo: {
        patient_code: firstPatient.patient_code,
        patient_name: patientDisplayName,
        patient_count: patientList.length
      },
      patientGroups: patientList,
      items: sameCaregiverItems,
      totals: totals,
      allowances: allowances,
      insurance: insurance,
      tax: tax,
      rowSpan: sameCaregiverItems.length
    };

    // computed 내부에서는 계산하지 않음 - watch에서 처리
    groups.push(group);

    // 처리된 아이템들 마킹
    sameCaregiverItems.forEach((groupItem: any) => {
      const itemIndex = filteredMemberData.value.findIndex((originalItem: any) => originalItem === groupItem);
      processed.add(itemIndex);
    });
  });

  return groups;
});

// 행 펼치기/접기 함수
const toggleRow = (groupIndex: number) => {
  if (expandedRows.value.has(groupIndex)) {
    expandedRows.value.delete(groupIndex);
  } else {
    expandedRows.value.add(groupIndex);
  }
};

// 행이 펼쳐져 있는지 확인하는 함수
const isRowExpanded = (groupIndex: number) => {
  return expandedRows.value.has(groupIndex);
};

// 요양보호사의 모든 수가명이 '가족'을 포함하는지 확인하는 함수
const isAllFamilyCaregiver = (group: any): boolean => {
  if (!group || !group.items || group.items.length === 0) {
    return false;
  }
  
  // 모든 items의 suga_name에 '가족'이 포함되어 있는지 확인
  return group.items.every((item: any) => {
    const sugaName = item.suga_name || '';
    return sugaName.includes('가족');
  });
};

// 스낵바
const snackbar = ref({
  show: false,
  message: "",
  color: "success",
});

// 유효성 검증 규칙
const rules = {
  required: (value: string) => !!value || "필수 입력 항목입니다.",
  yyyymm: (value: string) =>
    /^\d{6}$/.test(value) || "YYYYMM 형식으로 입력해주세요.",
};

// API 호출 함수
const fetchData = async () => {
  if (loading.value) return; // 실행 중이면 바로 return

  if (!searchParams.value.sisulCode || !searchParams.value.lebYm) {
    showSnackbar("장기요양기관기호와 년월을 입력해주세요.", "error");
    return;
  }

  loading.value = true;
  try {
    // 모든 데이터를 병렬로 가져오기
    const [memberJoinResponse, memberJoinDetailResponse, individualServicesResponse, dailyServicesResponse, weeklyServicesResponse] = await Promise.all([
      axios.get("/api/calc/caregiver-member-join", {
        params: {
          sisulCode: searchParams.value.sisulCode,
          lebYm: searchParams.value.lebYm,
        },
      }),
      axios.get("/api/calc/caregiver-member-join-detail", {
        params: {
          sisulCode: searchParams.value.sisulCode,
          lebYm: searchParams.value.lebYm,
        },
      }),
      axios.get("/api/calc/caregiver-individual-services", {
        params: {
          sisulCode: searchParams.value.sisulCode,
          lebYm: searchParams.value.lebYm,
        },
      }),
      axios.get("/api/calc/caregiver-daily-services", {
        params: {
          sisulCode: searchParams.value.sisulCode,
          lebYm: searchParams.value.lebYm,
        },
      }),
      axios.get("/api/calc/caregiver-weekly-services", {
        params: {
          sisulCode: searchParams.value.sisulCode,
          lebYm: searchParams.value.lebYm,
        },
      })
    ]);

    // 데이터 조회 후 중복 제거
    const rawMemberJoinData = memberJoinResponse.data.data || [];
    const rawMemberJoinDetailData = memberJoinDetailResponse.data.data || [];

    // memberJoinData 중복 제거
    const memberJoinMap = new Map();
    rawMemberJoinData.forEach((item: any) => {
      const uniqueKey = `${item.caregiver_name}_${item.caregiver_birth}_${item.lem_gender}_${item.leb_patient_code}_${item.suga_code || ''}`;
      if (!memberJoinMap.has(uniqueKey)) {
        memberJoinMap.set(uniqueKey, item);
      }
    });
    memberJoinData.value = Array.from(memberJoinMap.values());

    // memberJoinDetailData 중복 제거
    const memberJoinDetailMap = new Map();
    rawMemberJoinDetailData.forEach((item: any) => {
      const uniqueKey = `${item.caregiver_name}_${item.caregiver_birth}_${item.lem_gender}_${item.leb_patient_code}_${item.suga_code || ''}`;
      if (!memberJoinDetailMap.has(uniqueKey)) {
        memberJoinDetailMap.set(uniqueKey, item);
      }
    });
    memberJoinDetailData.value = Array.from(memberJoinDetailMap.values());

    individualServicesData.value = individualServicesResponse.data.data || [];
    dailyServicesData.value = dailyServicesResponse.data.data || [];
    weeklyServicesData.value = weeklyServicesResponse.data.data || [];

    // 각 항목에 time_pay 필드 초기화 (저장된 시급이 있으면 사용, 없으면 빈 문자열)
    memberJoinData.value.forEach((item: any) => {
      if (!item.time_pay) {
        item.time_pay = ''; // 빈 문자열로 초기화
      }
      // 저장된 시급이 있으면 포맷팅하여 표시
      if (item.time_pay && item.time_pay !== '') {
        const numericValue = parseFloat(String(item.time_pay).replace(/[^\d]/g, '')) || 0;
        if (numericValue > 0) {
          item.time_pay = new Intl.NumberFormat('ko-KR').format(numericValue);
        }
      }
    });

    // 중증수급자 가산 데이터도 time_pay 필드 초기화
    memberJoinDetailData.value.forEach((item: any) => {
      if (!item.time_pay) {
        item.time_pay = ''; // 빈 문자열로 초기화
      }
      // 저장된 시급이 있으면 포맷팅하여 표시
      if (item.time_pay && item.time_pay !== '') {
        const numericValue = parseFloat(String(item.time_pay).replace(/[^\d]/g, '')) || 0;
        if (numericValue > 0) {
          item.time_pay = new Intl.NumberFormat('ko-KR').format(numericValue);
        }
      }
    });


    showSnackbar(
      `멤버 조인 데이터 ${memberJoinData.value.length}건 조회완료`,
      "success"
    );

    // hh_longterm_excel_member_info 테이블에 데이터가 있는지 확인
    if (memberJoinData.value.length > 0) {
      try {
        const memberInfoResponse = await axios.get(`/api/calc/member-info/${searchParams.value.sisulCode}/${searchParams.value.lebYm}`);
        const memberInfoData = memberInfoResponse.data.data || [];

        // 데이터가 없으면 자동으로 과거 설정 불러오기
        if (memberInfoData.length === 0) {
          await autoLoadPreviousSettings();
        }
      } catch (error: any) {
        // API 호출 실패 시에도 과거 설정 불러오기 시도
        await autoLoadPreviousSettings();
      }
    }
  } catch (error: any) {
    console.error("데이터 조회 오류:", error);
    showSnackbar("데이터가 존재하지 않습니다.", "error");
  } finally {
    loading.value = false;
  }
};


// 금액 포맷팅 함수
const formatMoney = (amount: number) => {
  if (!amount) return "0";
  return new Intl.NumberFormat("ko-KR").format(amount);
};

// 수가시간 포맷팅 함수
const formatSugaHours = (hours: any) => {
  if (!hours) return "0시간";

  // 문자열에서 숫자 부분만 추출
  const numericHours = parseFloat(String(hours).replace(/[^\d.-]/g, '')) || 0;

  // 소수점이 있는 경우 적절히 표시
  if (numericHours % 1 === 0) {
    return `${numericHours}시간`;
  } else {
    return `${numericHours}시간`;
  }
};

const formatTotalMinutes = (minutes: any) => {
  if (!minutes) return "0시간";

  // 숫자로 변환 (문자열이면 숫자 부분만 추출)
  const numericMinutes = parseFloat(String(minutes).replace(/[^\d.-]/g, '')) || 0;

  const hours = Math.floor(numericMinutes / 60);
  const remainingMinutes = numericMinutes % 60;

  if (hours === 0) return `${remainingMinutes}분`;
  else if (remainingMinutes === 0) return `${hours}시간`;
  else return `${hours}시간 ${remainingMinutes}분`;
};

// 월급여 계산 함수 (시급 × 수가시간)
const calculateMonthlyPay = (timePay: any, sugaHours: any) => {
  // 시급에서 숫자만 추출
  const numericTimePay = parseFloat(String(timePay || 0).replace(/[^\d]/g, '')) || 0;

  // 수가시간에서 숫자만 추출
  const numericSugaHours = parseFloat(String(sugaHours || 0).replace(/[^\d.-]/g, '')) || 0;

  // 월급여 = 시급 × 수가시간 (반올림)
  const monthlyPay = Math.round(numericTimePay * numericSugaHours);

  return monthlyPay;
};

// 시급 가중평균 계산 함수 (수가시간을 가중치로)
const calculateWeightedAverageTimePay = (items: any[]) => {
  let totalWeightedPay = 0;
  let totalSugaHours = 0;

  items.forEach(item => {
    const timePay = parseFloat(String(item.time_pay || 0).replace(/[^\d]/g, '')) || 0;
    const sugaHours = parseFloat(String(item.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;
    const serviceDays = item.service_days || 0;

    // 수가시간 = 서비스횟수 × 수가시간
    const calculatedSugaHours = serviceDays * sugaHours;

    if (calculatedSugaHours > 0) {
      totalWeightedPay += timePay * calculatedSugaHours;
      totalSugaHours += calculatedSugaHours;
    }
  });

  return totalSugaHours > 0 ? Math.round(totalWeightedPay / totalSugaHours) : 0;
};


// 생년월일을 주민등록번호 형식으로 변환하는 함수
const formatBirthToJumin = (birthDate: string, gender: string) => {
  if (!birthDate || birthDate === "-") return "-";

  // YYYY-MM-DD 형식을 YYMMDD로 변환
  const dateParts = birthDate.split('-');
  if (dateParts.length !== 3) return birthDate;

  const year = dateParts[0];
  const month = dateParts[1];
  const day = dateParts[2];

  const yy = year.slice(-2); // 마지막 2자리
  const yymmdd = yy + month + day;

  // 성별에 따른 마지막 자리 결정
  const yearNum = parseInt(year);
  let genderCode = "";

  if (yearNum >= 2000) {
    // 2000년 이후
    genderCode = gender === "여" ? "-4" : "-3";
  } else {
    // 2000년 이전
    genderCode = gender === "여" ? "-2" : "-1";
  }

  return yymmdd + genderCode;
};


// 시급 입력 필드 포커스 이벤트 (컴마 제거)
const onTimePayFocus = (event: FocusEvent, item: any, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  // 컴마 제거하여 숫자만 표시
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
  item.time_pay = numericValue;

  // settingsData도 동기화
  if (settingsData.value[groupIndex] && item.suga_name && item.leb_patient_code) {
    if (!settingsData.value[groupIndex].serviceTimePay) {
      settingsData.value[groupIndex].serviceTimePay = {};
    }
    if (!settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code]) {
      settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code] = {};
    }
    settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code][item.suga_name] = numericValue;
  }
};

// 시급 입력 필드 포커스 아웃 이벤트 (컴마 추가)
const onTimePayBlur = (event: FocusEvent, item: any, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;

  // 숫자만 추출
  const numericValue = value.replace(/[^\d]/g, '');

  if (numericValue) {
    // 천원단위로 컴마 추가
    const formattedValue = new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
    input.value = formattedValue;
    item.time_pay = formattedValue;

    // 시급이 입력되면 noPreviousData 플래그 제거
    if (item.noPreviousData) {
      item.noPreviousData = false;
    }

    // settingsData도 동기화
    if (settingsData.value[groupIndex] && item.suga_name && item.leb_patient_code) {
      if (!settingsData.value[groupIndex].serviceTimePay) {
        settingsData.value[groupIndex].serviceTimePay = {};
      }
      if (!settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code]) {
        settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code] = {};
      }
      settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code][item.suga_name] = formattedValue;
    }
  } else {
    input.value = '';
    item.time_pay = '';

    // settingsData도 동기화
    if (settingsData.value[groupIndex] && item.suga_name && item.leb_patient_code) {
      if (!settingsData.value[groupIndex].serviceTimePay) {
        settingsData.value[groupIndex].serviceTimePay = {};
      }
      if (!settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code]) {
        settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code] = {};
      }
      settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code][item.suga_name] = '';
    }
  }
};

// 시급 입력 필드 입력 이벤트 (숫자만 허용)
const onTimePayInput = (event: Event, item: any, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;

  // 숫자가 아닌 문자 제거
  value = value.replace(/[^\d]/g, '');

  // 최대 9자리까지 제한
  if (value.length > 9) {
    value = value.slice(0, 9);
  }

  input.value = value;
  item.time_pay = value;

  // 시급이 입력되면 noPreviousData 플래그 제거
  if (value && item.noPreviousData) {
    item.noPreviousData = false;
  }

  // settingsData도 동기화
  if (settingsData.value[groupIndex] && item.suga_name && item.leb_patient_code) {
    if (!settingsData.value[groupIndex].serviceTimePay) {
      settingsData.value[groupIndex].serviceTimePay = {};
    }
    if (!settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code]) {
      settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code] = {};
    }
    settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code][item.suga_name] = value;
  }
};
// 엑셀 다운로드 컴포넌트 참조
const wageReportExcelRef = ref<any>(null);

// 임금대장 엑셀 다운로드 함수 (컴포넌트 호출)
const handleDownloadPayrollReport = () => {
  if (wageReportExcelRef.value) {
    wageReportExcelRef.value.downloadPayrollReport();
  }
};

// 전체 데이터 저장 함수
const saveAllData = async () => {
  if (saving.value) return;

  if (!searchParams.value.sisulCode || !searchParams.value.lebYm) {
    showSnackbar("장기요양기관기호와 년월을 입력해주세요.", "error");
    return;
  }

  if (memberJoinData.value.length === 0) {
    showSnackbar("저장할 데이터가 없습니다.", "error");
    return;
  }

  // 시급이 입력된 항목만 필터링 (settingsData의 serviceTimePay도 확인)
  const itemsWithTimePay = memberJoinData.value.filter((item: any) => {
    // settingsData에서 시급 확인
    const groupIndex = groupedMemberData.value.findIndex((group: any) =>
      group.caregiverInfo.caregiver_name === item.caregiver_name &&
      group.caregiverInfo.caregiver_birth === item.caregiver_birth &&
      group.caregiverInfo.lem_gender === item.lem_gender &&
      group.caregiverInfo.lem_license === item.lem_license &&
      group.caregiverInfo.lem_work_start_date === item.lem_work_start_date &&
      group.caregiverInfo.lem_work_end_date === item.lem_work_end_date
    );
    const settings = settingsData.value[groupIndex];
    const timePayFromSettings = settings?.serviceTimePay?.[item.leb_patient_code]?.[item.suga_name];
    const timePayFromItem = item.time_pay;
    // settingsData의 serviceTimePay를 우선 확인, 없으면 item.time_pay 확인
    const timePayValue = timePayFromSettings || timePayFromItem || '';

    // 숫자만 추출하여 확인
    const numericValue = timePayValue ? String(timePayValue).replace(/[^\d]/g, '') : '';
    // item.time_pay가 null이거나 ''라도 포함 (timePayFromItem이 존재하는 경우)
    return numericValue || timePayFromItem !== undefined;
  });

  if (itemsWithTimePay.length === 0) {
    showSnackbar("저장할 시급 정보가 없습니다. 시급을 입력해주세요.", "error");
    return;
  }


  saving.value = true;
  try {
    // 그룹 인덱스 찾기 함수
    const findGroupIndex = (item: any) => {
      return groupedMemberData.value.findIndex((group: any) =>
        group.caregiverInfo.caregiver_name === item.caregiver_name &&
        group.caregiverInfo.caregiver_birth === item.caregiver_birth &&
        group.caregiverInfo.lem_gender === item.lem_gender &&
        group.caregiverInfo.lem_license === item.lem_license &&
        group.caregiverInfo.lem_work_start_date === item.lem_work_start_date &&
        group.caregiverInfo.lem_work_end_date === item.lem_work_end_date
      );
    };

    // 중복 제거를 위한 Map 사용
    const uniqueItemsMap = new Map();

    // 시급이 입력된 항목을 순회하며 유니크한 항목만 Map에 저장
    itemsWithTimePay.forEach((item: any) => {
      // 유니크 키: 요양보호사명 + 생년월일 + 성별 + 수급자코드 + 수가코드
      const uniqueKey = `${item.caregiver_name}_${item.caregiver_birth}_${item.lem_gender}_${item.leb_patient_code}_${item.suga_code || ''}`;

      // 이미 존재하지 않는 경우에만 추가 (첫 번째 항목만 유지)
      if (!uniqueItemsMap.has(uniqueKey)) {
        uniqueItemsMap.set(uniqueKey, item);
      }
    });

    // Map에서 유니크한 항목들만 추출
    const uniqueItems = Array.from(uniqueItemsMap.values());

    // 유니크한 항목만 저장할 데이터 준비
    const saveData = uniqueItems.map((item: any) => {
      // group._originalIndex를 사용하여 settingsData 접근 (화면 표시와 동일한 방식)
      let originalIndex = -1;
      if (groupedMemberData.value) {
        const group = groupedMemberData.value.find((g: any) =>
          g.caregiverInfo.caregiver_name === item.caregiver_name &&
          g.caregiverInfo.caregiver_birth === item.caregiver_birth &&
          g.caregiverInfo.lem_gender === item.lem_gender &&
          g.caregiverInfo.lem_license === item.lem_license &&
          g.caregiverInfo.lem_work_start_date === item.lem_work_start_date &&
          g.caregiverInfo.lem_work_end_date === item.lem_work_end_date
        );
        if (group && group._originalIndex !== undefined) {
          originalIndex = group._originalIndex;
        }
      }

      // originalIndex를 찾지 못한 경우 findGroupIndex 사용 (fallback)
      if (originalIndex < 0) {
        originalIndex = findGroupIndex(item);
      }

      const settings = settingsData.value[originalIndex];

      // 국민연금 금액
      const yeonPayAmount = settings?.yeonPay
        ? String(settings.yeonPay).replace(/[^\d]/g, '')
        : '';
      const applyGun = settings?.applyHealthInsurance !== false ? 'Y' : 'N';
      const applyGo = settings?.applyEmploymentInsurance !== false ? 'Y' : 'N';
      const applyHolidayPay = settings?.applyHolidayAllowance !== false ? 'Y' : 'N';
      const applyOvertimePay = settings?.applyOvertimeAllowance !== false ? 'Y' : 'N';
      const applyLunchPay = settings?.applyMealAllowance !== false ? 'Y' : 'N';
      const applyOutPay = settings?.applyRetirement !== false ? 'Y' : 'N';
      // 근로자의날 휴일수당은 기본값이 Y (체크된 상태)
      const applyLaborDay = settings?.applyLaborDayAllowance !== false ? 'Y' : 'N';

      // 장기근속수당 금액
      const longServiceAmount = settings?.longServiceAllowance
        ? String(settings.longServiceAllowance).replace(/[^\d]/g, '')
        : '';

      // 보수교육비 금액
      const bosuEduPayAmount = settings?.bosuEduPayAllowance
        ? String(settings.bosuEduPayAllowance).replace(/[^\d]/g, '')
        : '';

      // 시급 가져오기: settingsData의 serviceTimePay를 우선 확인, 없으면 item.time_pay 사용
      // 화면에 표시되는 시급과 동일한 로직 사용 (group._originalIndex 사용)
      let timePayValue = '';
      if (settings?.serviceTimePay?.[item.leb_patient_code]?.[item.suga_name]) {
        timePayValue = settings.serviceTimePay[item.leb_patient_code][item.suga_name];
      } else if (item.time_pay) {
        timePayValue = item.time_pay;
      }

      // 컴마 제거하여 숫자만 추출
      const numericTimePay = timePayValue ? String(timePayValue).replace(/[^\d]/g, '') : '';

      return {
        m_sisul_code: searchParams.value.sisulCode,
        lemi_ym: searchParams.value.lebYm,
        lemi_name: item.caregiver_name,
        lemi_time_pay: numericTimePay,
        lemi_birth: item.caregiver_birth,
        lemi_gender: item.lem_gender,
        lemi_license: item.lem_license,
        lemi_work_start_date: item.lem_work_start_date,
        lemi_work_end_date: item.lem_work_end_date,
        lemi_patient_code: item.leb_patient_code,
        lemi_suga_code: item.suga_code || '',
        lemi_yeon_pay: yeonPayAmount,
        lemi_apply_gun: applyGun,
        lemi_apply_go: applyGo,
        lemi_apply_holiday_pay: applyHolidayPay,
        lemi_apply_overtime_pay: applyOvertimePay,
        lemi_apply_lunch_pay: applyLunchPay,
        lemi_apply_out_pay: applyOutPay,
        lemi_apply_labor_day_pay: applyLaborDay,
        lemi_long_service_pay: longServiceAmount,
        lemi_bosu_edu_pay: bosuEduPayAmount
      };
    });
    const response = await axios.post('/api/calc/save-member-info', {
      sisulCode: searchParams.value.sisulCode,
      lebYm: searchParams.value.lebYm,
      memberData: saveData
    });

    if (response.data.success) {
      showSnackbar(`시급 및 설정 정보 ${response.data.savedCount}건이 성공적으로 저장되었습니다. (전체 ${memberJoinData.value.length}개 중)`, "success");
    } else {
      showSnackbar(response.data.message || "저장에 실패했습니다.", "error");
    }
  } catch (error: any) {
    console.error("데이터 저장 오류:", error);
    showSnackbar("데이터 저장 중 오류가 발생했습니다.", "error");
  } finally {
    saving.value = false;
  }
};

// 개별 요양보호사 저장
const saveSingleCaregiver = async (groupIndex: number) => {
  if (saving.value) return;

  if (!searchParams.value.sisulCode || !searchParams.value.lebYm) {
    showSnackbar("장기요양기관기호와 년월을 입력해주세요.", "error");
    return;
  }

  if (memberJoinData.value.length === 0) {
    showSnackbar("저장할 데이터가 없습니다.", "error");
    return;
  }

  const group = groupedMemberData.value[groupIndex];
  if (!group) {
    showSnackbar("요양보호사 정보를 찾을 수 없습니다.", "error");
    return;
  }

  // 해당 요양보호사의 항목만 필터링 (시급이 입력된 항목만, settingsData의 serviceTimePay도 확인)
  const settings = settingsData.value[groupIndex];
  const caregiverItems = memberJoinData.value.filter((item: any) => {
    // 요양보호사 정보 매칭 확인
    const matchesCaregiver =
      item.caregiver_name === group.caregiverInfo.caregiver_name &&
      item.caregiver_birth === group.caregiverInfo.caregiver_birth &&
      item.lem_gender === group.caregiverInfo.lem_gender &&
      item.lem_license === group.caregiverInfo.lem_license &&
      item.lem_work_start_date === group.caregiverInfo.lem_work_start_date &&
      item.lem_work_end_date === group.caregiverInfo.lem_work_end_date;

    if (!matchesCaregiver) return false;

    // settingsData에서 시급 확인
    const timePayFromSettings = settings?.serviceTimePay?.[item.leb_patient_code]?.[item.suga_name];
    const timePayFromItem = item.time_pay;

    // settingsData의 serviceTimePay를 우선 확인, 없으면 item.time_pay 확인
    const timePayValue = timePayFromSettings || timePayFromItem || '';

    // 숫자만 추출하여 확인
    const numericValue = timePayValue ? String(timePayValue).replace(/[^\d]/g, '') : '';
    return numericValue && numericValue !== '0';
  });

  if (caregiverItems.length === 0) {
    showSnackbar("저장할 시급 정보가 없습니다. 시급을 입력해주세요.", "error");
    return;
  }

  saving.value = true;
  try {
    // 중복 제거를 위한 Map 사용
    const uniqueItemsMap = new Map();

    caregiverItems.forEach((item: any) => {
      // 유니크 키: 요양보호사명 + 생년월일 + 성별 + 수급자코드 + 수가코드
      const uniqueKey = `${item.caregiver_name}_${item.caregiver_birth}_${item.lem_gender}_${item.leb_patient_code}_${item.suga_code || ''}`;

      if (!uniqueItemsMap.has(uniqueKey)) {
        uniqueItemsMap.set(uniqueKey, item);
      }
    });

    const uniqueItems = Array.from(uniqueItemsMap.values());
    const settings = settingsData.value[groupIndex];

    // 저장할 데이터 준비
    const saveData = uniqueItems.map((item: any) => {
      // 국민연금 금액
      const yeonPayAmount = settings?.yeonPay
        ? String(settings.yeonPay).replace(/[^\d]/g, '')
        : '';
      const applyGun = settings?.applyHealthInsurance !== false ? 'Y' : 'N';
      const applyGo = settings?.applyEmploymentInsurance !== false ? 'Y' : 'N';
      const applyHolidayPay = settings?.applyHolidayAllowance !== false ? 'Y' : 'N';
      const applyOvertimePay = settings?.applyOvertimeAllowance !== false ? 'Y' : 'N';
      const applyLunchPay = settings?.applyMealAllowance !== false ? 'Y' : 'N';
      const applyOutPay = settings?.applyRetirement !== false ? 'Y' : 'N';
      const applyLaborDay = settings?.applyLaborDayAllowance !== false ? 'Y' : 'N';

      // 장기근속수당 금액
      const longServiceAmount = settings?.longServiceAllowance
        ? String(settings.longServiceAllowance).replace(/[^\d]/g, '')
        : '';

      // 보수교육비 금액
      const bosuEduPayAmount = settings?.bosuEduPayAllowance
        ? String(settings.bosuEduPayAllowance).replace(/[^\d]/g, '')
        : '';

      // 시급 가져오기: settingsData의 serviceTimePay를 우선 확인, 없으면 item.time_pay 사용
      // 화면에 표시되는 시급과 동일한 로직 사용
      let timePayValue = '';
      if (settings?.serviceTimePay?.[item.leb_patient_code]?.[item.suga_name]) {
        timePayValue = settings.serviceTimePay[item.leb_patient_code][item.suga_name];
      } else if (item.time_pay) {
        timePayValue = item.time_pay;
      }

      // 컴마 제거하여 숫자만 추출
      const numericTimePay = timePayValue ? String(timePayValue).replace(/[^\d]/g, '') : '';

      return {
        m_sisul_code: searchParams.value.sisulCode,
        lemi_ym: searchParams.value.lebYm,
        lemi_name: item.caregiver_name,
        lemi_time_pay: numericTimePay,
        lemi_birth: item.caregiver_birth,
        lemi_gender: item.lem_gender,
        lemi_license: item.lem_license,
        lemi_work_start_date: item.lem_work_start_date,
        lemi_work_end_date: item.lem_work_end_date,
        lemi_patient_code: item.leb_patient_code,
        lemi_suga_code: item.suga_code || '',
        lemi_yeon_pay: yeonPayAmount,
        lemi_apply_gun: applyGun,
        lemi_apply_go: applyGo,
        lemi_apply_holiday_pay: applyHolidayPay,
        lemi_apply_overtime_pay: applyOvertimePay,
        lemi_apply_lunch_pay: applyLunchPay,
        lemi_apply_out_pay: applyOutPay,
        lemi_apply_labor_day_pay: applyLaborDay,
        lemi_long_service_pay: longServiceAmount,
        lemi_bosu_edu_pay: bosuEduPayAmount
      };
    });

    const response = await axios.post('/api/calc/save-member-info', {
      sisulCode: searchParams.value.sisulCode,
      lebYm: searchParams.value.lebYm,
      memberData: saveData
    });

    if (response.data.success) {
      showSnackbar(`${group.caregiverInfo.caregiver_name} 요양보호사의 시급 및 설정 정보 ${response.data.savedCount}건이 성공적으로 저장되었습니다.`, "success");
    } else {
      showSnackbar(response.data.message || "저장에 실패했습니다.", "error");
    }
  } catch (error: any) {
    console.error("데이터 저장 오류:", error);
    showSnackbar("데이터 저장 중 오류가 발생했습니다.", "error");
  } finally {
    saving.value = false;
  }
};

// 설정 모달 열기
const openSetModal = () => {
  showSetModal.value = true;
};

// 저장 후 모달 닫기
const handleSaveAndClose = async () => {
  // 데이터가 적용될 때까지 대기
  await nextTick();
  await saveAllData();
  // 저장이 완료되면 모달 닫기
  if (!saving.value) {
    showSetModal.value = false;
  }
};

// 모든 과거 설정 적용 핸들러 (SetModal에서 호출 시)
const handleApplyAllPreviousSettings = async (data: { allData: Array<{ lebYm: string; data: any[] }>; recentData: { lebYm: string; data: any[] } }) => {
  // 모든 과거 설정 적용 (완료될 때까지 대기)
  await applyAllPreviousSettings(data, false);

  // 모든 그룹의 계산이 완료될 때까지 대기
  await nextTick();
  await new Promise(resolve => setTimeout(resolve, 500));

  // 모든 그룹에 대해 계산 수행 (반응성 업데이트 완료 보장)
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    isCalculating.value = true;
    try {
      for (let i = 0; i < groupedMemberData.value.length; i++) {
        const group = groupedMemberData.value[i];
        if (group) {
          calculateGroupTotals(group, i);
        }
      }
    } finally {
      isCalculating.value = false;
    }
  }

  // 계산 완료 후 추가 대기
  await nextTick();
  await new Promise(resolve => setTimeout(resolve, 300));

  showSnackbar("모든 과거 설정이 적용되었습니다. '모두 저장' 버튼을 눌러 저장해주세요.", "success");
};

// 일괄 시급 적용 (일반 - '가족'이 포함되지 않은 수가명)
const applyBulkTimePay = (timePay: string) => {
  if (!timePay || timePay === '0') return;

  // 모든 memberJoinData의 time_pay를 변경 (새 배열로 생성하여 반응성 트리거)
  memberJoinData.value = memberJoinData.value.map((item: any) => {
    if (item.suga_name !== '중증수급자 가산' && !item.suga_name.includes('가족')) {
      return { ...item, time_pay: timePay };
    }
    return item;
  });

  // 모든 memberJoinDetailData의 time_pay를 변경 (중증수급자 가산 및 가족 제외)
  memberJoinDetailData.value = memberJoinDetailData.value.map((item: any) => {
    if (item.suga_name !== '중증수급자 가산' && !item.suga_name.includes('가족')) {
      return { ...item, time_pay: timePay };
    }
    return item;
  });

  // settingsData의 serviceTimePay도 업데이트
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex] && settingsData.value[groupIndex].serviceTimePay) {
        const serviceTimePay = settingsData.value[groupIndex].serviceTimePay;

        // 모든 수급자의 모든 수가명에 대해 시급 업데이트 (가족 제외)
        Object.keys(serviceTimePay).forEach(patientCode => {
          Object.keys(serviceTimePay[patientCode]).forEach(sugaName => {
            if (sugaName !== '중증수급자 가산' && !sugaName.includes('가족')) {
              serviceTimePay[patientCode][sugaName] = timePay;
            }
          });
        });
      }
    });
  }

  showSnackbar(`모든 일반 시급이 ${timePay}원으로 변경되었습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
};

// 가족 60분 케어 수가명 매칭 함수 (60분이상 + 가족)
const isFamily60Suga = (sugaName: string): boolean => {
  if (!sugaName) return false;
  return sugaName.includes('60분') && sugaName.includes('가족');
};

// 일괄 시급 적용 (가족 60분 케어 - '60분' + '가족'이 포함된 수가명)
const applyBulkTimePayFamily60 = (timePay: string) => {
  if (!timePay || timePay === '0') return;

  // 모든 memberJoinData의 time_pay를 변경 (60분이상 가족이 포함된 수가명만)
  memberJoinData.value = memberJoinData.value.map((item: any) => {
    if (isFamily60Suga(item.suga_name)) {
      return { ...item, time_pay: timePay };
    }
    return item;
  });

  // 모든 memberJoinDetailData의 time_pay를 변경 (60분이상 가족이 포함된 수가명만)
  memberJoinDetailData.value = memberJoinDetailData.value.map((item: any) => {
    if (isFamily60Suga(item.suga_name)) {
      return { ...item, time_pay: timePay };
    }
    return item;
  });

  // settingsData의 serviceTimePay도 업데이트
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex] && settingsData.value[groupIndex].serviceTimePay) {
        const serviceTimePay = settingsData.value[groupIndex].serviceTimePay;

        // 모든 수급자의 모든 수가명에 대해 시급 업데이트 (60분이상 가족만)
        Object.keys(serviceTimePay).forEach(patientCode => {
          Object.keys(serviceTimePay[patientCode]).forEach(sugaName => {
            if (isFamily60Suga(sugaName)) {
              serviceTimePay[patientCode][sugaName] = timePay;
            }
          });
        });
      }
    });
  }

  showSnackbar(`모든 가족 60분 시급이 ${timePay}원으로 변경되었습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
};

// 가족 90분 케어 수가명 매칭 함수 (90분이상 + 가족)
const isFamily90Suga = (sugaName: string): boolean => {
  if (!sugaName) return false;
  return sugaName.includes('90분') && sugaName.includes('가족');
};

// 일괄 시급 적용 (가족 90분 케어 - '90분' + '가족'이 포함된 수가명)
const applyBulkTimePayFamily90 = (timePay: string) => {
  if (!timePay || timePay === '0') return;

  // 모든 memberJoinData의 time_pay를 변경 (90분이상 가족이 포함된 수가명만)
  memberJoinData.value = memberJoinData.value.map((item: any) => {
    if (isFamily90Suga(item.suga_name)) {
      return { ...item, time_pay: timePay };
    }
    return item;
  });

  // 모든 memberJoinDetailData의 time_pay를 변경 (90분이상 가족이 포함된 수가명만)
  memberJoinDetailData.value = memberJoinDetailData.value.map((item: any) => {
    if (isFamily90Suga(item.suga_name)) {
      return { ...item, time_pay: timePay };
    }
    return item;
  });

  // settingsData의 serviceTimePay도 업데이트
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex] && settingsData.value[groupIndex].serviceTimePay) {
        const serviceTimePay = settingsData.value[groupIndex].serviceTimePay;

        // 모든 수급자의 모든 수가명에 대해 시급 업데이트 (90분이상 가족만)
        Object.keys(serviceTimePay).forEach(patientCode => {
          Object.keys(serviceTimePay[patientCode]).forEach(sugaName => {
            if (isFamily90Suga(sugaName)) {
              serviceTimePay[patientCode][sugaName] = timePay;
            }
          });
        });
      }
    });
  }

  showSnackbar(`모든 가족 90분 시급이 ${timePay}원으로 변경되었습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
};
// 이전 달 시급 일괄 적용 (순차적으로 여러 달 데이터 검색)
const applyBulkTimePayFromPrevious = async (allPreviousData: Array<{ lebYm: string; data: any[] }>) => {
  if (!allPreviousData || allPreviousData.length === 0) return;

  // 현재 연월과 동일한 데이터 필터링 (중복 방지)
  const currentLebYm = searchParams.value.lebYm;
  const filteredData = allPreviousData.filter(monthData => monthData.lebYm !== currentLebYm);

  if (filteredData.length === 0) {
    showSnackbar("과거 데이터를 찾을 수 없습니다.", "warning");
    return;
  }

  let appliedCount = 0;
  const matchStats: { [key: string]: number } = {}; // 각 연월별 매칭 통계

  // 각 항목에 대해 가장 최근 데이터부터 순차적으로 매칭 시도하는 함수
  const findMatchingTimePay = (item: any): string | null => {
    // 최신 데이터부터 순차적으로 검색
    for (const monthData of filteredData) {
      const matchedItem = monthData.data.find((prevItem: any) =>
        prevItem.caregiver_name === item.caregiver_name &&
        prevItem.leb_patient_code === item.leb_patient_code &&
        prevItem.suga_name === item.suga_name
      );

      if (matchedItem && matchedItem.time_pay) {
        // 통계 기록
        if (!matchStats[monthData.lebYm]) {
          matchStats[monthData.lebYm] = 0;
        }
        matchStats[monthData.lebYm]++;
        return matchedItem.time_pay;
      }
    }
    return null;
  };

  // 현재 memberJoinData의 각 항목에 대해 매칭되는 시급을 찾아 적용 (새 배열로 생성)
  memberJoinData.value = memberJoinData.value.map((item: any) => {
    if (item.suga_name === '중증수급자 가산') return item;

    const matchedTimePay = findMatchingTimePay(item);

    if (matchedTimePay) {
      appliedCount++;
      return { ...item, time_pay: matchedTimePay, noPreviousData: false };
    }
    // 과거 데이터를 찾지 못한 경우 플래그 추가
    return { ...item, noPreviousData: true };
  });

  // memberJoinDetailData도 동일하게 처리
  memberJoinDetailData.value = memberJoinDetailData.value.map((item: any) => {
    if (item.suga_name === '중증수급자 가산') return item;

    const matchedTimePay = findMatchingTimePay(item);

    if (matchedTimePay) {
      return { ...item, time_pay: matchedTimePay, noPreviousData: false };
    }
    // 과거 데이터를 찾지 못한 경우 플래그 추가
    return { ...item, noPreviousData: true };
  });

  // settingsData의 serviceTimePay도 업데이트 (memberJoinData 기준으로)
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    // memberJoinData의 각 항목에 대해 해당하는 그룹을 찾아서 settingsData 업데이트
    memberJoinData.value.forEach((item: any) => {
      if (item.suga_name === '중증수급자 가산') return;

      // 해당 항목이 속한 그룹 찾기
      const groupIndex = groupedMemberData.value.findIndex((group: any) =>
        group.caregiverInfo.caregiver_name === item.caregiver_name &&
        group.caregiverInfo.caregiver_birth === item.caregiver_birth &&
        group.caregiverInfo.lem_gender === item.lem_gender &&
        group.caregiverInfo.lem_license === item.lem_license &&
        group.caregiverInfo.lem_work_start_date === item.lem_work_start_date &&
        group.caregiverInfo.lem_work_end_date === item.lem_work_end_date
      );

      if (groupIndex >= 0 && settingsData.value[groupIndex]) {
        if (!settingsData.value[groupIndex].serviceTimePay) {
          settingsData.value[groupIndex].serviceTimePay = {};
        }
        if (!settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code]) {
          settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code] = {};
        }

        // item.time_pay가 업데이트되었으므로 그것을 사용 (matchedTimePay가 있으면 이미 item.time_pay에 반영됨)
        if (item.time_pay && !item.noPreviousData) {
          settingsData.value[groupIndex].serviceTimePay[item.leb_patient_code][item.suga_name] = item.time_pay;
        }
      }
    });
  }

  // 과거 데이터를 찾지 못한 항목 개수 계산
  const noDataCount = memberJoinData.value.filter((item: any) =>
    item.suga_name !== '중증수급자 가산' && item.noPreviousData === true
  ).length;

  if (appliedCount > 0) {
    // 가장 최근 데이터의 연월 찾기
    const mostRecentLebYm = filteredData.length > 0 ? filteredData[0].lebYm : '';
    const year = mostRecentLebYm.substring(0, 4);
    const month = mostRecentLebYm.substring(4, 6);

    let message = `시간당 임금 설정이 과거 데이터에서 불러왔습니다. (${year}년 ${month}월 기준)`;
    if (noDataCount > 0) {
      message += ` 과거 데이터가 없는 항목 ${noDataCount}건은 빨간색으로 표시됩니다.`;
    }

    // 반응성 업데이트 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    // 모든 그룹에 대해 계산 수행
    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    // 계산 완료 후 추가 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`${message} '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  } else {
    if (noDataCount > 0) {
      showSnackbar(`과거 데이터를 찾을 수 없습니다. ${noDataCount}건의 항목이 빨간색으로 표시됩니다.`, "warning");
    } else {
      showSnackbar("일치하는 과거 데이터를 찾을 수 없습니다.", "warning");
    }
  }
};

// 일괄 건강보험 적용/해제
const applyBulkHealthInsurance = (applyInsurance: boolean) => {
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex]) {
        settingsData.value[groupIndex].applyHealthInsurance = applyInsurance;
      }
    });
  }

  const message = applyInsurance ? "모든 요양보호사의 건강보험이 적용되었습니다." : "모든 요양보호사의 건강보험이 해제되었습니다.";
  showSnackbar(`${message} '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
};

// 일괄 휴일수당 적용
const applyBulkHolidayAllowance = (applyAllowance: boolean) => {
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex]) {
        // 수가시간 60시간 이상인 경우에만 적용
        if (canApplyHolidayAllowance(groupIndex)) {
          settingsData.value[groupIndex].applyHolidayAllowance = applyAllowance;
        }
      }
    });
  }

  const message = applyAllowance ? "모든 요양보호사의 휴일수당이 적용되었습니다." : "모든 요양보호사의 휴일수당이 미적용되었습니다.";
  showSnackbar(`${message} '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
};

// 일괄 연장수당 적용
const applyBulkOvertimeAllowance = (applyAllowance: boolean) => {
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex]) {
        // 수가시간 60시간 이상인 경우에만 적용
        if (canApplyOvertimeAllowance(groupIndex)) {
          settingsData.value[groupIndex].applyOvertimeAllowance = applyAllowance;
        }
      }
    });
  }

  const message = applyAllowance ? "모든 요양보호사의 연장수당이 적용되었습니다." : "모든 요양보호사의 연장수당이 미적용되었습니다.";
  showSnackbar(`${message} '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
};

// 일괄 퇴직금 적용
const applyBulkRetirement = (applyRetirement: boolean) => {
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex]) {
        // 퇴직금 적립 가능한 경우에만 적용
        if (canApplyRetirement(groupIndex)) {
          settingsData.value[groupIndex].applyRetirement = applyRetirement;
        }
      }
    });
  }

  const message = applyRetirement ? "모든 요양보호사의 퇴직금 적립이 적용되었습니다." : "모든 요양보호사의 퇴직금 적립이 미적용되었습니다.";
  showSnackbar(`${message} '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
};

// 일괄 식대 적용
const applyBulkMealAllowance = (applyAllowance: boolean) => {
  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex]) {
        settingsData.value[groupIndex].applyMealAllowance = applyAllowance;
      }
    });
  }

  const message = applyAllowance ? "모든 요양보호사의 식대가 적용되었습니다." : "모든 요양보호사의 식대가 미적용되었습니다.";
  showSnackbar(`${message} '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
};

// 일괄 근로자의날 수당 적용 (2025년 이하에서만 동작)
const applyBulkLaborDayAllowance = (applyAllowance: boolean) => {
  const year = parseInt(searchParams.value.lebYm.substring(0, 4));
  const month = searchParams.value.lebYm.substring(4, 6);
  
  // 2026년 이상에서는 근로자의날이 일반 공휴일로 처리됨
  if (year >= 2026) {
    showSnackbar("2026년부터 근로자의날은 일반 공휴일로 처리됩니다.", "info");
    return;
  }
  
  if (month !== '05') {
    showSnackbar("근로자의날 수당은 5월에만 적용됩니다.", "warning");
    return;
  }

  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex]) {
        // 수가시간 60시간 이상인 경우에만 적용
        if (canApplyLaborDayAllowance(groupIndex)) {
          settingsData.value[groupIndex].applyLaborDayAllowance = applyAllowance;
        }
      }
    });
  }

  const message = applyAllowance ? "모든 요양보호사의 근로자의날 수당이 적용되었습니다." : "모든 요양보호사의 근로자의날 수당이 미적용되었습니다.";
  showSnackbar(`${message} '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
};

// 과거 휴일수당 설정 일괄 적용 (가장 최근 데이터만)
const applyBulkHolidayAllowanceFromPrevious = async (recentData: { lebYm: string; data: any[] }) => {
  if (!recentData || !recentData.data || recentData.data.length === 0) return;

  // 현재 연월과 동일한 경우 중복 방지
  if (recentData.lebYm === searchParams.value.lebYm) {
    showSnackbar("현재 연월의 데이터는 과거 설정으로 불러올 수 없습니다.", "warning");
    return;
  }

  let appliedCount = 0;

  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex] && canApplyHolidayAllowance(groupIndex)) {
        // 최근 데이터에서 요양보호사 찾기
        const matchedItem = recentData.data.find((prevItem: any) =>
          prevItem.caregiver_name === group.caregiverInfo.caregiver_name &&
          prevItem.caregiver_birth === group.caregiverInfo.caregiver_birth &&
          prevItem.lem_gender === group.caregiverInfo.lem_gender
        );

        if (matchedItem && matchedItem.lemi_apply_holiday_pay !== undefined) {
          settingsData.value[groupIndex].applyHolidayAllowance = matchedItem.lemi_apply_holiday_pay === 'Y';
          appliedCount++;
        }
      }
    });
  }

  if (appliedCount > 0) {
    const year = recentData.lebYm.substring(0, 4);
    const month = recentData.lebYm.substring(4, 6);

    // 반응성 업데이트 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    // 모든 그룹에 대해 계산 수행
    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    // 계산 완료 후 추가 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`휴일수당 설정이 ${year}년 ${month}월 과거 설정으로 불러왔습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  } else {
    showSnackbar("일치하는 과거 데이터를 찾을 수 없습니다.", "warning");
  }
};

// 과거 연장수당 설정 일괄 적용 (가장 최근 데이터만)
const applyBulkOvertimeAllowanceFromPrevious = async (recentData: { lebYm: string; data: any[] }) => {
  if (!recentData || !recentData.data || recentData.data.length === 0) return;

  // 현재 연월과 동일한 경우 중복 방지
  if (recentData.lebYm === searchParams.value.lebYm) {
    showSnackbar("현재 연월의 데이터는 과거 설정으로 불러올 수 없습니다.", "warning");
    return;
  }

  let appliedCount = 0;

  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex] && canApplyOvertimeAllowance(groupIndex)) {
        // 최근 데이터에서 요양보호사 찾기
        const matchedItem = recentData.data.find((prevItem: any) =>
          prevItem.caregiver_name === group.caregiverInfo.caregiver_name &&
          prevItem.caregiver_birth === group.caregiverInfo.caregiver_birth &&
          prevItem.lem_gender === group.caregiverInfo.lem_gender
        );

        if (matchedItem && matchedItem.lemi_apply_overtime_pay !== undefined) {
          settingsData.value[groupIndex].applyOvertimeAllowance = matchedItem.lemi_apply_overtime_pay === 'Y';
          appliedCount++;
        }
      }
    });
  }

  if (appliedCount > 0) {
    const year = recentData.lebYm.substring(0, 4);
    const month = recentData.lebYm.substring(4, 6);

    // 반응성 업데이트 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    // 모든 그룹에 대해 계산 수행
    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    // 계산 완료 후 추가 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`연장수당 설정이 ${year}년 ${month}월 과거 설정으로 불러왔습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  } else {
    showSnackbar("일치하는 과거 데이터를 찾을 수 없습니다.", "warning");
  }
};

// 과거 퇴직금 설정 일괄 적용 (가장 최근 데이터만)
const applyBulkRetirementFromPrevious = async (recentData: { lebYm: string; data: any[] }) => {
  if (!recentData || !recentData.data || recentData.data.length === 0) return;

  // 현재 연월과 동일한 경우 중복 방지
  if (recentData.lebYm === searchParams.value.lebYm) {
    showSnackbar("현재 연월의 데이터는 과거 설정으로 불러올 수 없습니다.", "warning");
    return;
  }

  let appliedCount = 0;

  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex] && canApplyRetirement(groupIndex)) {
        // 최근 데이터에서 요양보호사 찾기
        const matchedItem = recentData.data.find((prevItem: any) =>
          prevItem.caregiver_name === group.caregiverInfo.caregiver_name &&
          prevItem.caregiver_birth === group.caregiverInfo.caregiver_birth &&
          prevItem.lem_gender === group.caregiverInfo.lem_gender
        );

        if (matchedItem && matchedItem.lemi_apply_out_pay !== undefined) {
          settingsData.value[groupIndex].applyRetirement = matchedItem.lemi_apply_out_pay === 'Y';
          appliedCount++;
        }
      }
    });
  }

  if (appliedCount > 0) {
    const year = recentData.lebYm.substring(0, 4);
    const month = recentData.lebYm.substring(4, 6);

    // 반응성 업데이트 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    // 모든 그룹에 대해 계산 수행
    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    // 계산 완료 후 추가 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`퇴직금 설정이 ${year}년 ${month}월 과거 설정으로 불러왔습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  } else {
    showSnackbar("일치하는 과거 데이터를 찾을 수 없습니다.", "warning");
  }
};

// 과거 식대 설정 일괄 적용 (가장 최근 데이터만)
const applyBulkMealAllowanceFromPrevious = async (recentData: { lebYm: string; data: any[] }) => {
  if (!recentData || !recentData.data || recentData.data.length === 0) return;

  // 현재 연월과 동일한 경우 중복 방지
  if (recentData.lebYm === searchParams.value.lebYm) {
    showSnackbar("현재 연월의 데이터는 과거 설정으로 불러올 수 없습니다.", "warning");
    return;
  }

  let appliedCount = 0;

  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex]) {
        // 최근 데이터에서 요양보호사 찾기
        const matchedItem = recentData.data.find((prevItem: any) =>
          prevItem.caregiver_name === group.caregiverInfo.caregiver_name &&
          prevItem.caregiver_birth === group.caregiverInfo.caregiver_birth &&
          prevItem.lem_gender === group.caregiverInfo.lem_gender
        );

        if (matchedItem && matchedItem.lemi_apply_lunch_pay !== undefined) {
          settingsData.value[groupIndex].applyMealAllowance = matchedItem.lemi_apply_lunch_pay === 'Y';
          appliedCount++;
        }
      }
    });
  }

  if (appliedCount > 0) {
    const year = recentData.lebYm.substring(0, 4);
    const month = recentData.lebYm.substring(4, 6);

    // 반응성 업데이트 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    // 모든 그룹에 대해 계산 수행
    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    // 계산 완료 후 추가 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`식대 설정이 ${year}년 ${month}월 과거 설정으로 불러왔습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  } else {
    showSnackbar("일치하는 과거 데이터를 찾을 수 없습니다.", "warning");
  }
};

// 과거 장기근속수당 설정 일괄 적용 (가장 최근 데이터만)
const applyBulkLongServiceFromPrevious = async (recentData: { lebYm: string; data: any[] }) => {
  if (!recentData || !recentData.data || recentData.data.length === 0) return;

  // 현재 연월과 동일한 경우 중복 방지
  if (recentData.lebYm === searchParams.value.lebYm) {
    showSnackbar("현재 연월의 데이터는 과거 설정으로 불러올 수 없습니다.", "warning");
    return;
  }

  let appliedCount = 0;

  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex]) {
        // 최근 데이터에서 요양보호사 찾기
        const matchedItem = recentData.data.find((prevItem: any) =>
          prevItem.caregiver_name === group.caregiverInfo.caregiver_name &&
          prevItem.caregiver_birth === group.caregiverInfo.caregiver_birth &&
          prevItem.lem_gender === group.caregiverInfo.lem_gender
        );

        if (matchedItem && matchedItem.lemi_long_service_pay) {
          const numericValue = parseFloat(String(matchedItem.lemi_long_service_pay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            settingsData.value[groupIndex].longServiceAllowance = new Intl.NumberFormat('ko-KR').format(numericValue);
            appliedCount++;
          }
        }
      }
    });
  }

  if (appliedCount > 0) {
    const year = recentData.lebYm.substring(0, 4);
    const month = recentData.lebYm.substring(4, 6);

    // 반응성 업데이트 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    // 모든 그룹에 대해 계산 수행
    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    // 계산 완료 후 추가 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`장기근속수당 설정이 ${year}년 ${month}월 과거 설정으로 불러왔습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  } else {
    showSnackbar("일치하는 과거 데이터를 찾을 수 없습니다.", "warning");
  }
};

// 근속년수 계산 함수
const calculateServiceYears = (startDate: string, targetYm: string): number => {
  if (!startDate || !targetYm) return 0;
  
  try {
    // startDate 형식: YYYY-MM-DD
    const start = new Date(startDate);
    
    // targetYm 형식: YYYYMM -> 해당 월의 마지막 날
    const targetYear = parseInt(targetYm.substring(0, 4));
    const targetMonth = parseInt(targetYm.substring(4, 6));
    const target = new Date(targetYear, targetMonth, 0); // 해당 월의 마지막 날
    
    // 근속 년수 계산 (개월 수 / 12)
    const months = (target.getFullYear() - start.getFullYear()) * 12 + (target.getMonth() - start.getMonth());
    const years = months / 12;
    
    return years;
  } catch (error) {
    console.error('근속년수 계산 오류:', error);
    return 0;
  }
};

// 근속년수에 따른 장기근속수당 계산
const calculateLongServiceAmount = (years: number): number => {
  if (years >= 7) return 100000; // 7년 이상: 10만원
  if (years >= 5) return 80000;  // 5년 이상: 8만원
  if (years >= 3) return 60000;  // 3년 이상: 6만원
  return 0; // 3년 미만: 0원
};

// 장기근속수당 자동계산 적용
const applyBulkLongServiceAutoCalculate = async () => {
  if (!groupedMemberData.value || groupedMemberData.value.length === 0) {
    showSnackbar("적용할 데이터가 없습니다.", "warning");
    return;
  }

  let appliedCount = 0;

  groupedMemberData.value.forEach((group: any, groupIndex: number) => {
    if (settingsData.value[groupIndex]) {
      const startDate = group.caregiverInfo?.lem_work_start_date;
      
      if (startDate) {
        // 근속년수 계산
        const serviceYears = calculateServiceYears(startDate, searchParams.value.lebYm);
        
        // 장기근속수당 금액 계산
        const amount = calculateLongServiceAmount(serviceYears);
        
        // 설정 적용
        settingsData.value[groupIndex].longServiceAllowance = new Intl.NumberFormat('ko-KR').format(amount);
        appliedCount++;
      }
    }
  });

  if (appliedCount > 0) {
    // 반응성 업데이트 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    // 모든 그룹에 대해 계산 수행
    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    // 계산 완료 후 추가 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`장기근속수당이 근속년수에 따라 자동계산되었습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  } else {
    showSnackbar("입사일 정보가 없어 장기근속수당을 계산할 수 없습니다.", "warning");
  }
};

// 장기근속수당 모두 미지급 (0원)
const applyBulkLongServiceClear = async () => {
  if (!groupedMemberData.value || groupedMemberData.value.length === 0) {
    showSnackbar("적용할 데이터가 없습니다.", "warning");
    return;
  }

  let appliedCount = 0;

  groupedMemberData.value.forEach((group: any, groupIndex: number) => {
    if (settingsData.value[groupIndex]) {
      settingsData.value[groupIndex].longServiceAllowance = '0';
      appliedCount++;
    }
  });

  if (appliedCount > 0) {
    // 반응성 업데이트 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    // 모든 그룹에 대해 계산 수행
    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    // 계산 완료 후 추가 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`모든 장기근속수당이 0원(미지급)으로 설정되었습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  }
};

// 보수교육비 일괄 적용
const applyBulkBosuEduPay = async (value: string) => {
  if (!groupedMemberData.value || groupedMemberData.value.length === 0) {
    showSnackbar("적용할 데이터가 없습니다.", "warning");
    return;
  }

  const numericValue = value.replace(/[^\d]/g, '');
  const amount = parseInt(numericValue) || 0;
  let appliedCount = 0;

  groupedMemberData.value.forEach((group: any, groupIndex: number) => {
    if (settingsData.value[groupIndex]) {
      settingsData.value[groupIndex].bosuEduPayAllowance = amount > 0 
        ? new Intl.NumberFormat('ko-KR').format(amount) 
        : '0';
      appliedCount++;
    }
  });

  if (appliedCount > 0) {
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`보수교육비 ${new Intl.NumberFormat('ko-KR').format(amount)}원이 일괄 적용되었습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  }
};

// 보수교육비 모두 미지급 (0원)
const applyBulkBosuEduPayClear = async () => {
  if (!groupedMemberData.value || groupedMemberData.value.length === 0) {
    showSnackbar("적용할 데이터가 없습니다.", "warning");
    return;
  }

  let appliedCount = 0;

  groupedMemberData.value.forEach((group: any, groupIndex: number) => {
    if (settingsData.value[groupIndex]) {
      settingsData.value[groupIndex].bosuEduPayAllowance = '0';
      appliedCount++;
    }
  });

  if (appliedCount > 0) {
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`모든 보수교육비가 0원(미지급)으로 설정되었습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  }
};

// 과거 국민연금/건강보험/고용보험 설정 일괄 적용 (가장 최근 데이터만)
const applyBulkInsuranceFromPrevious = async (recentData: { lebYm: string; data: any[] }) => {
  if (!recentData || !recentData.data || recentData.data.length === 0) return;

  // 현재 연월과 동일한 경우 중복 방지
  if (recentData.lebYm === searchParams.value.lebYm) {
    showSnackbar("현재 연월의 데이터는 과거 설정으로 불러올 수 없습니다.", "warning");
    return;
  }

  let appliedCount = 0;

  if (groupedMemberData.value && groupedMemberData.value.length > 0) {
    groupedMemberData.value.forEach((group: any, groupIndex: number) => {
      if (settingsData.value[groupIndex]) {
        // 최근 데이터에서 요양보호사 찾기
        const matchedItem = recentData.data.find((prevItem: any) =>
          prevItem.caregiver_name === group.caregiverInfo.caregiver_name &&
          prevItem.caregiver_birth === group.caregiverInfo.caregiver_birth &&
          prevItem.lem_gender === group.caregiverInfo.lem_gender
        );

        if (matchedItem) {
          // 나이 조건 확인
          const caregiverAge = calculateAge(group.caregiverInfo.caregiver_birth, searchParams.value.lebYm);

          // 건강보험 - 모든 연령에 적용 가능
          if (matchedItem.lemi_apply_gun !== undefined) {
            settingsData.value[groupIndex].applyHealthInsurance = matchedItem.lemi_apply_gun === 'Y';
            appliedCount++;
          }

          // 고용보험 - 만 65세 미만만 적용
          if (matchedItem.lemi_apply_go !== undefined && caregiverAge < 65) {
            settingsData.value[groupIndex].applyEmploymentInsurance = matchedItem.lemi_apply_go === 'Y';
            appliedCount++;
          }

          // 국민연금 금액 - 만 60세 미만만 적용
          if (matchedItem.lemi_yeon_pay !== undefined && caregiverAge < 60) {
            let yeonPay = matchedItem.lemi_yeon_pay || '';
            if (yeonPay) {
              const numericValue = parseFloat(String(yeonPay).replace(/[^\d]/g, '')) || 0;
              if (numericValue > 0) {
                yeonPay = new Intl.NumberFormat('ko-KR').format(numericValue);
              }
            }
            settingsData.value[groupIndex].yeonPay = yeonPay;
            appliedCount++;
          }
        }
      }
    });
  }

  if (appliedCount > 0) {
    const year = recentData.lebYm.substring(0, 4);
    const month = recentData.lebYm.substring(4, 6);

    // 반응성 업데이트 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    // 모든 그룹에 대해 계산 수행
    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        for (let i = 0; i < groupedMemberData.value.length; i++) {
          const group = groupedMemberData.value[i];
          if (group) {
            calculateGroupTotals(group, i);
          }
        }
      } finally {
        isCalculating.value = false;
      }
    }

    // 계산 완료 후 추가 대기
    await nextTick();
    await new Promise(resolve => setTimeout(resolve, 300));

    showSnackbar(`국민연금/건강보험/고용보험 설정이 ${year}년 ${month}월 과거 설정으로 불러왔습니다. '모두 저장' 버튼을 눌러 저장해주세요.`, "success");
  } else {
    showSnackbar("일치하는 과거 데이터를 찾을 수 없습니다.", "warning");
  }
};

// 모든 과거 설정 일괄 적용
const applyAllPreviousSettings = async (data: { allData: Array<{ lebYm: string; data: any[] }>; recentData: { lebYm: string; data: any[] } }, autoSave: boolean = false) => {
  // 시간당 임금 적용 (여러 달 순차 검색)
  if (data.allData && data.allData.length > 0) {
    applyBulkTimePayFromPrevious(data.allData);
    // 반응성 업데이트 대기
    await nextTick();
  }

  // 수당 및 보험 설정 적용 (최근 1개월)
  if (data.recentData && data.recentData.data && data.recentData.data.length > 0) {
    applyBulkHolidayAllowanceFromPrevious(data.recentData);
    await nextTick();

    applyBulkOvertimeAllowanceFromPrevious(data.recentData);
    await nextTick();

    applyBulkRetirementFromPrevious(data.recentData);
    await nextTick();

    applyBulkMealAllowanceFromPrevious(data.recentData);
    await nextTick();

    applyBulkLongServiceFromPrevious(data.recentData);
    await nextTick();

    applyBulkInsuranceFromPrevious(data.recentData);
    await nextTick();
  }

  // 모든 설정 적용 후 추가 대기 (computed 재계산 완료 보장)
  await nextTick();
  await new Promise(resolve => setTimeout(resolve, 300));
  await nextTick();
  await new Promise(resolve => setTimeout(resolve, 300));

  // 자동 저장이 아닐 때만 스낵바 표시
  if (!autoSave) {
    showSnackbar("모든 과거 설정이 적용되었습니다. '모두 저장' 버튼을 눌러 저장해주세요.", "success");
  }
};
// 자동으로 과거 설정 불러오기 (hh_longterm_excel_member_info에 데이터가 없을 때)
const autoLoadPreviousSettings = async () => {
  if (!searchParams.value.sisulCode || !searchParams.value.lebYm) {
    return;
  }

  const currentYear = parseInt(searchParams.value.lebYm.substring(0, 4));
  const currentMonth = parseInt(searchParams.value.lebYm.substring(4, 6));
  const currentLebYm = searchParams.value.lebYm;

  try {
    // 시간당 임금용 여러 달 데이터 수집
    const allPreviousData: Array<{ lebYm: string; data: any[] }> = [];
    let searchMonth = currentMonth - 1;

    while (searchMonth >= 1) {
      const targetLebYm = `${currentYear}${String(searchMonth).padStart(2, '0')}`;

      if (targetLebYm === currentLebYm) {
        searchMonth--;
        continue;
      }

      try {
        const response = await axios.get('/api/calc/caregiver-member-join', {
          params: {
            sisulCode: searchParams.value.sisulCode,
            lebYm: targetLebYm,
          },
        });

        const data = response.data.data || [];

        if (data.length > 0) {
          allPreviousData.push({
            lebYm: targetLebYm,
            data: data
          });
        }
      } catch (error) {
        // 데이터 없음, 계속 검색
      }

      searchMonth--;
    }

    // 수당/보험용 최근 1개월 데이터 찾기
    let recentData: { lebYm: string; data: any[] } | null = null;
    searchMonth = currentMonth - 1;

    while (searchMonth >= 1) {
      const targetLebYm = `${currentYear}${String(searchMonth).padStart(2, '0')}`;

      if (targetLebYm === currentLebYm) {
        searchMonth--;
        continue;
      }

      try {
        const response = await axios.get('/api/calc/caregiver-member-join', {
          params: {
            sisulCode: searchParams.value.sisulCode,
            lebYm: targetLebYm,
          },
        });

        const data = response.data.data || [];

        if (data.length > 0) {
          recentData = {
            lebYm: targetLebYm,
            data: data
          };
          break; // 첫 번째 데이터를 찾으면 중단
        }
      } catch (error) {
        // 데이터 없음, 계속 검색
      }

      searchMonth--;
    }

    // 과거 설정 적용
    if (allPreviousData.length > 0 || recentData) {
      // 모든 과거 설정 적용 (완료될 때까지 대기)
      await applyAllPreviousSettings({
        allData: allPreviousData,
        recentData: recentData || { lebYm: '', data: [] }
      }, true); // autoSave: true로 설정하여 자동 저장 모드

      // 모든 그룹의 계산이 완료될 때까지 대기
      await nextTick();
      await new Promise(resolve => setTimeout(resolve, 500));

      // 모든 그룹에 대해 계산 수행 (반응성 업데이트 완료 보장)
      if (groupedMemberData.value && groupedMemberData.value.length > 0) {
        isCalculating.value = true;
        try {
          for (let i = 0; i < groupedMemberData.value.length; i++) {
            const group = groupedMemberData.value[i];
            if (group) {
              calculateGroupTotals(group, i);
            }
          }
        } finally {
          isCalculating.value = false;
        }
      }

      // 계산 완료 후 추가 대기

    }
  } catch (error: any) {
    console.error('자동 과거 설정 불러오기 오류:', error);
  }
};

// 스낵바 표시
const showSnackbar = (message: string, color: string = "success") => {
  snackbar.value = {
    show: true,
    message,
    color,
  };
};

const getTax = async () => {
  if (!searchParams.value.lebYm) {
    showSnackbar("년월을 입력해주세요.", "error");
    return;
  }
  try {
    const response = await axios.get(`/api/calc/tax/${searchParams.value.lebYm.slice(0, 4)}`);
    if (response.data.success) {
      taxData.value = response.data.data.taxRate;
      sojiData.value = response.data.data.soji;
    } else {
      showSnackbar(response.data.message || "세액 조회 중 오류가 발생했습니다.", "error");
    }
  } catch (error: any) {
    console.error("세액 조회 오류:", error);
    showSnackbar("세액 조회 중 오류가 발생했습니다.", "error");
  }
};

// 기본 급여 계산 헬퍼 (기본급 + 주휴수당 + 연차수당)
const calculateBasicPay = (sugaHours: number, serviceDays: number, workStartDate?: string) => {
  if (!taxData.value) return 0;

  const basicPay = taxData.value.tr_time_pay * sugaHours;
  const holidayPay = taxData.value.tr_time_pay * sugaHours * 0.2;
  // 연차수당 = (시간당임금 * 수가시간 / 근무일수) * 연차개수 / 12
  const annualLeaveDays = workStartDate ? getAnnualLeaveDays(workStartDate) : 0;
  const vacationPay = Math.round((taxData.value.tr_time_pay * sugaHours / serviceDays) * annualLeaveDays / 12);

  return basicPay + holidayPay + vacationPay;
};

// 그룹별 총계 계산 함수
const calculateGroupTotals = (group: any, groupIndex: number) => {
  if (!taxData.value || Object.keys(taxData.value).length === 0) return;

  // 기본 급여 계산 (개별 값)
  const basicPay = taxData.value.tr_time_pay * group.totals.suga_hours;
  const holidayPay = taxData.value.tr_time_pay * group.totals.suga_hours * 0.2;
  const annualLeaveDays = getAnnualLeaveDays(group.caregiverInfo.lem_work_start_date);
  let vacationPay = Math.round((taxData.value.tr_time_pay * group.totals.suga_hours / group.totals.service_days) * annualLeaveDays / 12);

  // 장기근속수당 (급여외수당) - 설정탭에서 입력한 값만 사용
  const originalIndex = group._originalIndex !== undefined ? group._originalIndex : groupIndex;
  const longServiceAllowance = settingsData.value[originalIndex]?.longServiceAllowance
    ? parseFloat(String(settingsData.value[originalIndex].longServiceAllowance).replace(/[^\d]/g, '')) || 0
    : 0;

  // 보수교육비 (급여외수당) - 설정탭에서 입력한 값만 사용
  const bosuEduPayAllowance = settingsData.value[originalIndex]?.bosuEduPayAllowance
    ? parseFloat(String(settingsData.value[originalIndex].bosuEduPayAllowance).replace(/[^\d]/g, '')) || 0
    : 0;

  // 중증수급자 가산 자동 계산 (급여외수당)
  const severeCareAddition = getSevereCareAdditionTotal(originalIndex);

  // 가족 케어 전용 여부 확인
  const isFamilyOnlyCaregiver = isAllFamilyCaregiver(group);

  // 휴일수당 계산 (설정에 따라) (급여외수당) - 60시간 미만이면 지급 안됨
  // 공휴일가산 수가가 있는 서비스만 대상 (가족요양도 공휴일가산 수가가 있으면 적용 가능)
  const applyHoliday = canApplyHolidayAllowance(originalIndex) && settingsData.value[originalIndex]?.applyHolidayAllowance !== false;
  const holidayAllowance = getHolidayAllowanceAmount(originalIndex, applyHoliday);

  // 연장수당 계산 (설정에 따라) (급여외수당) - 60시간 미만이면 지급 안됨
  const applyOvertime = canApplyOvertimeAllowance(originalIndex) && settingsData.value[originalIndex]?.applyOvertimeAllowance !== false;
  const overtimeAllowance = getOvertimeAllowanceAmount(originalIndex, applyOvertime);

  // 근로자의날 수당 계산 (설정에 따라) - 5월만 (급여외수당) - 60시간 미만이면 지급 안됨
  const applyLaborDay = canApplyLaborDayAllowance(originalIndex) && settingsData.value[originalIndex]?.applyLaborDayAllowance !== false;
  const laborDayAllowance = getLaborDayAllowanceAmount(originalIndex, applyLaborDay);

  // 총 월급여
  const totalMonthlyPay = group.totals.monthly_pay;

  // 식대 계산 (월급여 = 기본급 + 주휴 + 연차 + 식대)
  const applyMeal = settingsData.value[originalIndex]?.applyMealAllowance !== false;
  let actualMealAllowance = 0;

  // 가족 케어 전용이 아닌 경우만 식대 지급
  if (!isFamilyOnlyCaregiver && applyMeal && group.totals?.lunch_time_count > 0) {
    // 월급여에서 기본급+주휴+연차를 뺀 나머지
    const remainingForMeal = totalMonthlyPay - (basicPay + holidayPay + vacationPay);

    if (remainingForMeal > 0) {
      // 요청 식대 = min(일수 × 20000, 200000)
      const lunchTimeCount = group.totals.lunch_time_count;
      const requestedMealAllowance = Math.min(lunchTimeCount * 20000, 200000);
      // 실제 식대 = min(요청 식대, 남은 금액)
      actualMealAllowance = Math.min(requestedMealAllowance, remainingForMeal);
    }
  }

  // 남은 금액을 연차수당에 합산 (월급여 - 기본급 - 주휴 - 연차 - 식대)
  const remainingAmount = totalMonthlyPay - (basicPay + holidayPay + vacationPay) - actualMealAllowance;
  if (remainingAmount > 0) {
    vacationPay += remainingAmount;
  }

  // 기본급 총합 계산
  let basicTotal;
  if (isFamilyOnlyCaregiver) {
    // 가족 케어 전용: 월급여 전체가 기본급 (주휴수당, 연차수당, 식대 모두 포함)
    basicTotal = totalMonthlyPay;
  } else {
    // 일반: 기본급 + 주휴수당 + 연차수당
    basicTotal = basicPay + holidayPay + vacationPay;
  }

  // 수당 업데이트
  group.allowances.holiday = holidayAllowance;
  group.allowances.overtime = overtimeAllowance;
  group.allowances.laborDay = laborDayAllowance;
  group.allowances.meal = actualMealAllowance;
  group.allowances.longService = longServiceAllowance;
  group.allowances.bosuEduPay = bosuEduPayAllowance;
  group.allowances.severeCareAddition = severeCareAddition;

  // totals에 basicTotal 저장
  if (isFamilyOnlyCaregiver) {
    // 가족 케어 전용: basicTotal에 이미 모든 것이 포함됨
    group.totals.basicTotal = basicTotal;
  } else {
    // 일반: 급여 소계 표시용: 기본급 + 주휴수당 + 연차수당 + 식대
    group.totals.basicTotal = basicTotal + actualMealAllowance;
  }
  group.totals.vacationPay = vacationPay;

  // 과세 수당 (장기근속수당 + 보수교육비 + 중증수급자가산 + 근로자의날)
  const taxableAllowances = longServiceAllowance + bosuEduPayAllowance + severeCareAddition + laborDayAllowance;

  // 총 급여 = 월급여 + 급여외수당
  let totalPay;
  let salarySubtotal;
  
  if (isFamilyOnlyCaregiver) {
    // 가족 케어 전용: basicTotal에 이미 모든 월급여가 포함됨
    totalPay = basicTotal + longServiceAllowance + bosuEduPayAllowance + severeCareAddition + laborDayAllowance + holidayAllowance + overtimeAllowance;
    salarySubtotal = basicTotal;
  } else {
    // 일반: 월급여 = basicTotal + actualMealAllowance
    // 급여외수당 = longServiceAllowance + bosuEduPayAllowance + severeCareAddition + laborDayAllowance + holidayAllowance + overtimeAllowance
    totalPay = basicTotal + actualMealAllowance + longServiceAllowance + bosuEduPayAllowance + severeCareAddition + laborDayAllowance + holidayAllowance + overtimeAllowance;
    // 급여 소계 계산 (기본급 + 주휴수당 + 연차수당 + 식대) - 비과세 처리 조건 확인용
    salarySubtotal = basicTotal + actualMealAllowance;
  }
  
  // 비과세 처리 조건:
  // 1. 수가시간 60시간 이상
  // 2. 급여 소계가 210만원 이하
  const canApplyHoliday = canApplyHolidayAllowance(originalIndex);
  const canApplyOvertime = canApplyOvertimeAllowance(originalIndex);
  const isSalaryUnder2_1M = salarySubtotal <= 2100000; // 210만원 이하 체크
  
  // 휴일수당, 연장수당 비과세 처리 (각각 최대 20만원까지, 급여 소계 210만원 이하일 때만)
  const nonTaxableHoliday = (canApplyHoliday && isSalaryUnder2_1M) 
    ? Math.min(holidayAllowance, 200000) 
    : 0;
  const nonTaxableOvertime = (canApplyOvertime && isSalaryUnder2_1M) 
    ? Math.min(overtimeAllowance, 200000) 
    : 0;
  
  // 휴일수당, 연장수당 중 과세 처리되는 부분 (비과세 한도 초과분)
  const taxableHoliday = holidayAllowance - nonTaxableHoliday;
  const taxableOvertime = overtimeAllowance - nonTaxableOvertime;

  // 과세 급여 계산 (식대와 비과세 처리되는 휴일/연장수당 제외)
  const taxablePay = basicTotal + taxableAllowances + taxableHoliday + taxableOvertime;

  // 만 나이 계산
  const caregiverAge = calculateAge(group.caregiverInfo.caregiver_birth, searchParams.value.lebYm);

  // 국민연금 계산 (직접 입력한 금액 사용, 만 60세 미만만 적용)
  const yeonPayAmount = settingsData.value[originalIndex]?.yeonPay
    ? parseFloat(String(settingsData.value[originalIndex].yeonPay).replace(/[^\d]/g, '')) || 0
    : 0;
  const applyNationalPension = caregiverAge < 60 && yeonPayAmount > 0;
  group.insurance.nationalPension.amount = applyNationalPension ? yeonPayAmount : 0;

  // 건강보험 계산 (체크박스 해제면 0원, 원단위 절삭)
  const applyHealthInsurance = settingsData.value[originalIndex]?.applyHealthInsurance !== false;
  if (!applyHealthInsurance) {
    group.insurance.healthInsurance.amount = 0;
  } else {
    group.insurance.healthInsurance.amount = truncateToTen(taxablePay * taxData.value.tr_gun);
  }

  // 고용보험 계산 (체크박스 해제면 0원, 원단위 절삭)
  const applyEmploymentInsurance = settingsData.value[originalIndex]?.applyEmploymentInsurance !== false;
  if (!applyEmploymentInsurance) {
    group.insurance.employmentInsurance.amount = 0;
  } else {
    group.insurance.employmentInsurance.amount = truncateToTen(taxablePay * taxData.value.tr_go);
  }

  // 산재보험 재계산 (원단위 절삭)
  group.insurance.industrialAccident.amount = truncateToTen(taxablePay * taxData.value.tr_san);

  // 장기요양보험은 건강보험료의 12.95%로 자동 계산 (원단위 절삭)
  group.insurance.longTermCare.amount = truncateToTen(group.insurance.healthInsurance.amount * taxData.value.tr_jang);

  // 실업급여 재계산 (원단위 절삭)
  group.insurance.unemployment.amount = truncateToTen(taxablePay * taxData.value.tr_sil);

  // 사대보험 총합 계산 (사용자가 입력한 값들 합산 - 지원공제 차감)
  group.insurance.total =
    (group.insurance.nationalPension.amount || 0) +
    (group.insurance.healthInsurance.amount || 0) +
    (group.insurance.employmentInsurance.amount || 0) +
    (group.insurance.industrialAccident.amount || 0) +
    (group.insurance.longTermCare.amount || 0) +
    (group.insurance.unemployment.amount || 0) -
    (group.insurance.supportDeduction || 0);

  // 지원공제 계산
  // 공식: (장기근속수당+보수교육비+중증수급자가산) × (국민연금율 + 건강보험율 + 장기요양보험율 + 고용보험율 + 실업급여율 + 산재보험율) + ((장기근속수당+보수교육비) ÷ 12)
  // 단, 근무시간이 60시간 미만이면 퇴직금 부분 제외
  const supportDeductionBase = (group.allowances.longService || 0) + (group.allowances.bosuEduPay || 0) + (group.allowances.severeCareAddition || 0);
  const longServiceAndBosuEdu = (group.allowances.longService || 0) + (group.allowances.bosuEduPay || 0);
  
  if (supportDeductionBase > 0) {
    let supportDeduction = 0;

    // 1. (장기근속수당+중증수급자가산) × 보험료율 합계 (원단위 절삭)
    
    // 국민연금 (근로자 부담)
    if (applyNationalPension) {
      supportDeduction += truncateToTen(supportDeductionBase * taxData.value.tr_yeon);
    }

    // 건강보험 (근로자 부담)
    let healthInsurance = 0;
    if (applyHealthInsurance) {
      healthInsurance = truncateToTen(supportDeductionBase * taxData.value.tr_gun);
      supportDeduction += healthInsurance;
    }

    // 장기요양보험 (근로자 부담) - 건강보험의 12.95%
    if (applyHealthInsurance) {
      supportDeduction += truncateToTen(healthInsurance * taxData.value.tr_jang);
    }

    // 고용보험 (근로자 부담)
    if (applyEmploymentInsurance) {
      supportDeduction += truncateToTen(supportDeductionBase * taxData.value.tr_go);
    }

    // 실업급여 (근로자 부담)
    supportDeduction += truncateToTen(supportDeductionBase * taxData.value.tr_sil);

    // 산재보험 (근로자 부담)
    supportDeduction += truncateToTen(supportDeductionBase * taxData.value.tr_san);

    // 2. 퇴직금 부분 ((장기근속수당+보수교육비) ÷ 12)
    // 단, 근무시간이 60시간 미만이면 제외 (원단위 절삭)
    const monthlyWorkHours = group.totals.suga_hours || 0;
    if (monthlyWorkHours >= 60 && longServiceAndBosuEdu > 0) {
      supportDeduction += truncateToTen(longServiceAndBosuEdu / 12);
    }

    group.insurance.supportDeduction = supportDeduction;
  } else {
    group.insurance.supportDeduction = 0;
  }

  // 세금 계산 (과세 급여 기준, 원단위 절삭)
  group.tax.incomeTax = truncateToTen(calculateIncomeTax(taxablePay));
  group.tax.localTax = truncateToTen(group.tax.incomeTax * 0.1); // 지방세는 소득세의 10%
  group.tax.total = group.tax.incomeTax + group.tax.localTax;

  // 과세/비과세 구분 계산
  // 과세 금액 = 기본급 + 주휴수당 + 연차수당 + 기타수당 + 과세처리되는수당(장기근속+중증가산+근로자의날) + 휴일수당과세분 + 연장수당과세분
  group.totals.taxableAmount = basicTotal + taxableAllowances + taxableHoliday + taxableOvertime;
  group.totals.nonTaxableAmount = nonTaxableHoliday + nonTaxableOvertime + actualMealAllowance; // 비과세 금액 (휴일수당 + 연장수당 + 식대)
  group.totals.salarySubtotal = salarySubtotal; // 급여 소계 저장 (UI 표시용)

  // 비과세 효과 계산 (근로자 공제 절감 + 기관 공제 절감으로 분리)
  if (group.totals.nonTaxableAmount > 0) {
    // 비과세 금액을 과세로 했을 때의 총 과세 금액
    const taxableWithNonTax = group.totals.taxableAmount + group.totals.nonTaxableAmount;

    // === 근로자 부담 절감액 (실수령액 증가액) ===
    // 1. 소득세, 지방세 절감
    const incomeTaxWithNonTax = calculateIncomeTax(taxableWithNonTax);
    const localTaxWithNonTax = Math.round(incomeTaxWithNonTax * 0.1);
    const currentIncomeTax = group.tax.incomeTax;
    const currentLocalTax = group.tax.localTax;
    let taxSavings = (incomeTaxWithNonTax + localTaxWithNonTax) - (currentIncomeTax + currentLocalTax);

    // 세금 절약액이 0이거나 음수인 경우 최소 세율 적용
    if (taxSavings <= 0) {
      const estimatedTaxRate = 0.0726;
      taxSavings = Math.round(group.totals.nonTaxableAmount * estimatedTaxRate);
    }

    // 2. 국민연금 절감 (근로자 부담분)
    let pensionSavings = 0;
    if (applyNationalPension) {
      pensionSavings = truncateToTen(group.totals.nonTaxableAmount * taxData.value.tr_yeon);
    }

    // 3. 건강보험 + 장기요양보험 절감 (근로자 부담분)
    let healthInsuranceSavings = 0;
    let longTermCareSavings = 0;
    if (applyHealthInsurance) {
      healthInsuranceSavings = truncateToTen(group.totals.nonTaxableAmount * taxData.value.tr_gun);
      longTermCareSavings = truncateToTen(healthInsuranceSavings * taxData.value.tr_jang);
    }

    // 4. 고용보험 절감 (근로자 부담분)
    let employmentInsuranceSavings = 0;
    if (applyEmploymentInsurance) {
      employmentInsuranceSavings = truncateToTen(group.totals.nonTaxableAmount * taxData.value.tr_go);
    }

    // 근로자 부담 총 절감액 (실수령액 증가액)
    group.totals.employeeTaxSavings = Math.max(0,
      taxSavings +
      pensionSavings +
      healthInsuranceSavings +
      longTermCareSavings +
      employmentInsuranceSavings
    );

    // === 센터 부담 절감액 (인건비 절감액) ===
    // 1. 국민연금 절감 (사업주 부담분)
    let centerPensionSavings = 0;
    if (applyNationalPension) {
      centerPensionSavings = truncateToTen(group.totals.nonTaxableAmount * taxData.value.tr_yeon);
    }

    // 2. 건강보험 + 장기요양보험 절감 (사업주 부담분)
    const centerHealthInsuranceSavings = truncateToTen(group.totals.nonTaxableAmount * taxData.value.tr_gun);
    const centerLongTermCareSavings = truncateToTen(centerHealthInsuranceSavings * taxData.value.tr_jang);

    // 3. 고용보험 절감 (사업주 부담분)
    let centerEmploymentInsuranceSavings = 0;
    if (applyEmploymentInsurance) {
      centerEmploymentInsuranceSavings = truncateToTen(group.totals.nonTaxableAmount * taxData.value.tr_go);
    }

    // 4. 실업급여 절감 (사업주 부담분)
    const unemploymentSavings = truncateToTen(group.totals.nonTaxableAmount * taxData.value.tr_sil);

    // 5. 산재보험 절감 (사업주 부담분)
    const industrialAccidentSavings = truncateToTen(group.totals.nonTaxableAmount * taxData.value.tr_san);

    // 센터 부담 총 절감액 (인건비 절감액)
    group.totals.centerTaxSavings = Math.max(0,
      centerPensionSavings +
      centerHealthInsuranceSavings +
      centerLongTermCareSavings +
      centerEmploymentInsuranceSavings +
      unemploymentSavings +
      industrialAccidentSavings
    );

    // 전체 절약액 (호환성을 위해 유지)
    group.totals.taxSavings = group.totals.employeeTaxSavings + group.totals.centerTaxSavings;

  } else {
    group.totals.employeeTaxSavings = 0;
    group.totals.centerTaxSavings = 0;
    group.totals.taxSavings = 0;
  }

  // summaryInfo 업데이트 (비과세 효과 변경 반영)
  calculateSummaryInfo();

  // 근로자 공제 총합 계산 (실수령액 계산용)
  const totalEmployeeDeductions =
    (group.insurance.nationalPension.amount || 0) +
    (group.insurance.healthInsurance.amount || 0) +
    (group.insurance.longTermCare.amount || 0) +
    (group.insurance.employmentInsurance.amount || 0) +
    (group.tax.incomeTax || 0) +
    (group.tax.localTax || 0) +
    (group.insurance.supportDeduction || 0);

  const netPay = totalPay - totalEmployeeDeductions;

  // 최종 계산
  group.totals.totalPay = totalPay;
  group.totals.netPay = netPay;
};

// taxData 변경 시 모든 그룹 재계산
watch(
  () => taxData.value,
  () => {
    if (isCalculating.value) return;

    if (groupedMemberData.value && groupedMemberData.value.length > 0) {
      isCalculating.value = true;
      try {
        groupedMemberData.value.forEach((group: any, groupIndex: number) => {
          calculateGroupTotals(group, groupIndex);
        });
      } finally {
        isCalculating.value = false;
      }
    }
  },
  { deep: true }
);

// 중복된 watch 제거됨 - 위의 watch에서 처리

onMounted(async () => {
  // beforeunload 이벤트 리스너 추가 (페이지/탭 이탈 감지)
  window.addEventListener('beforeunload', handleBeforeUnload);

  // 사용자 정보가 로드된 후 자동 조회 (watch에서 처리되므로 여기서는 초기화만)
  if (searchParams.value.sisulCode && searchParams.value.lebYm) {
    // watch가 자동으로 호출됨
  }
});

// 페이지/탭 이탈 시 저장 확인
const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  // 데이터가 있는 경우에만 확인
  if (memberJoinData.value && memberJoinData.value.length > 0) {
    e.preventDefault();
    e.returnValue = ''; // Chrome에서는 빈 문자열 필요
    return ''; // 일부 브라우저에서는 반환값 필요
  }
};

// 컴포넌트 언마운트 시
onBeforeUnmount(async () => {
  // beforeunload 이벤트 리스너 제거
  window.removeEventListener('beforeunload', handleBeforeUnload);
  
  // 컴포넌트 언마운트 시 저장 확인 (같은 페이지 내 탭 전환 등)
  if (memberJoinData.value && memberJoinData.value.length > 0) {
    const confirmed = confirm('페이지를 빠져나가시겠습니까?\n\n변경사항을 모두 저장하시겠습니까?');
    
    if (confirmed) {
      await saveAllData();
    }
  }
});

// 탭 초기화 함수
const initializeTabs = () => {
  if (groupedMemberData.value) {
    groupedMemberData.value.forEach((_, index) => {
      if (!activeTab.value[index]) {
        activeTab.value[index] = 'summary';
      }
    });
  }
};

// 원단위 절삭 함수 (예: 51131 -> 51130)
const truncateToTen = (value: number) => Math.floor(value / 10) * 10;

// 만 나이 계산 함수
const calculateAge = (birthDateStr: string, baseYm: string) => {
  if (!birthDateStr || !baseYm) return 0;

  const birthDate = new Date(birthDateStr);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));

  // 기준일 설정 (해당 월의 마지막 날)
  const baseDate = new Date(baseYear, baseMonth, 0);

  // 만 나이 계산
  let age = baseDate.getFullYear() - birthDate.getFullYear();

  // 생일이 지나지 않았으면 1살 빼기
  if (
    baseDate.getMonth() < birthDate.getMonth() ||
    (baseDate.getMonth() === birthDate.getMonth() && baseDate.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};

// 근속년수 계산 함수 (년/월 단위)
const calculateWorkPeriod = (startDateStr: string, baseYm: string) => {
  if (!startDateStr || !baseYm) return { years: 0, months: 0 };

  const workStartDate = new Date(startDateStr);
  const baseYear = parseInt(baseYm.substring(0, 4));
  const baseMonth = parseInt(baseYm.substring(4, 6));
  const baseDate = new Date(baseYear, baseMonth - 1, 1); // 해당 월의 1일

  let years = baseDate.getFullYear() - workStartDate.getFullYear();
  let months = baseDate.getMonth() - workStartDate.getMonth();

  if (months < 0) {
    years--;
    months += 12;
  }

  return { years, months };
};

// 근속년수 포맷팅 함수
const formatWorkPeriod = (years: number, months: number) => {
  if (years === 0 && months === 0) return '0개월';
  if (years === 0) return `${months}개월`;
  if (months === 0) return `${years}년`;
  return `${years}년 ${months}개월`;
};

// 근속기간 텍스트 가져오기 함수
const getWorkPeriodText = (startDateStr: string) => {
  if (!startDateStr || !searchParams.value.lebYm) return '-';
  const period = calculateWorkPeriod(startDateStr, searchParams.value.lebYm);
  return formatWorkPeriod(period.years, period.months);
};

// 년차 계산 옵션 (WageCalc.vue와 동일)
const vacationYearOptions = [
  { value: 1, label: '1년차', days: 11 },
  { value: 2, label: '2년차', days: 15 },
  { value: 3, label: '3년차', days: 15 },
  { value: 4, label: '4년차', days: 16 },
  { value: 5, label: '5년차', days: 16 },
  { value: 6, label: '6년차', days: 17 },
  { value: 7, label: '7년차', days: 17 },
  { value: 8, label: '8년차', days: 18 },
  { value: 9, label: '9년차', days: 18 },
  { value: 10, label: '10년차', days: 19 },
  { value: 11, label: '11년차', days: 19 },
  { value: 12, label: '12년차', days: 20 },
  { value: 13, label: '13년차', days: 20 },
  { value: 14, label: '14년차', days: 21 },
  { value: 15, label: '15년차', days: 21 },
  { value: 16, label: '16년차', days: 22 },
  { value: 17, label: '17년차', days: 22 },
  { value: 18, label: '18년차', days: 23 },
  { value: 19, label: '19년차', days: 23 },
  { value: 20, label: '20년차', days: 24 },
  { value: 21, label: '21년차이상', days: 25 },
];

// 근속연수에 따른 연차 일수 계산 함수
// 9개월 -> 1년차, 1년1개월 -> 2년차, 2년2개월 -> 3년차
const getAnnualLeaveDays = (startDateStr: string) => {
  if (!startDateStr || !searchParams.value.lebYm) return 0;
  
  const period = calculateWorkPeriod(startDateStr, searchParams.value.lebYm);
  
  // 근속기간이 0인 경우 0 반환
  if (period.years === 0 && period.months === 0) return 0;
  
  // 년차 계산: 0년 9개월 -> 1년차, 1년 0개월 -> 2년차, 2년 0개월 -> 3년차
  const yearLevel = period.years + 1;
  
  // 21년차 이상은 25일
  if (yearLevel >= 21) {
    return 25;
  }
  
  // 년차에 따른 연차 일수 찾기
  const option = vacationYearOptions.find(opt => opt.value === yearLevel);
  
  return option ? option.days : 0;
};

// 기관 공제 합계 계산 함수
const getCenterDeductionTotal = (group: any, groupIndex: number) => {
  const retirement = settingsData.value[groupIndex]?.applyRetirement && canApplyRetirement(group._originalIndex)
    ? Math.round((group.totals.totalPay || 0) / 12)
    : 0;

  return (group.insurance.nationalPension.amount || 0) +
    (group.insurance.healthInsurance.amount || 0) +
    (group.insurance.longTermCare.amount || 0) +
    (group.insurance.employmentInsurance.amount || 0) +
    (group.insurance.unemployment.amount || 0) +
    (group.insurance.industrialAccident.amount || 0) +
    retirement;
};

// 참고자료 계산 함수 (총수가금액 - 세전월급여 - 기관 공제금액)
const getComparisonAmount = (group: any, groupIndex: number) => {
  const totalSugaMoney = Number(group.totals.total_suga_money || 0);
  const totalPay = Number(group.totals.totalPay || 0);
  const centerDeduction = getCenterDeductionTotal(group, groupIndex);

  return totalSugaMoney - totalPay - centerDeduction;
};

// 퇴직금 적립 가능 여부 확인 함수 (수가시간 60시간 이상만 체크)
const canApplyRetirement = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return false;

  const group = groupedMemberData.value[groupIndex];

  // 월 수가시간 60시간 이상 체크
  const sugaHours = parseFloat(String(group.totals?.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;

  if (sugaHours < 60) {
    return false;
  }

  return true;
};

// 근로자의날 수당 지급 가능 여부 확인 함수 (수가시간 60시간 이상만 체크)
// 2026년 이상에서는 근로자의날이 일반 휴일로 처리되므로 별도 수당 없음
const canApplyLaborDayAllowance = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return false;

  // 2026년 이상에서는 근로자의날이 일반 공휴일로 처리됨
  const year = parseInt(searchParams.value.lebYm.substring(0, 4));
  if (year >= 2026) return false;

  const group = groupedMemberData.value[groupIndex];

  // 월 수가시간 60시간 이상 체크
  const sugaHours = parseFloat(String(group.totals?.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;

  if (sugaHours < 60) {
    return false;
  }

  return true;
};

// 휴일수당 지급 가능 여부 확인 함수 (수가시간 60시간 이상, 가족요양 제외)
// 휴일수당 지급 가능 여부 확인 함수
// 공휴일가산 수가가 있는 서비스만 대상 (날짜/가족요양 무관)
const canApplyHolidayAllowance = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return false;

  const group = groupedMemberData.value[groupIndex];

  // 월 수가시간 60시간 이상 체크
  const sugaHours = parseFloat(String(group.totals?.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;

  if (sugaHours < 60) {
    return false;
  }

  return true;
};

// 휴일수당 지급 불가 사유 반환 함수
const getHolidayAllowanceDisabledReason = (groupIndex: number): string => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return '';

  const group = groupedMemberData.value[groupIndex];

  // 월 수가시간 60시간 미만
  const sugaHours = parseFloat(String(group.totals?.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;
  if (sugaHours < 60) {
    return '수가시간 60시간 미만으로, 휴일수당은 지급되지 않습니다.';
  }

  return '';
};

// 연장수당 지급 가능 여부 확인 함수 (수가시간 60시간 이상만 체크)
const canApplyOvertimeAllowance = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return false;

  const group = groupedMemberData.value[groupIndex];

  // 월 수가시간 60시간 이상 체크
  const sugaHours = parseFloat(String(group.totals?.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;

  if (sugaHours < 60) {
    return false;
  }

  return true;
};

// 퇴직금 미적립 사유 반환 함수
const getRetirementDisabledReason = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return '';

  const group = groupedMemberData.value[groupIndex];

  // 근속년수 계산 (표시용)
  const startDate = group.caregiverInfo.lem_work_start_date;
  let workPeriodText = '';
  if (startDate) {
    const { years, months } = calculateWorkPeriod(startDate, searchParams.value.lebYm);
    workPeriodText = formatWorkPeriod(years, months);
  }

  // 수가시간 계산
  const sugaHours = parseFloat(String(group.totals?.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;
  const sugaHoursText = formatSugaHours(group.totals.suga_hours);

  // 월 수가시간 60시간 미만 체크
  if (sugaHours < 60) {
    return `월 수가시간이 60시간 미만입니다. \n (근속: ${workPeriodText || '정보없음'}, 수가시간: ${sugaHoursText})`;
  }

  return '';
};

// 퇴직금 적립 정보 반환 함수 (근속연수 포함, 표시용)
const getRetirementApplyInfo = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return '';

  const group = groupedMemberData.value[groupIndex];

  const startDate = group.caregiverInfo.lem_work_start_date;
  let workPeriodText = '';
  if (startDate) {
    const { years, months } = calculateWorkPeriod(startDate, searchParams.value.lebYm);
    workPeriodText = formatWorkPeriod(years, months);
  }
  const sugaHoursText = formatSugaHours(group.totals.suga_hours);

  return `퇴직금 적립 가능\n(근속: ${workPeriodText || '정보없음'}, 수가시간: ${sugaHoursText})`;
};

const getDepe1 = (value: any) => {
  for (let obj of sojiData.value) {
    if (value >= obj.income1 * 1000 && value < obj.income2 * 1000) {
      return obj.depe_1;
    }
  }
  return null; // 해당 값이 어떤 범위에도 속하지 않을 때
}

// 소득세 계산 함수 (개선된 버전)
const calculateIncomeTax = (taxableIncome: number) => {
  if (!sojiData.value || sojiData.value.length === 0) {
    return 0;
  }

  // 소득세 데이터에서 해당 구간 찾기
  for (let i = 0; i < sojiData.value.length; i++) {
    const obj = sojiData.value[i];
    const minIncome = obj.income1 * 1000;
    const maxIncome = obj.income2 * 1000;

    if (taxableIncome >= minIncome && taxableIncome < maxIncome) {
      const result = Math.round(obj.depe_1);
      return result;
    }
  }

  return 0;
}
// 숫자의 1의 단위를 절삭하는 함수 (예시)
const oneDel = (value: any) => {
  return Math.floor(value / 10) * 10;
}
// 주차별 40시간 초과 여부 확인 (월~일 기준)
const getWeeklyOvertime = (groupIndex: number) => {
  const services = getIndividualServicesForGroup(groupIndex);
  if (!services || services.length === 0) return [];

  // 1단계: 날짜별로 그룹핑하여 중복 제거 (같은 날짜의 여러 수가 항목 중 실제 근무시간만 카운트)
  const dailyMinutes: any = {};
  services.forEach((service: any) => {
    const dateKey = service.service_date;

    // 같은 날짜의 서비스 중 가장 긴 시간을 실제 근무시간으로 간주
    // (중증가산 등은 시간이 0이거나 중복되므로 최대값을 사용)
    if (!dailyMinutes[dateKey] || dailyMinutes[dateKey] < (service.service_minutes || 0)) {
      dailyMinutes[dateKey] = service.service_minutes || 0;
    }
  });

  // 2단계: 주차별로 그룹핑
  const weeklyData: any = {};

  Object.entries(dailyMinutes).forEach(([dateStr, minutes]: [string, any]) => {
    const serviceDate = new Date(dateStr);

    // 해당 날짜가 속한 주의 월요일 구하기
    const dayOfWeek = serviceDate.getDay(); // 0(일) ~ 6(토)
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // 월요일로부터의 차이
    const monday = new Date(serviceDate);
    monday.setDate(serviceDate.getDate() + diff);
    monday.setHours(0, 0, 0, 0);

    // 월요일 날짜를 키로 사용 (YYYY-MM-DD 형식)
    const weekKey = monday.toISOString().split('T')[0];

    // 해당 월요일이 속한 월 정보 (표시용)
    const displayMonth = monday.getMonth() + 1;
    const displayYear = monday.getFullYear();

    // 해당 월의 몇 번째 주인지 계산
    const firstDayOfMonth = new Date(displayYear, displayMonth - 1, 1);
    const firstMonday = new Date(firstDayOfMonth);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const daysToMonday = firstDayOfWeek === 0 ? 1 : (8 - firstDayOfWeek) % 7;
    firstMonday.setDate(1 + daysToMonday);

    const weekNumber = Math.floor((monday.getTime() - firstMonday.getTime()) / (7 * 24 * 60 * 60 * 1000)) + 1;

    if (!weeklyData[weekKey]) {
      weeklyData[weekKey] = {
        year: displayYear,
        month: displayMonth,
        weekNumber: Math.max(1, weekNumber),
        weekStartDate: weekKey,
        totalMinutes: 0,
        dates: []
      };
    }

    weeklyData[weekKey].totalMinutes += minutes;
    weeklyData[weekKey].dates.push(dateStr);
  });

  // 40시간(2400분) 초과한 주차만 필터링
  const overtimeWeeks = Object.values(weeklyData).filter((week: any) => {
    return week.totalMinutes > 2400; // 40시간 = 2400분
  });

  // 날짜 순으로 정렬
  return overtimeWeeks.sort((a: any, b: any) => {
    return a.weekStartDate.localeCompare(b.weekStartDate);
  });
};

// 근무시간 포맷팅 (분 → 시간)
const formatWorkHours = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (mins === 0) {
    return `${hours}시간`;
  } else {
    return `${hours}시간 ${mins}분`;
  }
};

// 공휴일 데이터 (API에서 동적으로 가져옴)
const holidaysData = ref<any>({});

// 요일 가져오기 (0: 일요일, 1: 월요일, ... 6: 토요일)
const getDayOfWeek = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.getDay();
};

// 공휴일 이름 가져오기
const getHolidayName = (dateStr: string) => {
  return holidaysData.value[dateStr] || null;
};

// 공휴일 데이터 불러오기
const loadHolidays = async (year: string) => {
  try {
    const response = await axios.get(`/api/calc/holidays/${year}`);
    if (response.data.success) {
      // 날짜를 키로 하는 객체로 변환
      const holidayMap: any = {};
      response.data.data.forEach((holiday: any) => {
        holidayMap[holiday.hh_date.split('T')[0]] = holiday.hh_holiday_name;
      });
      holidaysData.value = holidayMap;
    }
  } catch (error) {
    console.error('공휴일 조회 오류:', error);
  }
};

// 일요일 여부 확인
const isSunday = (dateStr: string) => {
  return getDayOfWeek(dateStr) === 0;
};

// 토요일 여부 확인
const isSaturday = (dateStr: string) => {
  return getDayOfWeek(dateStr) === 6;
};

// 그룹별 고유한 수가명 목록 가져오기
const getUniqueSugaNames = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return [];

  const group = groupedMemberData.value[groupIndex];
  const sugaNames = new Set<string>();

  // patientGroups의 items에서 수가명 추출
  if (group.patientGroups) {
    group.patientGroups.forEach((patientGroup: any) => {
      if (patientGroup.items) {
        patientGroup.items.forEach((item: any) => {
          if (item.suga_name && item.suga_name !== '중증수급자 가산') {
            sugaNames.add(item.suga_name);
          }
        });
      }
    });
  }

  return Array.from(sugaNames);
};

// 그룹별 수급자별 수가명 목록 가져오기 (수급자별로 구분)
const getPatientSugaNames = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return [];

  const group = groupedMemberData.value[groupIndex];
  const result: Array<{ patientCode: string, patientName: string, sugaNames: string[] }> = [];

  // patientGroups의 items에서 수급자별 수가명 추출
  if (group.patientGroups) {
    group.patientGroups.forEach((patientGroup: any) => {
      if (patientGroup.items && patientGroup.items.length > 0) {
        const sugaNames = new Set<string>();
        patientGroup.items.forEach((item: any) => {
          if (item.suga_name && item.suga_name !== '중증수급자 가산') {
            sugaNames.add(item.suga_name);
          }
        });

        if (sugaNames.size > 0) {
          result.push({
            patientCode: patientGroup.patient_code,
            patientName: patientGroup.patient_name,
            sugaNames: Array.from(sugaNames)
          });
        }
      }
    });
  }

  return result;
};

// 과거 데이터가 없는지 확인하는 함수 (설정 탭용)
const getNoPreviousDataFlag = (groupIndex: number, patientCode: string, sugaName: string): boolean => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return false;

  const group = groupedMemberData.value[groupIndex];

  // memberJoinData에서 해당 항목 찾기
  const foundItem = memberJoinData.value.find((item: any) =>
    item.caregiver_name === group.caregiverInfo.caregiver_name &&
    item.caregiver_birth === group.caregiverInfo.caregiver_birth &&
    item.lem_gender === group.caregiverInfo.lem_gender &&
    item.leb_patient_code === patientCode &&
    item.suga_name === sugaName
  );

  return foundItem?.noPreviousData === true;
};

// 그룹 내에 시급이 비어있는 항목이 있는지 확인하는 함수
const hasEmptyTimePayInGroup = (groupIndex: number): boolean => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return false;

  const group = groupedMemberData.value[groupIndex];

  // 그룹의 모든 patientGroups를 순회하면서 시급이 비어있는 항목이 있는지 확인
  if (group.patientGroups) {
    for (const patientGroup of group.patientGroups) {
      if (patientGroup.items) {
        for (const item of patientGroup.items) {
          // 중증수급자 가산은 제외
          if (item.suga_name === '중증수급자 가산') continue;

          // settingsData에서 시급 확인
          const timePayFromSettings = settingsData.value[groupIndex]?.serviceTimePay?.[item.leb_patient_code]?.[item.suga_name];
          // item.time_pay 확인
          const timePayFromItem = item.time_pay;

          // 둘 다 비어있으면 빈 것으로 간주
          const timePayValue = timePayFromSettings || timePayFromItem || '';
          const numericValue = timePayValue ? String(timePayValue).replace(/[^\d]/g, '') : '';

          if (!numericValue || numericValue === '0') {
            return true; // 빈 시급이 하나라도 있으면 true 반환
          }
        }
      }
    }
  }

  return false; // 모든 항목에 시급이 있으면 false 반환
};

// 그룹별 공휴일가산 수가 서비스 횟수 계산
// 수가명에 '공휴일가산'이 포함된 서비스의 총 서비스 횟수
const getHolidayCount = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return 0;

  const applyHoliday = settingsData.value[groupIndex]?.applyHolidayAllowance !== false;

  // 휴일수당 지급이 체크 해제되어 있으면 0
  if (!applyHoliday) return 0;

  const group = groupedMemberData.value[groupIndex];
  if (!group.patients || group.patients.length === 0) return 0;

  let count = 0;

  // 공휴일가산 수가를 가진 환자들의 서비스 횟수 합산
  group.patients.forEach((patient: any) => {
    const sugaName = patient.suga_name || patient.sugaName;
    
    // 수가명에 '공휴일가산'이 포함된 경우만 처리
    if (isHolidaySuga(sugaName)) {
      count += patient.service_days || patient.serviceDays || 0;
    }
  });

  return count;
};

// 일자별 서비스 데이터를 요양보호사별로 필터링하는 함수
const getDailyServicesForGroup = (groupIndex: number) => {
  if (!dailyServicesData.value || !groupedMemberData.value || !groupedMemberData.value[groupIndex]) {
    return [];
  }

  const group = groupedMemberData.value[groupIndex];
  const caregiverName = group.caregiverInfo.caregiver_name;
  const caregiverBirth = group.caregiverInfo.caregiver_birth;

  return dailyServicesData.value.filter((service: any) =>
    service.caregiver_name === caregiverName &&
    service.caregiver_birth === caregiverBirth
  );
};

// 그룹별 5월 1일(근로자의날)에 근무했는지 확인
const isWorkedOnLaborDay = (groupIndex: number) => {
  const services = getIndividualServicesForGroup(groupIndex);
  if (!services || services.length === 0) return false;

  // 현재 조회 년월에서 년도 추출
  const year = searchParams.value.lebYm.substring(0, 4);
  const laborDay = `${year}-05-01`;

  // 5월 1일에 근무한 기록이 있는지 확인
  return services.some((service: any) => service.service_date === laborDay);
};

// 그룹별 5월 평균 근무시간 계산 (분) - 근로자의날 휴일수당용
const getLaborDayAverageMinutes = (groupIndex: number) => {
  const services = getIndividualServicesForGroup(groupIndex);
  if (!services || services.length === 0) return 0;

  // 5월 데이터만 필터링
  const year = searchParams.value.lebYm.substring(0, 4);
  const month = searchParams.value.lebYm.substring(4, 6);

  if (month !== '05') return 0; // 5월이 아니면 0 반환

  // 날짜별로 수가시간 합산 (서비스횟수 × 수가시간)
  const dailySugaMinutesByDate: any = {};

  services.forEach((service: any) => {
    const dateStr = service.service_date;
    // 5월 데이터만 처리
    if (dateStr.startsWith(`${year}-05`)) {
      // 수가시간을 분으로 변환 (1회의 수가시간)
      const sugaHours = parseFloat(String(service.suga_hours || 0).replace(/[^\d.-]/g, '')) || 0;
      const minutes = sugaHours * 60;

      // 같은 날짜에 여러 서비스가 있으면 합산
      if (!dailySugaMinutesByDate[dateStr]) {
        dailySugaMinutesByDate[dateStr] = 0;
      }
      dailySugaMinutesByDate[dateStr] += minutes;
    }
  });

  // 근무 일수
  const workDays = Object.keys(dailySugaMinutesByDate).length;
  if (workDays === 0) return 0;

  // 총 수가시간 (분)
  let totalMinutes = 0;
  Object.values(dailySugaMinutesByDate).forEach((minutes: any) => {
    totalMinutes += minutes;
  });

  // 평균 계산 후 30분 단위로 내림
  const averageMinutes = Math.floor(totalMinutes / workDays);
  const roundedTo30Minutes = Math.floor(averageMinutes / 30) * 30;
  return roundedTo30Minutes;
};

// 수가명이 공휴일가산인지 확인하는 함수
// '공휴일가산', '공휴일 가산', 또는 '공휴일'과 '가산'이 함께 포함된 경우
const isHolidaySuga = (sugaName: string | null | undefined): boolean => {
  if (!sugaName) return false;
  const name = sugaName.toLowerCase().replace(/\s/g, ''); // 공백 제거
  // '공휴일가산'이 포함되어 있거나, '공휴일'과 '가산'이 모두 포함된 경우
  if (name.includes('공휴일가산')) return true;
  if (name.includes('공휴일') && name.includes('가산')) return true;
  // '일요일가산'이 포함되어 있거나, '일요일'과 '가산'이 모두 포함된 경우
  if (name.includes('일요일가산')) return true;
  if (name.includes('일요일') && name.includes('가산')) return true;
  return false;
};

// 그룹별 휴일 수당 대상 총 시간 계산 (분)
// 수가명에 '공휴일가산'이 포함된 서비스만 대상 (날짜 무관)
const getHolidayTotalMinutes = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return 0;

  const applyHoliday = settingsData.value[groupIndex]?.applyHolidayAllowance !== false;

  // 휴일수당 지급이 체크 해제되어 있으면 0
  if (!applyHoliday) return 0;

  const group = groupedMemberData.value[groupIndex];
  if (!group.patients || group.patients.length === 0) return 0;

  let totalMinutes = 0;

  // 공휴일가산 수가를 가진 환자들의 시간 합산
  group.patients.forEach((patient: any) => {
    const sugaName = patient.suga_name || patient.sugaName;
    
    // 수가명에 '공휴일가산'이 포함된 경우만 처리
    if (isHolidaySuga(sugaName)) {
      const serviceDays = patient.service_days || patient.serviceDays || 0;
      const sugaHours = parseFloat(String(patient.suga_hours || patient.sugaHours || 0).replace(/[^\d.-]/g, '')) || 0;
      totalMinutes += serviceDays * sugaHours * 60;
    }
  });

  return Math.round(totalMinutes);
};

// 그룹별 근로자의날 수당 계산
// 근로자의날은 유급휴일이므로:
// - 일을 안했으면: 일일 평균 근로시간만큼 기본 급여 (시급 × 일일 평균 시간)
// - 일을 했으면: 기본 1일치 + 일한 시간만큼 휴일근로수당 (시급 × 일한 시간 × 0.5 추가)
const getLaborDayAllowanceAmount = (groupIndex: number, applyAllowance: boolean) => {
  if (!applyAllowance) return 0;

  const month = searchParams.value.lebYm.substring(4, 6);
  if (month !== '05') return 0; // 5월이 아니면 0

  const worked = isWorkedOnLaborDay(groupIndex);
  const timePay = taxData.value?.tr_time_pay || 0;
  if (timePay === 0) return 0;

  // 일일 평균 근로시간 계산 (분)
  const averageMinutes = getLaborDayAverageMinutes(groupIndex);
  if (averageMinutes === 0) return 0;

  // 기본 급여: 일일 평균 시간 × 시급 (쉬어도 받음)
  const basePay = (averageMinutes / 60) * timePay;

  if (worked) {
    // 일을 했으면: 기본급 + 휴일근로수당 (일한 시간 × 시급 × 0.5)
    // 근로자의날에 일한 시간 계산
    const dailyData = getDailyServicesForGroup(groupIndex);
    const year = searchParams.value.lebYm.substring(0, 4);
    const laborDay = `${year}-05-01`;

    let workedMinutes = 0;
    dailyData.forEach((daily: any) => {
      if (daily.service_date === laborDay) {
        const minutes = (daily.daily_total_hours || 0) * 60;
        if (minutes > workedMinutes) {
          workedMinutes = minutes;
        }
      }
    });

    // 휴일근로수당: 일한 시간 × 시급 × 0.5 (추가 수당)
    const holidayWorkAllowance = (workedMinutes / 60) * timePay * 0.5;

    return Math.round(basePay + holidayWorkAllowance);
  } else {
    // 일을 안했으면: 기본급만
    return Math.round(basePay);
  }
};

// 그룹별 연장근무 개수 계산 (일일 8시간 초과 일수)
const getOvertimeCount = (groupIndex: number) => {
  const dailyData = getDailyServicesForGroup(groupIndex);
  if (!dailyData || dailyData.length === 0) return 0;

  const dailyMinutesByDate: any = {};

  dailyData.forEach((daily: any) => {
    const dateStr = daily.service_date;
    const minutes = (daily.daily_total_hours || 0) * 60;

    // 같은 날짜의 여러 레코드가 있을 수 있으므로 최대값 사용
    if (!dailyMinutesByDate[dateStr] || dailyMinutesByDate[dateStr] < minutes) {
      dailyMinutesByDate[dateStr] = minutes;
    }
  });

  // 8시간을 초과한 날 카운트
  let overtimeDays = 0;
  Object.values(dailyMinutesByDate).forEach((minutes: any) => {
    if (minutes > 480) { // 8시간(480분) 초과
      overtimeDays++;
    }
  });

  return overtimeDays;
};

// 그룹별 일일 초과시간 계산 (분)
const getDailyOvertimeMinutes = (groupIndex: number) => {
  const dailyData = getDailyServicesForGroup(groupIndex);
  if (!dailyData || dailyData.length === 0) return 0;

  let dailyOvertimeMinutes = 0;
  const dailyMinutesByDate: any = {};

  dailyData.forEach((daily: any) => {
    const dateStr = daily.service_date;
    const minutes = (daily.daily_total_hours || 0) * 60;

    // 같은 날짜의 여러 레코드가 있을 수 있으므로 최대값 사용
    if (!dailyMinutesByDate[dateStr] || dailyMinutesByDate[dateStr] < minutes) {
      dailyMinutesByDate[dateStr] = minutes;
    }
  });

  // 각 날짜별로 8시간 초과분 합산
  Object.values(dailyMinutesByDate).forEach((minutes: any) => {
    const dailyOvertime = Math.max(0, minutes - 480); // 8시간(480분) 초과분
    dailyOvertimeMinutes += dailyOvertime;
  });

  return Math.round(dailyOvertimeMinutes);
};

// 그룹별 주차 초과시간 계산 (분) - 최대값 반환
const getWeeklyOvertimeMinutes = (groupIndex: number) => {
  const dailyData = getDailyServicesForGroup(groupIndex);
  if (!dailyData || dailyData.length === 0) return 0;

  const dailyMinutesByDate: any = {};

  dailyData.forEach((daily: any) => {
    const dateStr = daily.service_date;
    const minutes = (daily.daily_total_hours || 0) * 60;

    if (!dailyMinutesByDate[dateStr] || dailyMinutesByDate[dateStr] < minutes) {
      dailyMinutesByDate[dateStr] = minutes;
    }
  });

  // 주차별로 그룹화
  let maxWeeklyOvertimeMinutes = 0;
  const weeklyData: any = {};

  Object.entries(dailyMinutesByDate).forEach(([dateStr, minutes]: [string, any]) => {
    const serviceDate = new Date(dateStr);

    // 해당 날짜가 속한 주의 월요일 구하기
    const dayOfWeek = serviceDate.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(serviceDate);
    monday.setDate(serviceDate.getDate() + diff);
    monday.setHours(0, 0, 0, 0);

    const weekKey = monday.toISOString().split('T')[0];

    if (!weeklyData[weekKey]) {
      weeklyData[weekKey] = 0;
    }
    weeklyData[weekKey] += minutes;
  });

  // 각 주차별로 40시간 초과분 중 최대값 찾기
  Object.values(weeklyData).forEach((totalMinutes: any) => {
    const weeklyOvertime = Math.max(0, totalMinutes - 2400); // 40시간(2400분) 초과분
    maxWeeklyOvertimeMinutes = Math.max(maxWeeklyOvertimeMinutes, weeklyOvertime);
  });

  return Math.round(maxWeeklyOvertimeMinutes);
};

// 그룹별 연장근무 총 시간 계산 (분)
// 일일 초과시간과 주차 초과시간 중 큰 값을 반환
const getOvertimeTotalMinutes = (groupIndex: number) => {
  const dailyOvertime = getDailyOvertimeMinutes(groupIndex);
  const weeklyOvertime = getWeeklyOvertimeMinutes(groupIndex);

  // 일일 초과시간과 주차 초과시간 중 큰 값 반환
  return Math.max(dailyOvertime, weeklyOvertime);
};

// 그룹별 평균 시급 계산 (설정된 시급 또는 기본 시급)
const getAverageTimePay = (groupIndex: number) => {
  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) {
    return taxData.value?.tr_time_pay || 0;
  }

  const group = groupedMemberData.value[groupIndex];
  const settings = settingsData.value[groupIndex];

  // 설정에서 서비스별 시급이 입력되어 있는지 확인
  if (settings && settings.serviceTimePay) {
    const serviceTimePayObj = settings.serviceTimePay;
    const timePayValues: number[] = [];

    // 입력된 시급 값들 수집
    Object.values(serviceTimePayObj).forEach((value: any) => {
      if (value && value !== '') {
        const numericValue = parseFloat(String(value).replace(/[^\d]/g, '')) || 0;
        if (numericValue > 0) {
          timePayValues.push(numericValue);
        }
      }
    });

    // 시급이 입력되어 있으면 평균 계산
    if (timePayValues.length > 0) {
      const sum = timePayValues.reduce((acc, val) => acc + val, 0);
      return Math.round(sum / timePayValues.length);
    }
  }

  // 설정된 시급이 없으면 기본 시급 또는 그룹의 평균 시급 사용
  if (group.totals && group.totals.average_time_pay) {
    return group.totals.average_time_pay;
  }

  return taxData.value?.tr_time_pay || 0;
};

// 휴일수당 금액 계산
// 기본급(최저시급 × 수가시간)에 이미 휴일근로시간이 포함되어 있으므로
// 수당 금액 계산 (휴일/연장 공통 로직)
const calculateAllowanceAmount = (totalMinutes: number, applyAllowance: boolean) => {
  if (!applyAllowance || totalMinutes === 0) return 0;

  const timePay = taxData.value?.tr_time_pay || 0;
  if (timePay === 0) return 0;

  const totalHours = totalMinutes / 60;
  return Math.round(totalHours * timePay * 0.5);
};

// 휴일수당 = 휴일 근무 시간 × 최저시급 × 0.5 (비과세)
const getHolidayAllowanceAmount = (groupIndex: number, applyAllowance: boolean) => {
  return calculateAllowanceAmount(getHolidayTotalMinutes(groupIndex), applyAllowance);
};

// 연장수당 = 연장 근무 시간 × 최저시급 × 0.5 (비과세)
const getOvertimeAllowanceAmount = (groupIndex: number, applyAllowance: boolean) => {
  return calculateAllowanceAmount(getOvertimeTotalMinutes(groupIndex), applyAllowance);
};

// 식대 금액 계산 (설정 탭 표시용)
const getMealAllowanceAmount = (groupIndex: number, applyAllowance: boolean) => {
  if (!applyAllowance) return 0;

  if (!groupedMemberData.value || !groupedMemberData.value[groupIndex]) return 0;
  if (!taxData.value || !taxData.value.tr_time_pay) return 0;

  const group = groupedMemberData.value[groupIndex];
  const lunchTimeCount = group.totals?.lunch_time_count || 0;

  if (lunchTimeCount === 0) return 0;

  // 기본 급여 계산
  const basicTotal = calculateBasicPay(group.totals.suga_hours, group.totals.service_days, group.caregiverInfo.lem_work_start_date);

  // 총 월급여
  const totalMonthlyPay = group.totals.monthly_pay;

  // 월급여에서 기본급+주휴+연차를 뺀 나머지 (급여외수당은 빼지 않음)
  const remainingForMeal = totalMonthlyPay - basicTotal;

  if (remainingForMeal <= 0) return 0;

  // 요청 식대 = min(일수 × 20000, 200000)
  const requestedMealAllowance = Math.min(lunchTimeCount * 20000, 200000);
  // 실제 식대 = min(요청 식대, 남은 금액)
  const actualMealAllowance = Math.min(requestedMealAllowance, remainingForMeal);

  return actualMealAllowance;
};
// 설정 데이터 초기화
const initializeSettingsData = () => {
  if (!groupedMemberData.value) return;

  groupedMemberData.value.forEach((group: any, groupIndex: number) => {
    if (!settingsData.value[groupIndex]) {
      const serviceTimePay: any = {};

      // 수급자별 수가명에 대해 기존 시급 값 찾아서 초기화
      if (group.patientGroups) {
        group.patientGroups.forEach((patientGroup: any) => {
          if (patientGroup.items && patientGroup.items.length > 0) {
            const patientCode = patientGroup.patient_code;
            serviceTimePay[patientCode] = {};

            patientGroup.items.forEach((item: any) => {
              if (item.suga_name && item.suga_name !== '중증수급자 가산') {
                // 기존 시급 값이 있으면 사용, 없으면 빈 문자열
                if (!serviceTimePay[patientCode][item.suga_name]) {
                  serviceTimePay[patientCode][item.suga_name] = (item.time_pay && item.time_pay.trim() !== '') ? item.time_pay : '';
                }
              }
            });
          }
        });
      }

      // 나이 계산하여 보험 적용 기본값 설정
      const caregiverAge = calculateAge(group.caregiverInfo.caregiver_birth, searchParams.value.lebYm);
      // 만 60세 이상이면 국민연금 기본 해제, 만 65세 이상이면 고용보험 기본 해제
      let defaultNationalPension = caregiverAge < 60;
      let defaultEmploymentInsurance = caregiverAge < 65;

      // 저장된 체크박스 값 불러오기 (memberJoinData에서 첫 번째 항목 확인)
      const firstItem = memberJoinData.value.find((item: any) =>
        item.caregiver_name === group.caregiverInfo.caregiver_name &&
        item.caregiver_birth === group.caregiverInfo.caregiver_birth &&
        item.lem_gender === group.caregiverInfo.lem_gender &&
        item.lem_license === group.caregiverInfo.lem_license &&
        item.lem_work_start_date === group.caregiverInfo.lem_work_start_date &&
        item.lem_work_end_date === group.caregiverInfo.lem_work_end_date
      );

      // 국민연금 금액 초기화 (저장된 값이 있으면 사용)
      let yeonPayAmount = '';
      if (firstItem && firstItem.lemi_yeon_pay) {
        const numericValue = parseFloat(String(firstItem.lemi_yeon_pay).replace(/[^\d]/g, '')) || 0;
        // 만 60세 이상이면 0원으로 초기화
        if (caregiverAge < 60 && numericValue > 0) {
          yeonPayAmount = new Intl.NumberFormat('ko-KR').format(numericValue);
        }
      }

      // 저장된 값이 있으면 사용하되, 나이 조건은 우선 적용
      if (firstItem) {
        if (firstItem.lemi_apply_go !== undefined) {
          // 저장된 값이 있으면 사용하되, 만 65세 이상이면 강제로 해제
          if (caregiverAge >= 65) {
            defaultEmploymentInsurance = false;
          } else {
            defaultEmploymentInsurance = firstItem.lemi_apply_go === 'Y';
          }
        }
      }

      // 휴일수당, 연장수당 기본값 (기본값은 항상 true)
      let defaultHolidayAllowance = true;
      let defaultOvertimeAllowance = true;
      let defaultMealAllowance = true; // 식대는 항상 기본값 true

      // 저장된 값이 있으면 사용
      if (firstItem) {
        if (firstItem.lemi_apply_holiday_pay !== undefined) {
          defaultHolidayAllowance = firstItem.lemi_apply_holiday_pay === 'Y';
        }
        if (firstItem.lemi_apply_overtime_pay !== undefined) {
          defaultOvertimeAllowance = firstItem.lemi_apply_overtime_pay === 'Y';
        }
        if (firstItem.lemi_apply_lunch_pay !== undefined) {
          defaultMealAllowance = firstItem.lemi_apply_lunch_pay === 'Y';
        }
      }

      // 퇴직금 적립 기본값 (기본값은 항상 true)
      let defaultRetirement = true;

      // 저장된 값이 있으면 사용
      if (firstItem && firstItem.lemi_apply_out_pay !== undefined) {
        defaultRetirement = firstItem.lemi_apply_out_pay === 'Y';
      }

      // 근로자의날 수당 기본값 (기본값은 항상 true)
      let defaultLaborDayAllowance = true;

      // 저장된 값이 있으면 사용
      if (firstItem && firstItem.lemi_apply_labor_day_pay !== undefined) {
        defaultLaborDayAllowance = firstItem.lemi_apply_labor_day_pay === 'Y';
      }

      // 장기근속수당 금액 (저장된 값이 있으면 사용)
      let longServiceAmount = '';
      if (firstItem && firstItem.lemi_long_service_pay) {
        const numericValue = parseFloat(String(firstItem.lemi_long_service_pay).replace(/[^\d]/g, '')) || 0;
        if (numericValue > 0) {
          longServiceAmount = new Intl.NumberFormat('ko-KR').format(numericValue);
        }
      }

      // 보수교육비 금액 (저장된 값이 있으면 사용)
      let bosuEduPayAmount = '';
      if (firstItem && firstItem.lemi_bosu_edu_pay) {
        const numericValue = parseFloat(String(firstItem.lemi_bosu_edu_pay).replace(/[^\d]/g, '')) || 0;
        if (numericValue > 0) {
          bosuEduPayAmount = new Intl.NumberFormat('ko-KR').format(numericValue);
        }
      }

      settingsData.value[groupIndex] = {
        serviceTimePay: serviceTimePay,
        yeonPay: yeonPayAmount,
        applyEmploymentInsurance: defaultEmploymentInsurance,
        applyHolidayAllowance: defaultHolidayAllowance,
        applyOvertimeAllowance: defaultOvertimeAllowance,
        applyMealAllowance: defaultMealAllowance,
        applyRetirement: defaultRetirement,
        applyLaborDayAllowance: defaultLaborDayAllowance,
        longServiceAllowance: longServiceAmount,
        bosuEduPayAllowance: bosuEduPayAmount
      };
    } else {
      // 기존 데이터가 있으면 저장된 체크박스 값 불러오기
      const firstItem = memberJoinData.value.find((item: any) =>
        item.caregiver_name === group.caregiverInfo.caregiver_name &&
        item.caregiver_birth === group.caregiverInfo.caregiver_birth &&
        item.lem_gender === group.caregiverInfo.lem_gender &&
        item.lem_license === group.caregiverInfo.lem_license &&
        item.lem_work_start_date === group.caregiverInfo.lem_work_start_date &&
        item.lem_work_end_date === group.caregiverInfo.lem_work_end_date
      );

      if (firstItem) {
        // 저장된 값이 있으면 사용하되, 나이 조건은 우선 적용
        const caregiverAge = calculateAge(group.caregiverInfo.caregiver_birth, searchParams.value.lebYm);
        // 국민연금 금액
        if (firstItem.lemi_yeon_pay !== undefined) {
          // 저장된 값이 있으면 사용하되, 만 60세 이상이면 0원
          if (caregiverAge >= 60) {
            settingsData.value[groupIndex].yeonPay = '';
          } else {
            let yeonPay = firstItem.lemi_yeon_pay || '';
            if (yeonPay) {
              const numericValue = parseFloat(String(yeonPay).replace(/[^\d]/g, '')) || 0;
              if (numericValue > 0) {
                yeonPay = new Intl.NumberFormat('ko-KR').format(numericValue);
              }
            }
            settingsData.value[groupIndex].yeonPay = yeonPay;
          }
        }
        if (firstItem.lemi_apply_go !== undefined) {
          // 저장된 값이 있으면 사용하되, 만 65세 이상이면 강제로 해제
          if (caregiverAge >= 65) {
            settingsData.value[groupIndex].applyEmploymentInsurance = false;
          } else {
            settingsData.value[groupIndex].applyEmploymentInsurance = firstItem.lemi_apply_go === 'Y';
          }
        }
        if (firstItem.lemi_apply_holiday_pay !== undefined) {
          settingsData.value[groupIndex].applyHolidayAllowance = firstItem.lemi_apply_holiday_pay === 'Y';
        }
        if (firstItem.lemi_apply_overtime_pay !== undefined) {
          settingsData.value[groupIndex].applyOvertimeAllowance = firstItem.lemi_apply_overtime_pay === 'Y';
        }
        if (firstItem.lemi_apply_lunch_pay !== undefined) {
          settingsData.value[groupIndex].applyMealAllowance = firstItem.lemi_apply_lunch_pay === 'Y';
        }
        if (firstItem.lemi_apply_out_pay !== undefined) {
          settingsData.value[groupIndex].applyRetirement = firstItem.lemi_apply_out_pay === 'Y';
        }
        if (firstItem.lemi_apply_labor_day_pay !== undefined) {
          settingsData.value[groupIndex].applyLaborDayAllowance = firstItem.lemi_apply_labor_day_pay === 'Y';
        }
        // 장기근속수당 금액
        if (firstItem.lemi_long_service_pay) {
          const numericValue = parseFloat(String(firstItem.lemi_long_service_pay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            settingsData.value[groupIndex].longServiceAllowance = new Intl.NumberFormat('ko-KR').format(numericValue);
          }
        }
        // 보수교육비 금액
        if (firstItem.lemi_bosu_edu_pay) {
          const numericValue = parseFloat(String(firstItem.lemi_bosu_edu_pay).replace(/[^\d]/g, '')) || 0;
          if (numericValue > 0) {
            settingsData.value[groupIndex].bosuEduPayAllowance = new Intl.NumberFormat('ko-KR').format(numericValue);
          }
        }
      } else {
        // 저장된 값이 없으면 나이에 따라 기본값 설정
        const caregiverAge = calculateAge(group.caregiverInfo.caregiver_birth, searchParams.value.lebYm);
        // 만 60세 이상이면 국민연금 0원, 만 65세 이상이면 고용보험 기본 해제
        settingsData.value[groupIndex].yeonPay = '';
        settingsData.value[groupIndex].applyEmploymentInsurance = caregiverAge < 65;
        // 휴일수당, 연장수당, 식대, 퇴직금, 근로자의날 수당 기본값은 모두 true
        settingsData.value[groupIndex].applyHolidayAllowance = true;
        settingsData.value[groupIndex].applyOvertimeAllowance = true;
        settingsData.value[groupIndex].applyMealAllowance = true;
        settingsData.value[groupIndex].applyRetirement = true;
        settingsData.value[groupIndex].applyLaborDayAllowance = true;
        settingsData.value[groupIndex].longServiceAllowance = '';
      }
    }
  });
};

// 설정 탭 시급 입력 필드 포커스 이벤트
const onSettingsTimePayFocus = (event: FocusEvent, groupIndex: number, patientCode: string, sugaName: string) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  // 컴마 제거하여 숫자만 표시
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
  if (settingsData.value[groupIndex]) {
    if (!settingsData.value[groupIndex].serviceTimePay) {
      settingsData.value[groupIndex].serviceTimePay = {};
    }
    if (!settingsData.value[groupIndex].serviceTimePay[patientCode]) {
      settingsData.value[groupIndex].serviceTimePay[patientCode] = {};
    }
    settingsData.value[groupIndex].serviceTimePay[patientCode][sugaName] = numericValue;
  }
};

// 설정 탭 시급 입력 필드 포커스 아웃 이벤트
const onSettingsTimePayBlur = (event: FocusEvent, groupIndex: number, patientCode: string, sugaName: string) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;

  // 숫자만 추출
  const numericValue = value.replace(/[^\d]/g, '');

  if (numericValue && settingsData.value[groupIndex]) {
    // 천원단위로 컴마 추가
    const formattedValue = new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
    input.value = formattedValue;

    if (!settingsData.value[groupIndex].serviceTimePay) {
      settingsData.value[groupIndex].serviceTimePay = {};
    }
    if (!settingsData.value[groupIndex].serviceTimePay[patientCode]) {
      settingsData.value[groupIndex].serviceTimePay[patientCode] = {};
    }
    settingsData.value[groupIndex].serviceTimePay[patientCode][sugaName] = formattedValue;

    // 같은 수급자, 같은 수가명을 가진 모든 아이템의 time_pay도 업데이트 및 noPreviousData 플래그 제거
    if (groupedMemberData.value && groupedMemberData.value[groupIndex]) {
      const group = groupedMemberData.value[groupIndex];
      if (group.patientGroups) {
        group.patientGroups.forEach((patientGroup: any) => {
          if (patientGroup.patient_code === patientCode && patientGroup.items) {
            patientGroup.items.forEach((item: any) => {
              if (item.suga_name === sugaName) {
                item.time_pay = formattedValue;
                // 시급이 입력되면 noPreviousData 플래그 제거
                if (item.noPreviousData) {
                  item.noPreviousData = false;
                }
              }
            });
          }
        });
      }
    }
  } else if (settingsData.value[groupIndex]) {
    input.value = '';
    if (!settingsData.value[groupIndex].serviceTimePay) {
      settingsData.value[groupIndex].serviceTimePay = {};
    }
    if (!settingsData.value[groupIndex].serviceTimePay[patientCode]) {
      settingsData.value[groupIndex].serviceTimePay[patientCode] = {};
    }
    settingsData.value[groupIndex].serviceTimePay[patientCode][sugaName] = '';

    // 같은 수급자, 같은 수가명을 가진 모든 아이템의 time_pay도 초기화
    if (groupedMemberData.value && groupedMemberData.value[groupIndex]) {
      const group = groupedMemberData.value[groupIndex];
      if (group.patientGroups) {
        group.patientGroups.forEach((patientGroup: any) => {
          if (patientGroup.patient_code === patientCode && patientGroup.items) {
            patientGroup.items.forEach((item: any) => {
              if (item.suga_name === sugaName) {
                item.time_pay = '';
              }
            });
          }
        });
      }
    }
  }
};

// 설정 탭 시급 입력 필드 입력 이벤트
const onSettingsTimePayInput = (event: Event, groupIndex: number, patientCode: string, sugaName: string) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;

  // 숫자가 아닌 문자 제거
  value = value.replace(/[^\d]/g, '');

  // 최대 9자리까지 제한
  if (value.length > 9) {
    value = value.slice(0, 9);
  }

  input.value = value;
  if (settingsData.value[groupIndex]) {
    if (!settingsData.value[groupIndex].serviceTimePay) {
      settingsData.value[groupIndex].serviceTimePay = {};
    }
    if (!settingsData.value[groupIndex].serviceTimePay[patientCode]) {
      settingsData.value[groupIndex].serviceTimePay[patientCode] = {};
    }
    settingsData.value[groupIndex].serviceTimePay[patientCode][sugaName] = value;

    // 같은 수급자, 같은 수가명을 가진 모든 아이템의 time_pay도 업데이트 및 noPreviousData 플래그 제거
    if (groupedMemberData.value && groupedMemberData.value[groupIndex]) {
      const group = groupedMemberData.value[groupIndex];
      if (group.patientGroups) {
        group.patientGroups.forEach((patientGroup: any) => {
          if (patientGroup.patient_code === patientCode && patientGroup.items) {
            patientGroup.items.forEach((item: any) => {
              if (item.suga_name === sugaName) {
                item.time_pay = value;
                // 시급이 입력되면 noPreviousData 플래그 제거
                if (value && item.noPreviousData) {
                  item.noPreviousData = false;
                }
              }
            });
          }
        });
      }
    }
  }
};

// 장기근속수당 입력 필드 포커스 이벤트
const onLongServiceFocus = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  // 컴마 제거하여 숫자만 표시
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
  if (settingsData.value[groupIndex]) {
    settingsData.value[groupIndex].longServiceAllowance = numericValue;
  }
};

// 장기근속수당 입력 필드 포커스 아웃 이벤트
const onLongServiceBlur = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;

  // 숫자만 추출
  const numericValue = value.replace(/[^\d]/g, '');

  if (numericValue && settingsData.value[groupIndex]) {
    // 천원단위로 컴마 추가
    const formattedValue = new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
    input.value = formattedValue;
    settingsData.value[groupIndex].longServiceAllowance = formattedValue;

    // 재계산 트리거
    if (groupedMemberData.value && groupedMemberData.value[groupIndex]) {
      calculateGroupTotals(groupedMemberData.value[groupIndex], groupIndex);
    }
  } else if (settingsData.value[groupIndex]) {
    input.value = '';
    settingsData.value[groupIndex].longServiceAllowance = '';

    // 재계산 트리거
    if (groupedMemberData.value && groupedMemberData.value[groupIndex]) {
      calculateGroupTotals(groupedMemberData.value[groupIndex], groupIndex);
    }
  }
};

// 장기근속수당 입력 필드 입력 이벤트
const onLongServiceInput = (event: Event, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;

  // 숫자가 아닌 문자 제거
  value = value.replace(/[^\d]/g, '');

  // 최대 9자리까지 제한
  if (value.length > 9) {
    value = value.slice(0, 9);
  }

  input.value = value;
  if (settingsData.value[groupIndex]) {
    settingsData.value[groupIndex].longServiceAllowance = value;
  }
};

// 보수교육비 입력 필드 포커스 이벤트
const onBosuEduPayFocus = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
  if (settingsData.value[groupIndex]) {
    settingsData.value[groupIndex].bosuEduPayAllowance = numericValue;
  }
};

// 보수교육비 입력 필드 포커스 아웃 이벤트
const onBosuEduPayBlur = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/[^\d]/g, '');

  if (numericValue && settingsData.value[groupIndex]) {
    const formattedValue = new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
    input.value = formattedValue;
    settingsData.value[groupIndex].bosuEduPayAllowance = formattedValue;

    if (groupedMemberData.value && groupedMemberData.value[groupIndex]) {
      calculateGroupTotals(groupedMemberData.value[groupIndex], groupIndex);
    }
  } else if (settingsData.value[groupIndex]) {
    input.value = '';
    settingsData.value[groupIndex].bosuEduPayAllowance = '';

    if (groupedMemberData.value && groupedMemberData.value[groupIndex]) {
      calculateGroupTotals(groupedMemberData.value[groupIndex], groupIndex);
    }
  }
};

// 보수교육비 입력 필드 입력 이벤트
const onBosuEduPayInput = (event: Event, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  value = value.replace(/[^\d]/g, '');
  if (value.length > 9) {
    value = value.slice(0, 9);
  }
  input.value = value;
  if (settingsData.value[groupIndex]) {
    settingsData.value[groupIndex].bosuEduPayAllowance = value;
  }
};

// 국민연금 입력 필드 포커스 이벤트
const onYeonPayFocus = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;

  // 컴마 제거하여 숫자만 표시
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
};

// 국민연금 입력 필드 포커스 아웃 이벤트
const onYeonPayBlur = (event: FocusEvent, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;

  // 숫자만 추출
  const numericValue = value.replace(/[^\d]/g, '');

  if (numericValue && settingsData.value[groupIndex]) {
    // 천원단위로 컴마 추가
    const formattedValue = new Intl.NumberFormat('ko-KR').format(parseInt(numericValue));
    input.value = formattedValue;
    settingsData.value[groupIndex].yeonPay = formattedValue;

    // 재계산 트리거
    if (groupedMemberData.value && groupedMemberData.value[groupIndex]) {
      calculateGroupTotals(groupedMemberData.value[groupIndex], groupIndex);
    }
  } else if (settingsData.value[groupIndex]) {
    input.value = '';
    settingsData.value[groupIndex].yeonPay = '';

    // 재계산 트리거
    if (groupedMemberData.value && groupedMemberData.value[groupIndex]) {
      calculateGroupTotals(groupedMemberData.value[groupIndex], groupIndex);
    }
  }
};

// 국민연금 입력 필드 입력 이벤트
const onYeonPayInput = (event: Event, groupIndex: number) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;

  // 숫자가 아닌 문자 제거
  value = value.replace(/[^\d]/g, '');

  // 최대 9자리까지 제한
  if (value.length > 9) {
    value = value.slice(0, 9);
  }

  input.value = value;
  if (settingsData.value[groupIndex]) {
    settingsData.value[groupIndex].yeonPay = value;
  }
};

// 그룹별 수당 계산 및 업데이트 함수
const updateGroupAllowances = () => {
  if (!groupedMemberData.value || groupedMemberData.value.length === 0) return;

  groupedMemberData.value.forEach((group: any, groupIndex: number) => {
    // 총계 재계산 (휴일수당, 식대, 기타수당(급여) 모두 포함)
    calculateGroupTotals(group, groupIndex);
  });
};

// 계산 중복 실행 방지 플래그
const isCalculating = ref(false);

// groupedMemberData 변경 시 설정 데이터 초기화 및 수당 계산
watch(
  () => groupedMemberData.value,
  async (newVal, oldVal) => {
    // 계산 중이면 리턴
    if (isCalculating.value) return;

    // 배열 길이가 같고 첫 번째 요소가 같으면 내부 수정이므로 리턴
    if (newVal && oldVal && newVal.length === oldVal.length && newVal[0] === oldVal[0]) {
      return;
    }

    if (newVal && newVal.length > 0) {
      isCalculating.value = true;
      try {
        initializeSettingsData();
        initializeTabs();
        // 다음 틱에서 수당 계산 (초기화 후)
        await nextTick();

        // 각 그룹에 대해 계산 수행
        newVal.forEach((group: any, groupIndex: number) => {
          calculateGroupTotals(group, groupIndex);
        });
      } finally {
        isCalculating.value = false;
      }
    }
  }
);

// 설정 데이터 변경 시 그룹 재계산 (체크박스와 금액 입력 감시)
watch(
  () => {
    // 체크박스 상태와 금액 입력값을 추출하여 감시
    const checkboxStates: any[] = [];
    Object.keys(settingsData.value).forEach((key) => {
      const idx = parseInt(key);
      if (settingsData.value[idx]) {
        checkboxStates.push({
          index: idx,
          applyHoliday: settingsData.value[idx].applyHolidayAllowance,
          applyOvertime: settingsData.value[idx].applyOvertimeAllowance,
          applyMeal: settingsData.value[idx].applyMealAllowance,
          yeonPay: settingsData.value[idx].yeonPay,
          applyEmploymentInsurance: settingsData.value[idx].applyEmploymentInsurance,
          applyRetirement: settingsData.value[idx].applyRetirement,
          applyLaborDay: settingsData.value[idx].applyLaborDayAllowance
        });
      }
    });
    return JSON.stringify(checkboxStates); // JSON 문자열로 비교
  },
  () => {
    if (isCalculating.value) return;

    isCalculating.value = true;
    try {
      updateGroupAllowances();
    } finally {
      isCalculating.value = false;
    }
  }
);

// memberJoinData 변경 시 summaryInfo 업데이트
watch(
  () => memberJoinData.value,
  () => {
    calculateSummaryInfo();
  },
  { deep: true }
);

// groupedMemberData의 각 그룹의 totals.taxSavings 변경 감지하여 summaryInfo 업데이트
watch(
  () => {
    if (!groupedMemberData.value || groupedMemberData.value.length === 0) return '';
    // 각 그룹의 totals.taxSavings를 명시적으로 읽어서 반응성 추적
    const taxSavingsArray: number[] = [];
    for (let i = 0; i < groupedMemberData.value.length; i++) {
      const group = groupedMemberData.value[i];
      if (group && group.totals) {
        // totals 객체와 taxSavings 속성을 명시적으로 읽어서 반응성 추적
        const totals = group.totals;
        const taxSavings = totals.taxSavings || 0;
        taxSavingsArray.push(taxSavings);
      } else {
        taxSavingsArray.push(0);
      }
    }
    return taxSavingsArray.join(',');
  },
  () => {
    // taxSavings가 변경되면 summaryInfo 업데이트
    calculateSummaryInfo();
  },
  { deep: true, immediate: false }
);

// 외부에서 접근 가능하도록 expose (마감 버튼용)
defineExpose({
  get groupedMemberData() {
    return groupedMemberData.value;
  },
  get lebYm() {
    return searchParams.value.lebYm;
  },
  get taxData() {
    return taxData.value;
  },
  get settingsData() {
    return settingsData.value;
  }
});

</script>

<style lang="scss" scoped>
@import "@/assets/styles/pgmWage.scss";
</style>